import * as sysml_types_star from '@arctek/sysml-types';
import { buildModelFromPackages } from '@arctek/sysml-builder';

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __reExport = (target, mod, secondTarget) => (__copyProps(target, mod, "default"), secondTarget);

// src/index.ts
var index_exports = {};
__export(index_exports, {
  ActionExecutor: () => ActionExecutor,
  EventQueue: () => EventQueue,
  IncrementalParser: () => IncrementalParser,
  Query: () => Query,
  SimulationClock: () => SimulationClock,
  SimulationEngine: () => SimulationEngine,
  StateInterpreter: () => StateInterpreter,
  TOASTER_SCENARIOS: () => TOASTER_SCENARIOS,
  TraceRecorder: () => TraceRecorder,
  ValidationRuleRegistry: () => ValidationRuleRegistry,
  VariableStore: () => VariableStore,
  applyTextEdit: () => applyTextEdit,
  checkDimensionalCompatibility: () => checkDimensionalCompatibility,
  compileSysMLModel: () => compileSysMLModel,
  computeCRDTChanges: () => computeCRDTChanges,
  createIncrementalParser: () => createIncrementalParser,
  createSnapshot: () => createSnapshot,
  detectStdlibCollisions: () => detectStdlibCollisions,
  diffModels: () => diffModels,
  evaluateValidationRules: () => evaluateValidationRules,
  executeQuery: () => executeQuery,
  exportToJson: () => exportToJson,
  exportToReqIF: () => exportToReqIF,
  exportToXMI: () => exportToXMI,
  formatDiffSummary: () => formatDiffSummary,
  formatElementDiff: () => formatElementDiff,
  formatSysmlSource: () => formatSysmlSource,
  generatePeerId: () => generatePeerId,
  generateSysMLv2: () => generateSysMLv2,
  getFixesForDiagnostic: () => getFixesForDiagnostic,
  getStandardLibrary: () => getStandardLibrary,
  getToasterScenarios: () => getToasterScenarios,
  importFromJson: () => importFromJson,
  importFromReqIF: () => importFromReqIF,
  importFromXMI: () => importFromXMI,
  isValidParseResult: () => isValidParseResult,
  mergeWithStdlib: () => mergeWithStdlib,
  modelToCRDTElements: () => modelToCRDTElements,
  parseExpression: () => parseExpression,
  parseSysML: () => parseSysML,
  parseSysMLWithImports: () => parseSysMLWithImports,
  peerColor: () => peerColor,
  queryModel: () => queryModel,
  reqifToSysmlSource: () => reqifToSysmlSource,
  resetStdlibCache: () => resetStdlibCache,
  resolveConflict: () => resolveConflict,
  serializeSnapshot: () => serializeSnapshot,
  solveConstraints: () => solveConstraints,
  summarizeValidation: () => summarizeValidation,
  traceElement: () => traceElement,
  transformSpecToIR: () => transformSpecToIR
});

// src/model.ts
var model_exports = {};
__reExport(model_exports, sysml_types_star);

// src/index.ts
__reExport(index_exports, model_exports);

// src/compiler.ts
function compileSysMLModel(model) {
  const graph = buildModelGraph(model);
  const viewProjections = {};
  for (const view of model.allViews) {
    viewProjections[view.id] = projectView(model, graph, view);
  }
  const viewsByElement = {};
  for (const projection of Object.values(viewProjections)) {
    for (const id of projection.nodeIds) {
      (viewsByElement[id] ??= []).push(projection.viewId);
    }
    for (const id of projection.edgeIds) {
      (viewsByElement[id] ??= []).push(projection.viewId);
    }
  }
  const viewNameById = {};
  for (const view of model.allViews) {
    viewNameById[view.id] = view.qualifiedName;
  }
  for (const projection of Object.values(viewProjections)) {
    projection.nav.viewsByElement = viewsByElement;
    for (const node of Object.values(projection.nodes)) {
      const viewIds = viewsByElement[node.id] ?? [];
      node.availableViews = viewIds.map((id) => viewNameById[id] ?? id);
    }
  }
  return { graph, viewProjections };
}
function buildModelGraph(model) {
  const elements = {};
  const byId = {};
  const byQualifiedName = {};
  const byName = {};
  const byTag = {};
  const byElementType = {};
  const byViewName = {};
  for (const element of model.allElements) {
    elements[element.id] = element;
    byId[element.id] = element;
    byQualifiedName[element.qualifiedName] = element.id;
    (byName[element.name] ??= []).push(element.id);
    (byElementType[element.elementType] ??= []).push(element.id);
    if (element.elementType === "view") {
      byViewName[element.qualifiedName] = element.id;
      if (!byViewName[element.name]) byViewName[element.name] = element.id;
    }
    const tags = element.meta?.tags ?? [];
    for (const tag of tags) {
      (byTag[tag] ??= []).push(element.id);
    }
  }
  return {
    elements,
    index: { byId, byQualifiedName, byName, byTag, byElementType, byViewName }
  };
}
function projectView(model, graph, view) {
  const selectedIdSet = resolveViewSelection(model, graph, view);
  const nodeIdSet = /* @__PURE__ */ new Set();
  const explicitlySelectedEdgeIds = /* @__PURE__ */ new Set();
  for (const id of selectedIdSet) {
    const elementType = graph.index.byId[id]?.elementType;
    if (elementType === "partDef") nodeIdSet.add(id);
    if (elementType === "relationship" || elementType === "partUsage") explicitlySelectedEdgeIds.add(id);
  }
  const edgeIdSet = resolveViewEdges(model, graph, view, nodeIdSet);
  for (const edgeId of explicitlySelectedEdgeIds) edgeIdSet.add(edgeId);
  const nodes = {};
  for (const nodeId of nodeIdSet) {
    const element = graph.index.byId[nodeId];
    if (!element) continue;
    nodes[nodeId] = {
      id: element.id,
      elementType: element.elementType,
      label: element.name,
      qualifiedName: element.qualifiedName,
      meta: element.meta,
      source: element.source,
      availableViews: []
    };
  }
  const edges = {};
  for (const edgeId of edgeIdSet) {
    const element = graph.index.byId[edgeId];
    if (element?.elementType === "relationship") {
      const rel = element;
      const from = resolveElementRef(graph, rel.from, { scopeQualifiedName: parentQualifiedName(rel.qualifiedName) });
      const to = resolveElementRef(graph, rel.to, { scopeQualifiedName: parentQualifiedName(rel.qualifiedName) });
      if (!from || !to) continue;
      edges[edgeId] = {
        id: rel.id,
        elementType: "relationship",
        label: rel.relType === "?" ? void 0 : String(rel.relType),
        from,
        to,
        source: rel.source
      };
    } else if (element?.elementType === "partUsage") {
      const usage = element;
      const { from, to } = resolveCompositionEndpoints(graph, usage);
      if (!from || !to) continue;
      edges[edgeId] = {
        id: usage.id,
        elementType: "partUsage",
        label: multiplicityLabel(usage.multiplicity),
        from,
        to,
        source: usage.source
      };
    }
  }
  const nodesByName = {};
  for (const node of Object.values(nodes)) {
    (nodesByName[node.label] ??= []).push(node.id);
  }
  return {
    viewId: view.id,
    nodeIds: Array.from(nodeIdSet),
    edgeIds: Array.from(edgeIdSet),
    nodes,
    edges,
    nav: {
      viewsByElement: {},
      nodesByName
    }
  };
}
function resolveViewSelection(model, graph, view) {
  const scopeQualifiedName = parentQualifiedName(view.qualifiedName);
  const context = { scopeQualifiedName };
  let selected = /* @__PURE__ */ new Set();
  if (view.uses.length) {
    const visited = /* @__PURE__ */ new Set();
    for (const useName of view.uses) {
      unionInto(selected, resolveUsedViewSelection(model, graph, useName, visited));
    }
  }
  if (view.select?.length) {
    unionInto(selected, resolveExplicitRefs(graph, view.select, context));
  }
  if (view.include.length) {
    if (view.include.some((r) => r.ref === "*")) {
      selected = new Set(Object.keys(graph.elements));
    } else {
      unionInto(selected, resolveExplicitRefs(graph, view.include, context));
    }
  }
  if (view.includePredicates?.length) {
    for (const pred of view.includePredicates) {
      unionInto(selected, evalPredicateToIds(graph, pred));
    }
  }
  if (view.exclude.length) {
    for (const id of resolveExplicitRefs(graph, view.exclude, context)) selected.delete(id);
  }
  if (view.excludePredicates?.length) {
    for (const pred of view.excludePredicates) {
      for (const id of evalPredicateToIds(graph, pred)) selected.delete(id);
    }
  }
  if (view.filter) {
    selected = new Set(Array.from(selected).filter((id) => passesViewFilter(graph.index.byId[id], view.filter)));
  }
  return selected;
}
function resolveUsedViewSelection(model, graph, viewName, visited) {
  if (visited.has(viewName)) return /* @__PURE__ */ new Set();
  visited.add(viewName);
  const viewId = graph.index.byViewName[viewName];
  if (!viewId) return /* @__PURE__ */ new Set();
  const view = model.allViews.find((v) => v.id === viewId);
  if (!view) return /* @__PURE__ */ new Set();
  return resolveViewSelection(model, graph, view);
}
function resolveViewEdges(model, graph, view, nodeIds) {
  const mode = view.relMode ?? "incidentToSelected";
  if (mode === "none") return /* @__PURE__ */ new Set();
  const scopeQualifiedName = parentQualifiedName(view.qualifiedName);
  const context = { scopeQualifiedName };
  const edgeIds = /* @__PURE__ */ new Set();
  for (const rel of model.allRelationships) {
    const from = resolveElementRef(graph, rel.from, context);
    const to = resolveElementRef(graph, rel.to, context);
    if (!from || !to) continue;
    if (mode === "all") edgeIds.add(rel.id);
    else if (mode === "betweenSelected" && nodeIds.has(from) && nodeIds.has(to)) edgeIds.add(rel.id);
    else if (mode === "incidentToSelected" && (nodeIds.has(from) || nodeIds.has(to))) edgeIds.add(rel.id);
  }
  for (const usage of model.allPartUsages) {
    const { from, to } = resolveCompositionEndpoints(graph, usage);
    if (!from || !to) continue;
    if (mode === "all") edgeIds.add(usage.id);
    else if (mode === "betweenSelected" && nodeIds.has(from) && nodeIds.has(to)) edgeIds.add(usage.id);
    else if (mode === "incidentToSelected" && (nodeIds.has(from) || nodeIds.has(to))) edgeIds.add(usage.id);
  }
  return edgeIds;
}
function resolveCompositionEndpoints(graph, usage) {
  const parentQn = parentQualifiedName(usage.qualifiedName);
  const from = parentQn ? graph.index.byQualifiedName[parentQn] : void 0;
  const to = usage.typeRef === "?" ? void 0 : resolveByNameOrQualified(graph, normalizeTypeRef(usage.typeRef), { scopeQualifiedName: parentQn });
  const fromIsPart = from ? graph.index.byId[from]?.elementType === "partDef" : false;
  const toIsPart = to ? graph.index.byId[to]?.elementType === "partDef" : false;
  return { from: fromIsPart ? from : void 0, to: toIsPart ? to : void 0 };
}
function resolveExplicitRefs(graph, refs, context) {
  const ids = /* @__PURE__ */ new Set();
  for (const r of refs) {
    if (r.ref === "?" || r.ref === "*") continue;
    const id = resolveByNameOrQualified(graph, r.ref, context);
    if (id) ids.add(id);
  }
  return ids;
}
function resolveElementRef(graph, ref, context) {
  if (ref.ref === "?" || ref.ref === "*") return void 0;
  return resolveByNameOrQualified(graph, ref.ref, context);
}
function resolveByNameOrQualified(graph, refText, context) {
  const raw = refText.startsWith("#") ? refText.slice(1) : refText;
  const byQn = graph.index.byQualifiedName[raw];
  if (byQn) return byQn;
  if (context.scopeQualifiedName && !raw.startsWith(`${context.scopeQualifiedName}.`)) {
    const scoped = `${context.scopeQualifiedName}.${raw}`;
    const byScoped = graph.index.byQualifiedName[scoped];
    if (byScoped) return byScoped;
  }
  const dotted = raw.includes("::") ? raw.replace(/::/g, ".") : void 0;
  if (dotted) {
    const byDotted = graph.index.byQualifiedName[dotted];
    if (byDotted) return byDotted;
  }
  if (!raw.includes(".")) {
    const matches = graph.index.byName[raw] ?? [];
    if (matches.length === 1) return matches[0];
  }
  if (raw.includes(".")) {
    const lastDot = raw.lastIndexOf(".");
    const parent = raw.slice(0, lastDot);
    const child = raw.slice(lastDot + 1);
    const parentId = graph.index.byQualifiedName[parent];
    const portId = graph.index.byQualifiedName[`${parent}.${child}`];
    if (parentId && portId) return portId;
  }
  return void 0;
}
function evalPredicateToIds(graph, predicate) {
  switch (predicate.kind) {
    case "all":
      return new Set(Object.keys(graph.elements));
    case "part":
      return new Set(graph.index.byElementType["partDef"] ?? []);
    case "rel":
      return new Set(graph.index.byElementType["relationship"] ?? []);
    case "tag":
      return new Set(graph.index.byTag[predicate.tag] ?? []);
    case "where": {
      const ids = /* @__PURE__ */ new Set();
      for (const [id, element] of Object.entries(graph.elements)) {
        if (evalExpr(element, predicate.expr)) ids.add(id);
      }
      return ids;
    }
    default:
      return /* @__PURE__ */ new Set();
  }
}
function evalExpr(element, expr) {
  switch (expr.kind) {
    case "or":
      return evalExpr(element, expr.left) || evalExpr(element, expr.right);
    case "and":
      return evalExpr(element, expr.left) && evalExpr(element, expr.right);
    case "not":
      return !evalExpr(element, expr.expr);
    case "cmp": {
      const left = readField(element, expr.field);
      const right = expr.lit;
      switch (expr.op) {
        case "==":
          return looselyEqual(left, right);
        case "!=":
          return !looselyEqual(left, right);
        case "~=":
          return typeof left === "string" && typeof right === "string" ? left.toLowerCase().includes(right.toLowerCase()) : false;
        case "in":
          return isIn(left, right);
        default:
          return false;
      }
    }
    default:
      return false;
  }
}
function readField(element, field) {
  if (!field.length) return void 0;
  const [head, ...rest] = field;
  if (head === "name") return rest.length ? void 0 : element.name;
  if (head === "qualifiedName") return rest.length ? void 0 : element.qualifiedName;
  if (head === "elementType") return rest.length ? void 0 : element.elementType;
  if (head === "tags") return rest.length ? void 0 : element.meta?.tags ?? [];
  if (head === "meta") {
    let current = element.meta ?? {};
    for (const seg of rest) current = current?.[seg];
    return current;
  }
  return void 0;
}
function looselyEqual(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => looselyEqual(v, b[i]));
  if (Array.isArray(a)) return a.some((v) => looselyEqual(v, b));
  if (Array.isArray(b)) return b.some((v) => looselyEqual(a, v));
  return String(a) === String(b);
}
function isIn(left, right) {
  if (Array.isArray(right)) return right.some((v) => looselyEqual(left, v));
  if (Array.isArray(left)) return left.some((v) => looselyEqual(v, right));
  return false;
}
function passesViewFilter(element, filter) {
  if (!element) return false;
  const state = element.meta?.state ?? "unknown";
  const confidence = element.meta?.confidence ?? "unknown";
  const tags = element.meta?.tags ?? [];
  if (filter.states?.length && !filter.states.includes(state)) return false;
  if (filter.confidence?.length && !filter.confidence.includes(confidence)) return false;
  if (filter.tagsAny?.length && !filter.tagsAny.some((t) => tags.includes(t))) return false;
  if (filter.kindsAny?.length) {
    const kind = element.meta?.kind ?? element.elementType;
    if (!filter.kindsAny.some((k) => String(kind) === k)) return false;
  }
  if (filter.relTypesAny?.length && element.elementType === "relationship") {
    const rel = element;
    if (!filter.relTypesAny.some((t) => String(rel.relType) === t)) return false;
  }
  return true;
}
function unionInto(into, from) {
  for (const x of from) into.add(x);
}
function parentQualifiedName(qualifiedName) {
  const idx = qualifiedName.lastIndexOf(".");
  if (idx < 0) return void 0;
  return qualifiedName.slice(0, idx);
}
function multiplicityLabel(multiplicity) {
  if (!multiplicity) return void 0;
  const { lower, upper } = multiplicity;
  return lower === upper ? `[${lower}]` : `[${lower}..${upper}]`;
}
function normalizeTypeRef(typeRef) {
  return typeRef.replace(/::/g, ".");
}

// src/parser.ts
var KEYWORDS = {
  package: "PACKAGE",
  system: "PACKAGE",
  part: "PART",
  def: "DEF",
  action: "ACTION",
  perform: "PERFORM",
  flow: "FLOW",
  of: "OF",
  from: "FROM",
  to: "TO",
  doc: "DOC",
  expose: "EXPOSE",
  connection: "CONNECTION",
  item: "ITEM",
  private: "PRIVATE",
  public: "PUBLIC",
  protected: "PROTECTED",
  meta: "META",
  attribute: "PROP",
  prop: "PROP",
  port: "PORT",
  tag: "TAG",
  in: "IN",
  out: "OUT",
  inout: "INOUT",
  enum: "ENUM",
  type: "TYPE",
  let: "LET",
  import: "IMPORT",
  rel: "REL",
  view: "VIEW",
  rule: "RULE",
  use: "USE",
  select: "SELECT",
  include: "INCLUDE",
  exclude: "EXCLUDE",
  applies: "APPLIES",
  assert: "ASSERT",
  severity: "SEVERITY",
  message: "MESSAGE",
  rels: "RELS",
  layout: "LAYOUT",
  where: "WHERE",
  and: "AND",
  or: "OR",
  not: "NOT",
  all: "ALL",
  requirement: "REQUIREMENT",
  verification: "VERIFICATION",
  subject: "SUBJECT",
  satisfy: "SATISFY",
  state: "STATE",
  transition: "TRANSITION",
  then: "THEN",
  when: "WHEN",
  constraint: "CONSTRAINT",
  allocation: "ALLOCATION",
  interface: "INTERFACE",
  analysis: "ANALYSIS",
  calc: "CALC",
  viewpoint: "VIEWPOINT",
  rendering: "RENDERING",
  occurrence: "OCCURRENCE",
  metadata: "METADATA",
  verify: "VERIFY",
  exhibit: "EXHIBIT",
  allocate: "ALLOCATE",
  end: "END",
  case: "CASE",
  entry: "ENTRY",
  exit: "EXIT",
  do: "DO",
  actor: "ACTOR",
  objective: "OBJECTIVE"
};
var Lexer = class {
  source;
  pos = 0;
  line = 1;
  column = 1;
  tokens = [];
  uri;
  lastNonTriviaTokenType;
  constructor(source, uri = "sysml:document") {
    this.source = source;
    this.uri = uri;
  }
  tokenize() {
    while (!this.isAtEnd()) {
      this.scanToken();
    }
    this.tokens.push({
      type: "EOF",
      value: "",
      line: this.line,
      column: this.column,
      startOffset: this.pos,
      endOffset: this.pos
    });
    return this.tokens;
  }
  isAtEnd() {
    return this.pos >= this.source.length;
  }
  peek() {
    return this.source[this.pos] ?? "\0";
  }
  peekNext() {
    return this.source[this.pos + 1] ?? "\0";
  }
  advance() {
    const ch = this.source[this.pos++];
    if (ch === "\n") {
      this.line++;
      this.column = 1;
    } else {
      this.column++;
    }
    return ch;
  }
  addToken(type, value, line, column, startOffset, endOffset) {
    this.tokens.push({ type, value, line, column, startOffset, endOffset });
    if (type !== "EOF") this.lastNonTriviaTokenType = type;
  }
  scanToken() {
    this.skipWhitespaceAndComments();
    if (this.isAtEnd()) return;
    const startLine = this.line;
    const startColumn = this.column;
    const startOffset = this.pos;
    const ch = this.advance();
    switch (ch) {
      case "{":
        this.addToken("LBRACE", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "}":
        this.addToken("RBRACE", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "[":
        this.addToken("LBRACKET", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "]":
        this.addToken("RBRACKET", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "(":
        this.addToken("LPAREN", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case ")":
        this.addToken("RPAREN", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "@":
        this.addToken("AT", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "#":
        this.addToken("HASH", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case ":":
        if (this.peek() === ":") {
          this.advance();
          this.addToken("SCOPE", "::", startLine, startColumn, startOffset, this.pos);
        } else if (this.peek() === ">") {
          this.advance();
          this.addToken("REDEFINES", ":>", startLine, startColumn, startOffset, this.pos);
        } else {
          this.addToken("COLON", ch, startLine, startColumn, startOffset, this.pos);
        }
        break;
      case ";":
        this.addToken("SEMICOLON", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "*":
        this.addToken("STAR", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case ",":
        this.addToken("COMMA", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case "?":
        this.addToken("QUESTION", ch, startLine, startColumn, startOffset, this.pos);
        break;
      case '"':
        this.scanString(startLine, startColumn, startOffset);
        break;
      case "-":
        if (this.peek() === ">") {
          this.advance();
          this.addToken("ARROW", "->", startLine, startColumn, startOffset, this.pos);
        } else {
          this.addToken("UNKNOWN", ch, startLine, startColumn, startOffset, this.pos);
        }
        break;
      case "=":
        if (this.peek() === "=") {
          this.advance();
          this.addToken("EQEQ", "==", startLine, startColumn, startOffset, this.pos);
        } else {
          this.addToken("EQ", "=", startLine, startColumn, startOffset, this.pos);
        }
        break;
      case "!":
        if (this.peek() === "=") {
          this.advance();
          this.addToken("NEQ", "!=", startLine, startColumn, startOffset, this.pos);
        } else {
          this.addToken("UNKNOWN", ch, startLine, startColumn, startOffset, this.pos);
        }
        break;
      case "~":
        if (this.peek() === "=") {
          this.advance();
          this.addToken("MATCH", "~=", startLine, startColumn, startOffset, this.pos);
        } else {
          this.addToken("TILDE", ch, startLine, startColumn, startOffset, this.pos);
        }
        break;
      case ".":
        if (this.peek() === ".") {
          this.advance();
          this.addToken("DOTDOT", "..", startLine, startColumn, startOffset, this.pos);
        } else {
          this.addToken("DOT", ".", startLine, startColumn, startOffset, this.pos);
        }
        break;
      default:
        if (this.isDigit(ch)) {
          this.scanNumber(ch, startLine, startColumn, startOffset);
        } else if (this.isAlpha(ch)) {
          this.scanIdentifier(ch, startLine, startColumn, startOffset);
        } else if (ch === "/" && this.peek() === "*") {
          if (this.lastNonTriviaTokenType === "DOC") {
            this.advance();
            const text = this.scanBlockCommentText();
            this.addToken("STRING", text, startLine, startColumn, startOffset, this.pos);
          } else {
            this.advance();
            this.skipBlockComment();
          }
        } else {
          this.addToken("UNKNOWN", ch, startLine, startColumn, startOffset, this.pos);
        }
    }
  }
  skipWhitespaceAndComments() {
    while (!this.isAtEnd()) {
      const ch = this.peek();
      if (ch === " " || ch === "	" || ch === "\r" || ch === "\n") {
        this.advance();
      } else if (ch === "/" && this.peekNext() === "/") {
        while (!this.isAtEnd() && this.peek() !== "\n") {
          this.advance();
        }
      } else if (ch === "/" && this.peekNext() === "*") {
        if (this.lastNonTriviaTokenType === "DOC") break;
        this.advance();
        this.advance();
        this.skipBlockComment();
      } else {
        break;
      }
    }
  }
  skipBlockComment() {
    while (!this.isAtEnd() && !(this.peek() === "*" && this.peekNext() === "/")) {
      this.advance();
    }
    if (!this.isAtEnd()) {
      this.advance();
      this.advance();
    }
  }
  scanBlockCommentText() {
    let value = "";
    while (!this.isAtEnd() && !(this.peek() === "*" && this.peekNext() === "/")) {
      value += this.advance();
    }
    if (!this.isAtEnd()) {
      this.advance();
      this.advance();
    }
    return value.trim();
  }
  isDigit(ch) {
    return ch >= "0" && ch <= "9";
  }
  isAlpha(ch) {
    return ch >= "a" && ch <= "z" || ch >= "A" && ch <= "Z" || ch === "_";
  }
  isAlphaNumeric(ch) {
    return this.isAlpha(ch) || this.isDigit(ch) || ch === "-";
  }
  scanNumber(first, startLine, startColumn, startOffset) {
    let value = first;
    while (!this.isAtEnd() && this.isDigit(this.peek())) {
      value += this.advance();
    }
    if (this.peek() === "." && this.isDigit(this.peekNext())) {
      value += this.advance();
      while (!this.isAtEnd() && this.isDigit(this.peek())) {
        value += this.advance();
      }
    }
    this.addToken("NUMBER", value, startLine, startColumn, startOffset, this.pos);
  }
  scanIdentifier(first, startLine, startColumn, startOffset) {
    let value = first;
    while (!this.isAtEnd() && this.isAlphaNumeric(this.peek())) {
      value += this.advance();
    }
    const type = KEYWORDS[value.toLowerCase()] ?? "IDENTIFIER";
    this.addToken(type, value, startLine, startColumn, startOffset, this.pos);
  }
  scanString(startLine, startColumn, startOffset) {
    let value = "";
    while (!this.isAtEnd()) {
      const ch = this.advance();
      if (ch === '"') {
        this.addToken("STRING", value, startLine, startColumn, startOffset, this.pos);
        return;
      }
      if (ch === "\\") {
        const next = this.advance();
        switch (next) {
          case "n":
            value += "\n";
            break;
          case "t":
            value += "	";
            break;
          case '"':
            value += '"';
            break;
          case "\\":
            value += "\\";
            break;
          default:
            value += next;
            break;
        }
      } else {
        value += ch;
      }
    }
    this.addToken("STRING", value, startLine, startColumn, startOffset, this.pos);
  }
};
var IDENTIFIER_LIKE_TOKENS = /* @__PURE__ */ new Set([
  "IDENTIFIER",
  "PACKAGE",
  "PART",
  "DEF",
  "ACTION",
  "PERFORM",
  "FLOW",
  "OF",
  "FROM",
  "TO",
  "DOC",
  "EXPOSE",
  "CONNECTION",
  "ITEM",
  "PRIVATE",
  "PUBLIC",
  "PROTECTED",
  "META",
  "PROP",
  "PORT",
  "TAG",
  "IN",
  "OUT",
  "INOUT",
  "ENUM",
  "TYPE",
  "LET",
  "IMPORT",
  "REL",
  "VIEW",
  "RULE",
  "USE",
  "SELECT",
  "INCLUDE",
  "EXCLUDE",
  "APPLIES",
  "ASSERT",
  "SEVERITY",
  "MESSAGE",
  "RELS",
  "LAYOUT",
  "WHERE",
  "AND",
  "OR",
  "NOT",
  "ALL",
  "REQUIREMENT",
  "VERIFICATION",
  "SUBJECT",
  "SATISFY",
  "STATE",
  "TRANSITION",
  "THEN",
  "WHEN",
  "ENTRY",
  "EXIT",
  "DO",
  "CONSTRAINT",
  "ACTOR",
  "OBJECTIVE"
]);
var Parser = class {
  tokens;
  pos = 0;
  diagnostics = [];
  uri;
  sourceText;
  constructor(tokens, uri = "sysml:document", sourceText = "") {
    this.tokens = tokens;
    this.uri = uri;
    this.sourceText = sourceText;
  }
  parse() {
    const packages = [];
    while (!this.isAtEnd()) {
      try {
        if (this.check("PACKAGE")) {
          packages.push(this.parsePackage(null));
        } else if (this.check("PART")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.partDefs.push(this.parsePartDef(topPkg.qualifiedName));
        } else if (this.check("ACTION")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.actionDefs.push(this.parseActionDef(topPkg.qualifiedName));
        } else if (this.check("PORT") && this.checkNext("DEF")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.portDefs.push(this.parsePortDef(topPkg.qualifiedName));
        } else if (this.check("ITEM") && this.checkNext("DEF")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.itemDefs.push(this.parseItemDef(topPkg.qualifiedName));
        } else if (this.check("REL")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.relationships.push(this.parseRelationship(topPkg.qualifiedName));
        } else if (this.check("CONNECTION") && this.checkNext("DEF")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.connectionDefs.push(this.parseConnectionDef(topPkg.qualifiedName));
        } else if (this.check("CONNECTION")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.relationships.push(this.parseConnection(topPkg.qualifiedName));
        } else if (this.check("VIEW")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.views.push(this.parseView(topPkg.qualifiedName));
        } else if (this.check("RULE")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.rules.push(this.parseRule(topPkg.qualifiedName));
        } else if (this.check("ENUM")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.enums.push(this.parseEnum(topPkg.qualifiedName));
        } else if (this.check("TYPE")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.typeAliases.push(this.parseTypeAlias(topPkg.qualifiedName));
        } else if (this.check("LET")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.lets.push(this.parseLet(topPkg.qualifiedName));
        } else if (this.check("IMPORT")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.imports.push(this.parseImport(topPkg.qualifiedName));
        } else if (this.check("ALLOCATION")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.allocationDefs.push(this.parseAllocationDef(topPkg.qualifiedName));
        } else if (this.check("USE") && this.checkNext("CASE")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.useCaseDefs.push(this.parseUseCaseDef(topPkg.qualifiedName));
        } else if (this.check("INTERFACE") && this.checkNext("DEF")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.interfaceDefs.push(this.parseInterfaceDef(topPkg.qualifiedName));
        } else if (this.check("ANALYSIS")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.analysisDefs.push(this.parseAnalysisDef(topPkg.qualifiedName));
        } else if (this.check("CALC")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.calcDefs.push(this.parseCalcDef(topPkg.qualifiedName));
        } else if (this.check("VIEWPOINT")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.viewpointDefs.push(this.parseViewpointDef(topPkg.qualifiedName));
        } else if (this.check("RENDERING")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.renderingDefs.push(this.parseRenderingDef(topPkg.qualifiedName));
        } else if (this.check("OCCURRENCE")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.occurrenceDefs.push(this.parseOccurrenceDef(topPkg.qualifiedName));
        } else if (this.check("METADATA") && this.checkNext("DEF")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.metadataDefs.push(this.parseMetadataDefBlock(topPkg.qualifiedName));
        } else if (this.check("SATISFY")) {
          const topPkg = this.getOrCreateDefaultPackage(packages);
          topPkg.relationships.push(this.parseSatisfy(topPkg.qualifiedName));
        } else {
          const token = this.advance();
          if (token.type !== "EOF") {
            this.addDiagnostic("warning", `Unexpected token: ${token.value}`, token);
          }
        }
      } catch {
        this.synchronize();
      }
    }
    const model = this.buildModel(packages);
    return { model, diagnostics: this.diagnostics };
  }
  buildModel(packages) {
    const allPartDefs = [];
    const allPartUsages = [];
    const allActionDefs = [];
    const allActionUsages = [];
    const allPerforms = [];
    const allFlows = [];
    const allPortDefs = [];
    const allItemDefs = [];
    const allPorts = [];
    const allProps = [];
    const allStateDefs = [];
    const allStateUsages = [];
    const allTransitions = [];
    const allRequirementDefs = [];
    const allRequirementUsages = [];
    const allSatisfies = [];
    const allConstraintDefs = [];
    const allConstraintUsages = [];
    const allAsserts = [];
    const allConnectionDefs = [];
    const allConnections = [];
    const allAllocationDefs = [];
    const allAllocations = [];
    const allUseCaseDefs = [];
    const allUseCaseUsages = [];
    const allActorUsages = [];
    const allIncludeUseCases = [];
    const allInterfaceDefs = [];
    const allInterfaceUsages = [];
    const allVerificationDefs = [];
    const allVerificationUsages = [];
    const allAnalysisDefs = [];
    const allAnalysisUsages = [];
    const allCalcDefs = [];
    const allCalcUsages = [];
    const allViewpointDefs = [];
    const allViewpointUsages = [];
    const allRenderingDefs = [];
    const allRenderingUsages = [];
    const allMetadataDefs = [];
    const allOccurrenceDefs = [];
    const allOccurrenceUsages = [];
    const allEnums = [];
    const allTypeAliases = [];
    const allLets = [];
    const allImports = [];
    const allRelationships = [];
    const allViews = [];
    const allRules = [];
    const allElements = [];
    const collectFromPackage = (pkg) => {
      allElements.push(pkg);
      for (const pd of pkg.partDefs) {
        allPartDefs.push(pd);
        allElements.push(pd);
        allPartUsages.push(...pd.parts);
        allElements.push(...pd.parts);
        if (pd.imports?.length) {
          allImports.push(...pd.imports);
          allElements.push(...pd.imports);
        }
        if (pd.actions?.length) {
          allActionUsages.push(...pd.actions);
          allElements.push(...pd.actions);
        }
        if (pd.performs?.length) {
          allPerforms.push(...pd.performs);
          allElements.push(...pd.performs);
        }
        if (pd.ports?.length) {
          allPorts.push(...pd.ports);
          allElements.push(...pd.ports);
        }
        if (pd.props?.length) {
          allProps.push(...pd.props);
          allElements.push(...pd.props);
        }
        if (pd.relationships?.length) {
          allRelationships.push(...pd.relationships);
          allElements.push(...pd.relationships);
        }
      }
      if (pkg.partUsages.length) {
        allPartUsages.push(...pkg.partUsages);
        allElements.push(...pkg.partUsages);
      }
      allActionDefs.push(...pkg.actionDefs);
      allElements.push(...pkg.actionDefs);
      for (const ad of pkg.actionDefs) {
        if (ad.actions?.length) {
          allActionUsages.push(...ad.actions);
          allElements.push(...ad.actions);
        }
        if (ad.flows?.length) {
          allFlows.push(...ad.flows);
          allElements.push(...ad.flows);
        }
        if (ad.performs?.length) {
          allPerforms.push(...ad.performs);
          allElements.push(...ad.performs);
        }
      }
      allPortDefs.push(...pkg.portDefs);
      allElements.push(...pkg.portDefs);
      allItemDefs.push(...pkg.itemDefs);
      allElements.push(...pkg.itemDefs);
      allStateDefs.push(...pkg.stateDefs);
      allElements.push(...pkg.stateDefs);
      for (const sd of pkg.stateDefs) {
        if (sd.states?.length) {
          allStateUsages.push(...sd.states);
          allElements.push(...sd.states);
        }
        if (sd.transitions?.length) {
          allTransitions.push(...sd.transitions);
          allElements.push(...sd.transitions);
        }
      }
      allRequirementDefs.push(...pkg.requirementDefs);
      allElements.push(...pkg.requirementDefs);
      for (const rd of pkg.requirementDefs) {
        if (rd.subrequirements?.length) {
          allRequirementUsages.push(...rd.subrequirements);
          allElements.push(...rd.subrequirements);
        }
        if (rd.constraints?.length) {
          allConstraintUsages.push(...rd.constraints);
          allElements.push(...rd.constraints);
        }
      }
      allConstraintDefs.push(...pkg.constraintDefs);
      allElements.push(...pkg.constraintDefs);
      allConnectionDefs.push(...pkg.connectionDefs);
      allElements.push(...pkg.connectionDefs);
      allConnections.push(...pkg.connections);
      allElements.push(...pkg.connections);
      allAllocationDefs.push(...pkg.allocationDefs);
      allElements.push(...pkg.allocationDefs);
      allAllocations.push(...pkg.allocations);
      allElements.push(...pkg.allocations);
      allUseCaseDefs.push(...pkg.useCaseDefs);
      allElements.push(...pkg.useCaseDefs);
      for (const uc of pkg.useCaseDefs) {
        if (uc.actors?.length) {
          allActorUsages.push(...uc.actors);
          allElements.push(...uc.actors);
        }
        if (uc.includes?.length) {
          allIncludeUseCases.push(...uc.includes);
          allElements.push(...uc.includes);
        }
      }
      allInterfaceDefs.push(...pkg.interfaceDefs);
      allElements.push(...pkg.interfaceDefs);
      allVerificationDefs.push(...pkg.verificationDefs);
      allElements.push(...pkg.verificationDefs);
      for (const vd of pkg.verificationDefs) {
        if (vd.actions?.length) {
          allActionUsages.push(...vd.actions);
          allElements.push(...vd.actions);
        }
      }
      allAnalysisDefs.push(...pkg.analysisDefs);
      allElements.push(...pkg.analysisDefs);
      allCalcDefs.push(...pkg.calcDefs);
      allElements.push(...pkg.calcDefs);
      allViewpointDefs.push(...pkg.viewpointDefs);
      allElements.push(...pkg.viewpointDefs);
      allRenderingDefs.push(...pkg.renderingDefs);
      allElements.push(...pkg.renderingDefs);
      allMetadataDefs.push(...pkg.metadataDefs);
      allElements.push(...pkg.metadataDefs);
      allOccurrenceDefs.push(...pkg.occurrenceDefs);
      allElements.push(...pkg.occurrenceDefs);
      allSatisfies.push(...pkg.satisfies);
      allElements.push(...pkg.satisfies);
      allAsserts.push(...pkg.asserts);
      allElements.push(...pkg.asserts);
      allRelationships.push(...pkg.relationships);
      allViews.push(...pkg.views);
      allRules.push(...pkg.rules);
      allElements.push(...pkg.relationships);
      allElements.push(...pkg.views);
      allElements.push(...pkg.rules);
      allEnums.push(...pkg.enums);
      allTypeAliases.push(...pkg.typeAliases);
      allLets.push(...pkg.lets);
      allImports.push(...pkg.imports);
      allElements.push(...pkg.enums);
      allElements.push(...pkg.typeAliases);
      allElements.push(...pkg.lets);
      allElements.push(...pkg.imports);
      for (const nested of pkg.packages) {
        collectFromPackage(nested);
      }
    };
    for (const pkg of packages) {
      collectFromPackage(pkg);
    }
    return {
      packages,
      // Core
      allPartDefs,
      allPartUsages,
      allActionDefs,
      allActionUsages,
      allPerforms,
      allFlows,
      allPortDefs,
      allItemDefs,
      allPorts,
      allProps,
      // State Machine
      allStateDefs,
      allStateUsages,
      allTransitions,
      // Requirements
      allRequirementDefs,
      allRequirementUsages,
      allSatisfies,
      // Constraints
      allConstraintDefs,
      allConstraintUsages,
      allAsserts,
      // Connections
      allConnectionDefs,
      allConnections,
      // Allocations
      allAllocationDefs,
      allAllocations,
      // Use Cases
      allUseCaseDefs,
      allUseCaseUsages,
      allActorUsages,
      allIncludeUseCases,
      // Interfaces
      allInterfaceDefs,
      allInterfaceUsages,
      // Verification
      allVerificationDefs,
      allVerificationUsages,
      // Analysis
      allAnalysisDefs,
      allAnalysisUsages,
      // Calculations
      allCalcDefs,
      allCalcUsages,
      // Viewpoints & Rendering
      allViewpointDefs,
      allViewpointUsages,
      allRenderingDefs,
      allRenderingUsages,
      // Metadata & Occurrences
      allMetadataDefs,
      allOccurrenceDefs,
      allOccurrenceUsages,
      // Other
      allEnums,
      allTypeAliases,
      allLets,
      allImports,
      allRelationships,
      allViews,
      allRules,
      allElements
    };
  }
  getOrCreateDefaultPackage(packages) {
    let defaultPkg = packages.find((p) => p.name === "$default");
    if (!defaultPkg) {
      const qualifiedName = "$default";
      defaultPkg = (0, model_exports.createEmptyPackage)(
        this.deriveId("package", qualifiedName),
        "$default",
        qualifiedName
      );
      packages.push(defaultPkg);
    }
    return defaultPkg;
  }
  parsePackage(parentQualifiedName2) {
    const pkgToken = this.consume("PACKAGE", 'Expected "package" or "system"');
    const nameToken = this.consume("IDENTIFIER", "Expected package name");
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    this.consume("LBRACE", 'Expected "{"');
    const partDefs = [];
    const partUsages = [];
    const actionDefs = [];
    const portDefs = [];
    const itemDefs = [];
    const nestedPackages = [];
    const relationships = [];
    const views = [];
    const rules = [];
    const enums = [];
    const typeAliases = [];
    const lets = [];
    const imports = [];
    const requirementDefs = [];
    const verificationDefs = [];
    const stateDefs = [];
    const constraintDefs = [];
    const connectionDefs = [];
    const allocationDefs = [];
    const useCaseDefs = [];
    const interfaceDefs = [];
    const analysisDefs = [];
    const calcDefs = [];
    const viewpointDefs = [];
    const renderingDefs = [];
    const metadataDefs = [];
    const occurrenceDefs = [];
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      if (this.check("PACKAGE")) {
        nestedPackages.push(this.parsePackage(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("PART")) {
        if (this.checkNext("DEF")) {
          partDefs.push(this.parsePartDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
        } else if (this.checkNext("IDENTIFIER")) {
          const afterName = this.pos + 2 < this.tokens.length ? this.tokens[this.pos + 2] : void 0;
          if (afterName && afterName.type === "COLON") {
            partUsages.push(this.parsePartUsage(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
          } else {
            partDefs.push(this.parsePartDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
          }
        } else {
          partDefs.push(this.parsePartDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
        }
      } else if (this.check("ACTION")) {
        actionDefs.push(this.parseActionDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("STATE")) {
        stateDefs.push(this.parseStateDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("CONSTRAINT")) {
        constraintDefs.push(this.parseConstraintDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("PORT") && this.checkNext("DEF")) {
        portDefs.push(this.parsePortDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("ITEM") && this.checkNext("DEF")) {
        itemDefs.push(this.parseItemDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("REL")) {
        relationships.push(this.parseRelationship(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("CONNECTION") && this.checkNext("DEF")) {
        connectionDefs.push(this.parseConnectionDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("CONNECTION")) {
        relationships.push(this.parseConnection(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("VIEW")) {
        views.push(this.parseView(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("RULE")) {
        rules.push(this.parseRule(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("ENUM")) {
        enums.push(this.parseEnum(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("TYPE")) {
        typeAliases.push(this.parseTypeAlias(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("LET")) {
        lets.push(this.parseLet(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("IMPORT")) {
        imports.push(this.parseImport(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("REQUIREMENT")) {
        requirementDefs.push(this.parseRequirementDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("VERIFICATION")) {
        verificationDefs.push(this.parseVerificationDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("ALLOCATION")) {
        allocationDefs.push(this.parseAllocationDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("USE") && this.checkNext("CASE")) {
        useCaseDefs.push(this.parseUseCaseDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("INTERFACE") && this.checkNext("DEF")) {
        interfaceDefs.push(this.parseInterfaceDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("ANALYSIS")) {
        analysisDefs.push(this.parseAnalysisDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("CALC")) {
        calcDefs.push(this.parseCalcDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("VIEWPOINT")) {
        viewpointDefs.push(this.parseViewpointDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("RENDERING")) {
        renderingDefs.push(this.parseRenderingDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("OCCURRENCE")) {
        occurrenceDefs.push(this.parseOccurrenceDef(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("METADATA") && this.checkNext("DEF")) {
        metadataDefs.push(this.parseMetadataDefBlock(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("SATISFY")) {
        relationships.push(this.parseSatisfy(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("VERIFY")) {
        relationships.push(this.parseVerify(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("ALLOCATE")) {
        relationships.push(this.parseAllocateUsage(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("EXHIBIT")) {
        relationships.push(this.parseExhibit(this.joinQualifiedName(parentQualifiedName2, nameToken.value)));
      } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
        this.parseMetaStmtInto(metaProps);
      } else if (this.check("SEMICOLON")) {
        this.advance();
      } else {
        const token = this.advance();
        this.addDiagnostic("warning", `Unexpected token in package: ${token.value}`, token);
      }
    }
    const endToken = this.consume("RBRACE", 'Expected "}"');
    const qualifiedName = this.joinQualifiedName(parentQualifiedName2, nameToken.value);
    return {
      id: this.stableId("package", qualifiedName, explicitId),
      elementType: "package",
      name: nameToken.value,
      qualifiedName,
      partDefs,
      partUsages,
      actionDefs,
      portDefs,
      itemDefs,
      // New type collections
      stateDefs,
      requirementDefs,
      constraintDefs,
      connectionDefs,
      connections: [],
      allocationDefs,
      allocations: [],
      useCaseDefs,
      interfaceDefs,
      verificationDefs,
      analysisDefs,
      calcDefs,
      viewDefs: views,
      viewpointDefs,
      renderingDefs,
      metadataDefs,
      occurrenceDefs,
      satisfies: [],
      asserts: [],
      // Existing collections
      relationships,
      views,
      rules,
      enums,
      typeAliases,
      lets,
      imports,
      packages: nestedPackages,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(pkgToken, endToken)
    };
  }
  parsePartDef(packageQualifiedName) {
    const partToken = this.consume("PART", 'Expected "part"');
    const isLegacyDef = this.check("DEF");
    if (isLegacyDef) this.advance();
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike("Expected part name");
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    let explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    const parts = [];
    const imports = [];
    const ports = [];
    const props = [];
    const relationships = [];
    const actions = [];
    const performs = [];
    const tags = [];
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      const partQualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("REL")) {
          relationships.push(this.parseRelationship(partQualifiedName));
          continue;
        }
        if (this.check("CONNECTION")) {
          relationships.push(this.parseConnection(partQualifiedName));
          continue;
        }
        if (this.check("PRIVATE") || this.check("PUBLIC") || this.check("PROTECTED") || this.check("IMPORT")) {
          imports.push(this.parseImport(partQualifiedName));
          continue;
        }
        if (this.check("PART") && this.checkNext("DEF")) {
          this.addDiagnostic(
            "warning",
            "Nested part definitions not supported in this grammar version",
            this.peek()
          );
          this.synchronizeToRbrace();
          continue;
        }
        if (this.check("PART") && this.checkNext("IDENTIFIER")) {
          parts.push(this.parsePartUsage(partQualifiedName));
          continue;
        }
        if (this.check("PROP")) {
          props.push(this.parseProp(partQualifiedName));
          continue;
        }
        if (this.check("PORT")) {
          if (this.checkNext("DEF")) {
            this.addDiagnostic("warning", "port def declarations are only supported at package scope", this.peek());
            this.synchronizeToStatementEnd();
            continue;
          }
          ports.push(this.parsePort(partQualifiedName));
          continue;
        }
        if (this.check("ACTION")) {
          actions.push(this.parseActionUsage(partQualifiedName));
          continue;
        }
        if (this.check("PERFORM")) {
          const performed = this.parsePerformInContainer(partQualifiedName);
          if (performed.actionUsage) actions.push(performed.actionUsage);
          if (performed.perform) performs.push(performed.perform);
          continue;
        }
        if (this.check("SATISFY")) {
          relationships.push(this.parseSatisfy(partQualifiedName));
          continue;
        }
        if (this.check("VERIFY")) {
          relationships.push(this.parseVerify(partQualifiedName));
          continue;
        }
        if (this.check("ALLOCATE")) {
          relationships.push(this.parseAllocateUsage(partQualifiedName));
          continue;
        }
        if (this.check("EXHIBIT")) {
          relationships.push(this.parseExhibit(partQualifiedName));
          continue;
        }
        if (this.check("TAG")) {
          tags.push(this.parseTag());
          continue;
        }
        if (this.check("DOC")) {
          this.parseDocInto(metaProps);
          continue;
        }
        if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          if (this.check("META") && this.checkNext("COLON")) this.parseMetaTagsAssignmentInto(metaProps);
          else this.parseMetaStmtInto(metaProps);
          continue;
        }
        if ((this.check("IDENTIFIER") || this.check("META")) && this.checkNext("COLON")) {
          const { key, value } = this.parsePropertyAssignment();
          if (key === "id" && explicitId === void 0) {
            const raw = typeof value === "string" ? value : String(value);
            explicitId = raw.startsWith("@") ? raw : `@${raw}`;
          } else if (key === "meta") {
            metaProps["tags"] = this.toStringArray(value);
          } else {
            metaProps[key] = value;
          }
          continue;
        }
        if (this.check("SEMICOLON")) {
          this.advance();
          continue;
        }
        const token = this.advance();
        if (token.type !== "RBRACE") {
          this.addDiagnostic("warning", `Unexpected token in part: ${token.value}`, token);
        }
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    const meta = this.buildMetadata(metaProps) ?? (tags.length ? { tags: [] } : void 0);
    if (meta && tags.length) {
      meta.tags = [.../* @__PURE__ */ new Set([...meta.tags ?? [], ...tags])];
    }
    const partDef = {
      id: this.stableId("partDef", qualifiedName, explicitId),
      elementType: "partDef",
      name: nameToken.value,
      qualifiedName,
      parts,
      imports: imports.length ? imports : void 0,
      ...supertype ? { supertype } : {},
      actions: actions.length ? actions : void 0,
      performs: performs.length ? performs : void 0,
      meta,
      source: this.rangeFrom(partToken, endToken)
    };
    if (ports.length) partDef.ports = ports;
    if (props.length) partDef.props = props;
    if (relationships.length) partDef.relationships = relationships;
    if (tags.length) partDef.tags = [...new Set(tags)];
    return partDef;
  }
  // ── requirement def / verification def ────────────────────────────────────
  parseRequirementDef(packageQualifiedName) {
    const startToken = this.consume("REQUIREMENT", 'Expected "requirement"');
    if (this.check("DEF")) this.advance();
    const nameToken = this.consumeIdentifierLike("Expected requirement name");
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const explicitId = this.parseOptId();
    const metaProps = {};
    this.parseOptMetaInto(metaProps);
    let subject;
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("DOC")) {
          this.parseDocInto(metaProps);
        } else if (this.check("SUBJECT")) {
          this.advance();
          if (this.check("COLON")) this.advance();
          const ref = this.consumeIdentifierLike("Expected subject reference");
          subject = { ref: ref.value, source: this.rangeFrom(ref, ref) };
          this.consumeStatementEnd("subject statement", { allowImplicitEndBefore: ["RBRACE", "DOC", "PROP", "REQUIREMENT", "SUBJECT", "META"] });
        } else if (this.check("PROP")) {
          this.synchronizeToStatementEnd();
          if (this.check("SEMICOLON")) this.advance();
        } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          this.parseMetaStmtInto(metaProps);
        } else if (this.check("SEMICOLON")) {
          this.advance();
        } else {
          this.advance();
        }
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    const text = typeof metaProps["notes"] === "string" ? metaProps["notes"] : void 0;
    return {
      id: this.stableId("requirementDef", qualifiedName, explicitId),
      elementType: "requirementDef",
      name: nameToken.value,
      qualifiedName,
      ...supertype ? { supertype } : {},
      ...text ? { text } : {},
      ...subject ? { subject } : {},
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(startToken, endToken)
    };
  }
  parseVerificationDef(packageQualifiedName) {
    const startToken = this.consume("VERIFICATION", 'Expected "verification"');
    if (this.check("DEF")) this.advance();
    const nameToken = this.consumeIdentifierLike("Expected verification name");
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const explicitId = this.parseOptId();
    const metaProps = {};
    this.parseOptMetaInto(metaProps);
    let subject;
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("DOC")) {
          this.parseDocInto(metaProps);
        } else if (this.check("SUBJECT")) {
          this.advance();
          if (this.check("COLON")) this.advance();
          const ref = this.consumeIdentifierLike("Expected subject reference");
          subject = { ref: ref.value, source: this.rangeFrom(ref, ref) };
          this.consumeStatementEnd("subject statement", { allowImplicitEndBefore: ["RBRACE", "DOC", "PROP", "VERIFICATION", "SUBJECT", "META"] });
        } else if (this.check("PROP")) {
          this.synchronizeToStatementEnd();
          if (this.check("SEMICOLON")) this.advance();
        } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          this.parseMetaStmtInto(metaProps);
        } else if (this.check("SEMICOLON")) {
          this.advance();
        } else {
          this.advance();
        }
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    typeof metaProps["notes"] === "string" ? metaProps["notes"] : void 0;
    return {
      id: this.stableId("verificationDef", qualifiedName, explicitId),
      elementType: "verificationDef",
      name: nameToken.value,
      qualifiedName,
      ...supertype ? { supertype } : {},
      ...subject ? { subject } : {},
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(startToken, endToken)
    };
  }
  parsePartUsage(partDefQualifiedName) {
    const partToken = this.consume("PART", 'Expected "part"');
    const nameToken = this.consume("IDENTIFIER", "Expected part usage name");
    let typeRef = "?";
    if (this.check("COLON")) {
      this.advance();
      typeRef = this.parseRefText("Expected type name");
    }
    let multiplicity;
    if (this.check("LBRACKET")) {
      this.advance();
      multiplicity = this.parseMultiplicity();
      this.consume("RBRACKET", 'Expected "]"');
    }
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      let depth = 1;
      while (depth > 0 && !this.isAtEnd()) {
        if (this.check("LBRACE")) depth++;
        else if (this.check("RBRACE")) depth--;
        this.advance();
      }
      endToken = this.tokens[this.pos - 1];
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(partDefQualifiedName, nameToken.value);
    return {
      id: this.deriveId("partUsage", qualifiedName),
      elementType: "partUsage",
      name: nameToken.value,
      qualifiedName,
      typeRef,
      multiplicity,
      source: this.rangeFrom(partToken, endToken)
    };
  }
  parseRelationship(packageQualifiedName) {
    const relToken = this.consume("REL", 'Expected "rel"');
    const first = this.consumeIdentifierLike("Expected relationship type or name");
    if (this.check("COLON")) {
      const nameToken2 = first;
      this.advance();
      const relType = this.parseTypeRefText("Expected relationship type");
      const fromRef2 = this.parseElementRef('Expected relationship "from" reference');
      this.consume("ARROW", 'Expected "->"');
      const toRef2 = this.parseElementRef('Expected relationship "to" reference');
      const metaProps2 = {};
      let explicitId2;
      let endToken2;
      if (this.check("LBRACE")) {
        this.advance();
        while (!this.check("RBRACE") && !this.isAtEnd()) {
          if (this.checkIdentifierLike() && this.checkNext("COLON")) {
            const { key, value } = this.parsePropertyAssignment();
            if (key === "id") {
              const raw = typeof value === "string" ? value : String(value);
              explicitId2 = raw.startsWith("@") ? raw : `@${raw}`;
            } else metaProps2[key] = value;
          } else if (this.check("META")) {
            this.parseMetaStmtInto(metaProps2);
          } else {
            const token = this.advance();
            if (token.type !== "RBRACE") {
              this.addDiagnostic("warning", `Unexpected token in relationship: ${token.value}`, token);
            }
          }
        }
        endToken2 = this.consume("RBRACE", 'Expected "}"');
        if (this.check("SEMICOLON")) endToken2 = this.advance();
      } else {
        endToken2 = this.consumeStatementEnd("relationship declaration", {
          allowImplicitEndBefore: ["REL", "VIEW", "PART", "ENUM", "TYPE", "LET", "IMPORT", "PACKAGE", "META", "RBRACE"]
        });
      }
      const qualifiedName2 = this.joinQualifiedName(packageQualifiedName, nameToken2.value);
      return {
        id: this.stableId("relationship", qualifiedName2, explicitId2),
        elementType: "relationship",
        name: nameToken2.value,
        qualifiedName: qualifiedName2,
        relType,
        from: fromRef2,
        to: toRef2,
        meta: this.buildMetadata(metaProps2),
        source: this.rangeFrom(relToken, endToken2)
      };
    }
    const relTypeToken = first;
    const nameToken = this.consumeIdentifierLike("Expected relationship name");
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    this.consume("COLON", 'Expected ":"');
    const fromRef = this.parseElementRef('Expected relationship "from" reference');
    this.consume("ARROW", 'Expected "->"');
    const toRef = this.parseElementRef('Expected relationship "to" reference');
    const endToken = this.consumeStatementEnd("relationship declaration", {
      allowImplicitEndBefore: ["REL", "VIEW", "PART", "ENUM", "TYPE", "LET", "IMPORT", "PACKAGE", "META", "RBRACE"]
    });
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.stableId("relationship", qualifiedName, explicitId),
      elementType: "relationship",
      name: nameToken.value,
      qualifiedName,
      relType: relTypeToken.value,
      from: fromRef,
      to: toRef,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(relToken, endToken)
    };
  }
  /**
   * Parse a satisfy usage: `satisfy <RequirementRef> by <ElementRef>;`
   * Produces a Relationship with relType 'satisfy'.
   * The `from` is the satisfying element (by), `to` is the requirement.
   */
  parseSatisfy(packageQualifiedName) {
    const satisfyToken = this.consume("SATISFY", 'Expected "satisfy"');
    const requirementRef = this.parseQualifiedIdentifier("Expected requirement reference");
    let byRef = requirementRef;
    if (this.checkIdentifierLike() && this.tokens[this.pos].value === "by") {
      this.advance();
      byRef = this.parseQualifiedIdentifier("Expected satisfying element reference");
    }
    const endToken = this.consumeStatementEnd("satisfy statement", {
      allowImplicitEndBefore: ["SATISFY", "REL", "VIEW", "PART", "REQUIREMENT", "VERIFICATION", "RBRACE"]
    });
    const name = `satisfy_${requirementRef}_by_${byRef}`;
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, name);
    return {
      id: this.stableId("relationship", qualifiedName),
      elementType: "relationship",
      name,
      qualifiedName,
      relType: "satisfy",
      from: { ref: byRef },
      to: { ref: requirementRef },
      source: this.rangeFrom(satisfyToken, endToken)
    };
  }
  /**
   * Parse an allocate usage: `allocate <BehaviorRef> to <StructureRef>;`
   * Produces a Relationship with relType 'allocate'.
   */
  parseAllocateUsage(packageQualifiedName) {
    const allocToken = this.consume("ALLOCATE", 'Expected "allocate"');
    const fromIdent = this.parseQualifiedIdentifier("Expected element reference");
    let toIdent = fromIdent;
    if (this.check("TO")) {
      this.advance();
      toIdent = this.parseQualifiedIdentifier("Expected target element reference");
    }
    const endToken = this.consumeStatementEnd("allocate statement", {
      allowImplicitEndBefore: ["ALLOCATE", "SATISFY", "VERIFY", "REL", "PART", "RBRACE"]
    });
    const name = `allocate_${fromIdent}_to_${toIdent}`;
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, name);
    return {
      id: this.stableId("relationship", qualifiedName),
      elementType: "relationship",
      name,
      qualifiedName,
      relType: "allocate",
      from: { ref: fromIdent },
      to: { ref: toIdent },
      source: this.rangeFrom(allocToken, endToken)
    };
  }
  /**
   * Parse an exhibit statement: `exhibit state <StateRef>;`
   * Produces a Relationship with relType 'exhibit'.
   */
  parseExhibit(packageQualifiedName) {
    const exhibitToken = this.consume("EXHIBIT", 'Expected "exhibit"');
    let kind;
    if (this.check("STATE") || this.check("ACTION")) {
      kind = this.advance().value;
    }
    const ref = this.parseQualifiedIdentifier("Expected element reference");
    const endToken = this.consumeStatementEnd("exhibit statement", {
      allowImplicitEndBefore: ["EXHIBIT", "PART", "PORT", "ACTION", "STATE", "SATISFY", "VERIFY", "RBRACE"]
    });
    const name = `exhibit_${kind ? kind + "_" : ""}${ref}`;
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, name);
    return {
      id: this.stableId("relationship", qualifiedName),
      elementType: "relationship",
      name,
      qualifiedName,
      relType: "exhibit",
      from: { ref },
      to: { ref: packageQualifiedName.split(".").pop() ?? packageQualifiedName },
      source: this.rangeFrom(exhibitToken, endToken)
    };
  }
  /**
   * Parse a verify usage: `verify <RequirementRef> by <VerificationRef>;`
   * Produces a Relationship with relType 'verify'.
   */
  parseVerify(packageQualifiedName) {
    const verifyToken = this.consume("VERIFY", 'Expected "verify"');
    const requirementRef = this.parseQualifiedIdentifier("Expected requirement reference");
    let verificationRef = requirementRef;
    if (this.checkIdentifierLike() && this.tokens[this.pos].value === "by") {
      this.advance();
      verificationRef = this.parseQualifiedIdentifier("Expected verification case reference");
    }
    const endToken = this.consumeStatementEnd("verify statement", {
      allowImplicitEndBefore: ["VERIFY", "SATISFY", "REL", "VIEW", "PART", "REQUIREMENT", "VERIFICATION", "RBRACE"]
    });
    const name = `verify_${requirementRef}_by_${verificationRef}`;
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, name);
    return {
      id: this.stableId("relationship", qualifiedName),
      elementType: "relationship",
      name,
      qualifiedName,
      relType: "verify",
      from: { ref: verificationRef },
      to: { ref: requirementRef },
      source: this.rangeFrom(verifyToken, endToken)
    };
  }
  parseView(packageQualifiedName) {
    const viewToken = this.consume("VIEW", 'Expected "view"');
    if (this.check("DEF")) this.advance();
    const nameToken = this.consumeIdentifierLike("Expected view name");
    const uses = [];
    const select = [];
    const include = [];
    const exclude = [];
    const includePredicates = [];
    const excludePredicates = [];
    let relMode;
    let layout;
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let filterProps;
    this.consume("LBRACE", 'Expected "{"');
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      if (this.check("USE")) {
        this.advance();
        const viewRef = this.parseQualifiedIdentifier('Expected view name after "use"');
        uses.push(viewRef);
        this.consumeStatementEnd("use statement", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("SELECT")) {
        this.advance();
        select.push(...this.parseRefList('Expected element reference after "select"'));
        this.consumeStatementEnd("select statement", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("INCLUDE")) {
        this.advance();
        if (this.isStartOfPredicate()) {
          includePredicates.push(this.parsePredicate());
        } else {
          include.push(...this.parseRefList('Expected element reference after "include"'));
        }
        this.consumeStatementEnd("include statement", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("EXCLUDE")) {
        this.advance();
        if (this.isStartOfPredicate()) {
          excludePredicates.push(this.parsePredicate());
        } else {
          exclude.push(...this.parseRefList('Expected element reference after "exclude"'));
        }
        this.consumeStatementEnd("exclude statement", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("EXPOSE")) {
        this.advance();
        include.push(...this.parseRefList('Expected element reference after "expose"'));
        this.consumeStatementEnd("expose statement", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "EXPOSE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("RELS")) {
        this.advance();
        this.consume("COLON", 'Expected ":"');
        const modeToken = this.consume("IDENTIFIER", "Expected relationship mode");
        if (["none", "betweenSelected", "incidentToSelected", "all"].includes(modeToken.value)) {
          relMode = modeToken.value;
        } else {
          this.addDiagnostic("warning", `Unknown rel mode: ${modeToken.value}`, modeToken);
        }
        this.consumeStatementEnd("rels setting", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("LAYOUT")) {
        this.advance();
        this.consume("COLON", 'Expected ":"');
        const modeToken = this.consume("IDENTIFIER", "Expected layout mode");
        if (["hier", "force", "manual"].includes(modeToken.value)) {
          layout = modeToken.value;
        } else {
          this.addDiagnostic("warning", `Unknown layout mode: ${modeToken.value}`, modeToken);
        }
        this.consumeStatementEnd("layout setting", {
          allowImplicitEndBefore: ["USE", "SELECT", "INCLUDE", "EXCLUDE", "RELS", "LAYOUT", "WHERE", "META", "RBRACE"]
        });
      } else if (this.check("WHERE")) {
        this.advance();
        const { key, value } = this.parsePropertyAssignment();
        filterProps ??= {};
        filterProps[key] = value;
      } else if (this.check("DOC")) {
        this.parseDocInto(metaProps);
      } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
        this.parseMetaStmtInto(metaProps);
      } else if (this.check("SEMICOLON")) {
        this.advance();
      } else {
        const token = this.advance();
        if (token.type !== "RBRACE") {
          this.addDiagnostic("warning", `Unexpected token in view: ${token.value}`, token);
        }
      }
    }
    const endToken = this.consume("RBRACE", 'Expected "}"');
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.stableId("view", qualifiedName, explicitId),
      elementType: "view",
      name: nameToken.value,
      qualifiedName,
      uses,
      select: select.length ? select : void 0,
      include,
      exclude,
      includePredicates: includePredicates.length ? includePredicates : void 0,
      excludePredicates: excludePredicates.length ? excludePredicates : void 0,
      relMode,
      layout,
      filter: filterProps ? this.buildViewFilter(filterProps) : void 0,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(viewToken, endToken)
    };
  }
  parseRule(packageQualifiedName) {
    const ruleToken = this.consume("RULE", 'Expected "rule"');
    const nameToken = this.consume("IDENTIFIER", "Expected rule name");
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let target = "all";
    let where;
    let assertExpr;
    let severity = "warning";
    let message;
    this.consume("LBRACE", 'Expected "{"');
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      if (this.check("APPLIES")) {
        this.advance();
        const tToken = this.consumeIdentifierLike('Expected target after "applies"');
        target = this.mapRuleTarget(tToken.value, tToken);
        if (this.check("WHERE")) {
          this.advance();
          where = this.parseExpr();
        }
        this.consumeStatementEnd("applies statement", {
          allowImplicitEndBefore: ["APPLIES", "ASSERT", "SEVERITY", "MESSAGE", "META", "RBRACE"]
        });
      } else if (this.check("ASSERT")) {
        this.advance();
        assertExpr = this.parseExpr();
        this.consumeStatementEnd("assert statement", {
          allowImplicitEndBefore: ["APPLIES", "ASSERT", "SEVERITY", "MESSAGE", "META", "RBRACE"]
        });
      } else if (this.check("SEVERITY")) {
        this.advance();
        this.consume("COLON", 'Expected ":"');
        const sevToken = this.consumeIdentifierLike("Expected severity value");
        const sev = sevToken.value.toLowerCase();
        if (sev === "error" || sev === "warning" || sev === "info") {
          severity = sev;
        } else {
          this.addDiagnostic("warning", `Unknown rule severity: ${sevToken.value}`, sevToken);
        }
        this.consumeStatementEnd("severity setting", {
          allowImplicitEndBefore: ["APPLIES", "ASSERT", "SEVERITY", "MESSAGE", "META", "RBRACE"]
        });
      } else if (this.check("MESSAGE")) {
        this.advance();
        this.consume("COLON", 'Expected ":"');
        const msgToken = this.check("STRING") ? this.advance() : this.consumeIdentifierLike("Expected message string");
        message = msgToken.value;
        this.consumeStatementEnd("message setting", {
          allowImplicitEndBefore: ["APPLIES", "ASSERT", "SEVERITY", "MESSAGE", "META", "RBRACE"]
        });
      } else if (this.check("META")) {
        this.parseMetaStmtInto(metaProps);
      } else if (this.check("SEMICOLON")) {
        this.advance();
      } else {
        const token = this.advance();
        if (token.type !== "RBRACE") {
          this.addDiagnostic("warning", `Unexpected token in rule: ${token.value}`, token);
        }
      }
    }
    const endToken = this.consume("RBRACE", 'Expected "}"');
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    if (!assertExpr) {
      this.addDiagnostic("error", 'Validation rule must include an "assert" expression', nameToken);
      assertExpr = { kind: "cmp", field: ["id"], op: "!=", lit: "__never__" };
    }
    return {
      id: this.stableId("rule", qualifiedName, explicitId),
      elementType: "rule",
      name: nameToken.value,
      qualifiedName,
      target,
      where,
      assert: assertExpr,
      severity,
      message,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(ruleToken, endToken)
    };
  }
  mapRuleTarget(raw, token) {
    const lower = raw.toLowerCase();
    switch (lower) {
      case "all":
        return "all";
      case "package":
        return "package";
      case "part":
        return "part";
      case "partusage":
      case "part_usage":
      case "part-usage":
        return "partUsage";
      case "port":
        return "port";
      case "prop":
        return "prop";
      case "rel":
      case "relationship":
        return "rel";
      case "view":
        return "view";
      case "enum":
        return "enum";
      case "typealias":
      case "type":
        return "typeAlias";
      case "let":
        return "let";
      case "import":
        return "import";
      default:
        this.addDiagnostic("warning", `Unknown rule target: ${raw}`, token);
        return "all";
    }
  }
  parseEnum(packageQualifiedName) {
    const enumToken = this.consume("ENUM", 'Expected "enum"');
    const nameToken = this.consume("IDENTIFIER", "Expected enum name");
    this.consume("LBRACE", 'Expected "{"');
    const values = [];
    if (!this.check("RBRACE")) {
      values.push(this.consume("IDENTIFIER", "Expected enum value").value);
      while (this.check("COMMA")) {
        this.advance();
        values.push(this.consume("IDENTIFIER", "Expected enum value").value);
      }
    }
    this.consume("RBRACE", 'Expected "}"');
    const endToken = this.consume("SEMICOLON", 'Expected ";"');
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.deriveId("enum", qualifiedName),
      elementType: "enum",
      name: nameToken.value,
      qualifiedName,
      values,
      source: this.rangeFrom(enumToken, endToken)
    };
  }
  parseTypeAlias(packageQualifiedName) {
    const typeToken = this.consume("TYPE", 'Expected "type"');
    const nameToken = this.consume("IDENTIFIER", "Expected type alias name");
    this.consume("EQ", 'Expected "="');
    const typeRef = this.parseTypeRefText("Expected type reference");
    const endToken = this.consume("SEMICOLON", 'Expected ";"');
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.deriveId("typeAlias", qualifiedName),
      elementType: "typeAlias",
      name: nameToken.value,
      qualifiedName,
      typeRef,
      source: this.rangeFrom(typeToken, endToken)
    };
  }
  parseLet(packageQualifiedName) {
    const letToken = this.consume("LET", 'Expected "let"');
    const nameToken = this.consume("IDENTIFIER", "Expected let name");
    this.consume("EQ", 'Expected "="');
    const value = this.parseValue();
    const endToken = this.consume("SEMICOLON", 'Expected ";"');
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.deriveId("let", qualifiedName),
      elementType: "let",
      name: nameToken.value,
      qualifiedName,
      value,
      source: this.rangeFrom(letToken, endToken)
    };
  }
  parseImport(packageQualifiedName) {
    const visibility = this.check("PRIVATE") || this.check("PUBLIC") || this.check("PROTECTED") ? this.advance().value.toLowerCase() : void 0;
    const importToken = this.consume("IMPORT", 'Expected "import"');
    const pathText = this.parseImportPathText("Expected import path");
    const endToken = this.consumeStatementEnd("import statement", {
      allowImplicitEndBefore: ["PART", "ACTION", "PORT", "ITEM", "REL", "CONNECTION", "VIEW", "ENUM", "TYPE", "LET", "IMPORT", "PACKAGE", "META", "RBRACE"]
    });
    const name = pathText;
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, `import:${name}`);
    return {
      id: this.deriveId("import", qualifiedName),
      elementType: "import",
      name,
      qualifiedName,
      path: pathText,
      meta: visibility ? { props: { visibility } } : void 0,
      source: this.rangeFrom(importToken, endToken)
    };
  }
  parseImportPathText(message) {
    if (this.check("STRING")) return this.advance().value;
    const first = this.consumeIdentifierLike(message);
    let text = first.value;
    while (this.check("SCOPE") || this.check("DOT")) {
      const sep = this.advance().type === "SCOPE" ? "::" : ".";
      if (this.check("STAR")) {
        this.advance();
        text += `${sep}*`;
        return text;
      }
      const next = this.consumeIdentifierLike(message);
      text += `${sep}${next.value}`;
    }
    return text;
  }
  parseConnection(packageQualifiedName) {
    const connToken = this.consume("CONNECTION", 'Expected "connection"');
    let nameToken;
    if (this.checkIdentifierLike() && this.checkNext("COLON")) {
      nameToken = this.consumeIdentifierLike("Expected connection name");
    }
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    this.consume("COLON", 'Expected ":"');
    let connectionTypeRef;
    this.pos;
    const firstRef = this.parseElementRef("Expected connection reference");
    if (this.checkIdentifierLike() && this.tokens[this.pos].value === "connect") {
      connectionTypeRef = String(firstRef.ref);
      this.advance();
      const fromRef = this.parseElementRef('Expected connection "from" reference');
      if (this.check("TO")) {
        this.advance();
      } else if (this.check("ARROW")) {
        this.advance();
      } else {
        this.addDiagnostic("warning", 'Expected "to" or "->" in typed connection', this.peek());
      }
      const toRef = this.parseElementRef('Expected connection "to" reference');
      return this.finishConnection(connToken, nameToken, explicitId, metaProps, fromRef, toRef, connectionTypeRef, packageQualifiedName);
    } else {
      this.consume("ARROW", 'Expected "->"');
      const toRef = this.parseElementRef('Expected connection "to" reference');
      return this.finishConnection(connToken, nameToken, explicitId, metaProps, firstRef, toRef, void 0, packageQualifiedName);
    }
  }
  finishConnection(connToken, nameToken, explicitId, metaProps, fromRef, toRef, connectionTypeRef, packageQualifiedName) {
    const endToken = this.consumeStatementEnd("connection declaration", {
      allowImplicitEndBefore: ["REL", "CONNECTION", "VIEW", "PART", "ACTION", "PORT", "ITEM", "ENUM", "TYPE", "LET", "IMPORT", "PACKAGE", "META", "RBRACE"]
    });
    let fromPort;
    let toPort;
    const fromStr = String(fromRef.ref);
    const toStr = String(toRef.ref);
    const fromDot = fromStr.lastIndexOf(".");
    const toDot = toStr.lastIndexOf(".");
    if (fromDot > 0) {
      fromPort = fromStr.slice(fromDot + 1);
      fromRef.ref = fromStr.slice(0, fromDot);
    }
    if (toDot > 0) {
      toPort = toStr.slice(toDot + 1);
      toRef.ref = toStr.slice(0, toDot);
    }
    const name = nameToken?.value ?? `conn_${String(fromRef.ref).replace(/[^A-Za-z0-9_]+/g, "_")}_${String(toRef.ref).replace(/[^A-Za-z0-9_]+/g, "_")}`;
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, name);
    return {
      id: this.stableId("relationship", qualifiedName, explicitId),
      elementType: "relationship",
      name,
      qualifiedName,
      relType: connectionTypeRef ?? "connection",
      from: fromRef,
      to: toRef,
      fromPort,
      toPort,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(connToken, endToken)
    };
  }
  parsePortDef(packageQualifiedName) {
    const portToken = this.consume("PORT", 'Expected "port"');
    this.consume("DEF", 'Expected "def" after "port"');
    const conjugated = this.check("TILDE") ? (this.advance(), true) : false;
    const nameToken = this.consumeIdentifierLike("Expected port definition name");
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const props = [];
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      const qn = this.joinQualifiedName(packageQualifiedName, nameToken.value);
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("PROP")) props.push(this.parseProp(qn));
        else if (this.check("DOC")) this.parseDocInto(metaProps);
        else if (this.check("META") || this.check("AT") && this.checkNext("META")) this.parseMetaStmtInto(metaProps);
        else if (this.check("SEMICOLON")) this.advance();
        else this.advance();
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.stableId("portDef", qualifiedName, explicitId),
      elementType: "portDef",
      name: nameToken.value,
      qualifiedName,
      ...supertype ? { supertype } : {},
      conjugated: conjugated || void 0,
      props: props.length ? props : void 0,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(portToken, endToken)
    };
  }
  parseItemDef(packageQualifiedName) {
    const itemToken = this.consume("ITEM", 'Expected "item"');
    this.consume("DEF", 'Expected "def" after "item"');
    const nameToken = this.consumeIdentifierLike("Expected item definition name");
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const props = [];
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      const qn = this.joinQualifiedName(packageQualifiedName, nameToken.value);
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("PROP")) props.push(this.parseProp(qn));
        else if (this.check("DOC")) this.parseDocInto(metaProps);
        else if (this.check("META") || this.check("AT") && this.checkNext("META")) this.parseMetaStmtInto(metaProps);
        else if (this.check("SEMICOLON")) this.advance();
        else this.advance();
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.stableId("itemDef", qualifiedName, explicitId),
      elementType: "itemDef",
      name: nameToken.value,
      qualifiedName,
      ...supertype ? { supertype } : {},
      props: props.length ? props : void 0,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(itemToken, endToken)
    };
  }
  parseActionDef(packageQualifiedName) {
    const actionToken = this.consume("ACTION", 'Expected "action"');
    const isDef = this.check("DEF");
    if (isDef) this.advance();
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike("Expected action name");
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    const params = [];
    const actions = [];
    const flows = [];
    const performs = [];
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      const actionQualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("IN") || this.check("OUT") || this.check("INOUT")) {
          params.push(this.parseActionParam());
          continue;
        }
        if (this.check("ACTION")) {
          actions.push(this.parseActionUsage(actionQualifiedName));
          continue;
        }
        if (this.check("FLOW")) {
          flows.push(this.parseFlowStatement(actionQualifiedName));
          continue;
        }
        if (this.check("PERFORM")) {
          const performed = this.parsePerformInContainer(actionQualifiedName);
          if (performed.perform) performs.push(performed.perform);
          if (performed.actionUsage) actions.push(performed.actionUsage);
          continue;
        }
        if (this.check("DOC")) {
          this.parseDocInto(metaProps);
          continue;
        }
        if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          this.parseMetaStmtInto(metaProps);
          continue;
        }
        if (this.check("SEMICOLON")) {
          this.advance();
          continue;
        }
        this.advance();
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consume("SEMICOLON", 'Expected ";" or "{"');
    }
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    return {
      id: this.stableId("actionDef", qualifiedName, explicitId),
      elementType: "actionDef",
      name: nameToken.value,
      qualifiedName,
      ...supertype ? { supertype } : {},
      params: params.length ? params : void 0,
      actions: actions.length ? actions : void 0,
      flows: flows.length ? flows : void 0,
      performs: performs.length ? performs : void 0,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(actionToken, endToken)
    };
  }
  parseActionParam() {
    const dirToken = this.advance();
    const nameToken = this.consumeIdentifierLike("Expected parameter name");
    this.consume("COLON", 'Expected ":"');
    const typeRef = this.parseTypeRefText("Expected parameter type");
    let multiplicity;
    if (this.check("LBRACKET")) {
      this.advance();
      multiplicity = this.parseMultiplicity();
      this.consume("RBRACKET", 'Expected "]"');
    }
    const endToken = this.consumeStatementEnd("action parameter", {
      allowImplicitEndBefore: ["IN", "OUT", "INOUT", "ACTION", "FLOW", "PERFORM", "META", "RBRACE"]
    });
    return {
      dir: dirToken.value,
      name: nameToken.value,
      typeRef,
      multiplicity,
      source: this.rangeFrom(dirToken, endToken)
    };
  }
  parseActionUsage(containerQualifiedName) {
    const actionToken = this.consume("ACTION", 'Expected "action"');
    const nameToken = this.consumeIdentifierLike("Expected action usage name");
    let typeRef = "?";
    let multiplicity;
    if (this.check("COLON") || this.check("REDEFINES")) {
      this.advance();
      typeRef = this.parseTypeRefText("Expected action type");
    }
    if (this.check("LBRACKET")) {
      this.advance();
      multiplicity = this.parseMultiplicity();
      this.consume("RBRACKET", 'Expected "]"');
    }
    if (typeRef === "?" && (this.check("COLON") || this.check("REDEFINES"))) {
      this.advance();
      typeRef = this.parseTypeRefText("Expected action type");
    }
    const actions = [];
    const flows = [];
    const performs = [];
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      const actionQualifiedName = this.joinQualifiedName(containerQualifiedName, nameToken.value);
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("ACTION")) actions.push(this.parseActionUsage(actionQualifiedName));
        else if (this.check("FLOW")) flows.push(this.parseFlowStatement(actionQualifiedName));
        else if (this.check("PERFORM")) {
          const performed = this.parsePerformInContainer(actionQualifiedName);
          if (performed.perform) performs.push(performed.perform);
          if (performed.actionUsage) actions.push(performed.actionUsage);
        } else if (this.check("SEMICOLON")) this.advance();
        else this.advance();
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
      if (this.check("SEMICOLON")) endToken = this.advance();
    } else {
      endToken = this.consumeStatementEnd("action usage", {
        allowImplicitEndBefore: ["ACTION", "FLOW", "PERFORM", "META", "RBRACE", "PART", "REL", "CONNECTION", "PORT", "PROP"]
      });
    }
    const qualifiedName = this.joinQualifiedName(containerQualifiedName, nameToken.value);
    return {
      id: this.deriveId("actionUsage", qualifiedName),
      elementType: "actionUsage",
      name: nameToken.value,
      qualifiedName,
      typeRef,
      multiplicity,
      actions: actions.length ? actions : void 0,
      flows: flows.length ? flows : void 0,
      performs: performs.length ? performs : void 0,
      source: this.rangeFrom(actionToken, endToken)
    };
  }
  parseFlowStatement(containerQualifiedName) {
    const flowToken = this.consume("FLOW", 'Expected "flow"');
    this.consume("OF", 'Expected "of"');
    const itemTypeRef = this.parseTypeRefText("Expected flow item type");
    this.consume("FROM", 'Expected "from"');
    const fromRef = this.parseQualifiedIdentifier("Expected flow source");
    this.consume("TO", 'Expected "to"');
    const toRef = this.parseQualifiedIdentifier("Expected flow target");
    const endToken = this.consumeStatementEnd("flow statement", {
      allowImplicitEndBefore: ["ACTION", "FLOW", "PERFORM", "META", "RBRACE"]
    });
    const name = `flow_${String(fromRef).replace(/[^A-Za-z0-9_]+/g, "_")}_${String(toRef).replace(/[^A-Za-z0-9_]+/g, "_")}`;
    const qualifiedName = this.joinQualifiedName(containerQualifiedName, name);
    return {
      id: this.deriveId("flow", qualifiedName),
      elementType: "flow",
      name,
      qualifiedName,
      itemTypeRef,
      fromRef,
      toRef,
      source: this.rangeFrom(flowToken, endToken)
    };
  }
  parsePerformInContainer(containerQualifiedName) {
    const performToken = this.consume("PERFORM", 'Expected "perform"');
    if (this.check("ACTION")) {
      const action = this.parseActionUsage(containerQualifiedName);
      return {
        actionUsage: {
          ...action,
          meta: { ...action.meta ?? {}, kind: "perform" },
          source: this.rangeFrom(performToken, this.tokens[this.pos - 1])
        }
      };
    }
    const refText = this.parseQualifiedIdentifier('Expected action reference after "perform"');
    const endToken = this.consumeStatementEnd("perform statement", {
      allowImplicitEndBefore: ["ACTION", "FLOW", "PERFORM", "META", "RBRACE", "PART", "REL", "CONNECTION", "PORT", "PROP"]
    });
    const name = refText;
    const qualifiedName = this.joinQualifiedName(containerQualifiedName, `perform:${refText}`);
    return {
      perform: {
        id: this.deriveId("perform", qualifiedName),
        elementType: "perform",
        name,
        qualifiedName,
        ref: refText,
        source: this.rangeFrom(performToken, endToken)
      }
    };
  }
  parseMultiplicity() {
    if (this.check("STAR")) {
      this.advance();
      return { lower: 0, upper: "*" };
    }
    const lowerToken = this.consume("NUMBER", "Expected multiplicity lower bound");
    const lower = parseInt(lowerToken.value, 10);
    if (this.check("DOTDOT")) {
      this.advance();
      if (this.check("STAR")) {
        this.advance();
        return { lower, upper: "*" };
      }
      const upperToken = this.consume("NUMBER", "Expected multiplicity upper bound");
      return { lower, upper: parseInt(upperToken.value, 10) };
    }
    return { lower, upper: lower };
  }
  // ---------------------------------------------------------------------------
  // State machine parsing
  // ---------------------------------------------------------------------------
  /**
   * Parse: state def <Name> { state ...; transition ...; }
   * or:    state def <Name>;
   */
  parseStateDef(packageQualifiedName) {
    const stateToken = this.consume("STATE", 'Expected "state"');
    const isDef = this.check("DEF");
    if (isDef) this.advance();
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike("Expected state definition name");
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    const qualifiedName = this.joinQualifiedName(packageQualifiedName, nameToken.value);
    const states = [];
    const transitions = [];
    let entryAction;
    let exitAction;
    let doAction;
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("STATE")) {
          const nextToken = this.peekNext();
          if (nextToken.type === "DEF") {
            this.addDiagnostic("warning", "Nested state def inside state def \u2014 consider using state usage instead", this.peek());
            this.synchronize();
          } else {
            states.push(this.parseStateUsage(qualifiedName));
          }
        } else if (this.check("TRANSITION")) {
          transitions.push(this.parseTransitionUsage(qualifiedName));
        } else if (this.check("ENTRY")) {
          this.advance();
          entryAction = this.parseQualifiedIdentifier("Expected entry action reference");
          this.consumeStatementEnd("entry action", {});
        } else if (this.check("EXIT")) {
          this.advance();
          exitAction = this.parseQualifiedIdentifier("Expected exit action reference");
          this.consumeStatementEnd("exit action", {});
        } else if (this.check("DO")) {
          this.advance();
          doAction = this.parseQualifiedIdentifier("Expected do action reference");
          this.consumeStatementEnd("do action", {});
        } else if (this.check("DOC")) {
          this.parseDocInto(metaProps);
        } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          this.parseMetaStmtInto(metaProps);
        } else if (this.check("SEMICOLON")) {
          this.advance();
        } else {
          const token = this.advance();
          this.addDiagnostic("warning", `Unexpected token in state def: ${token.value}`, token);
        }
      }
      endToken = this.consume("RBRACE", 'Expected "}" to close state def');
    } else {
      endToken = this.consumeStatementEnd("state def", {});
    }
    return {
      id: this.stableId("stateDef", qualifiedName, explicitId),
      elementType: "stateDef",
      name: nameToken.value,
      qualifiedName,
      supertype,
      states: states.length ? states : void 0,
      transitions: transitions.length ? transitions : void 0,
      entryAction,
      exitAction,
      doAction,
      source: this.rangeFrom(stateToken, endToken),
      meta: this.buildMetadata(metaProps)
    };
  }
  /**
   * Parse: state <name> : <TypeRef>;
   * or:    state <name> { ... }
   */
  parseStateUsage(containerQualifiedName) {
    const stateToken = this.consume("STATE", 'Expected "state"');
    const nameToken = this.consumeIdentifierLike("Expected state usage name");
    let isParallel = false;
    if (nameToken.value === "parallel") {
      isParallel = true;
      const actualName = this.consumeIdentifierLike('Expected state usage name after "parallel"');
      const qualifiedName2 = this.joinQualifiedName(containerQualifiedName, actualName.value);
      return this.finishStateUsage(stateToken, actualName, qualifiedName2, isParallel);
    }
    const qualifiedName = this.joinQualifiedName(containerQualifiedName, nameToken.value);
    return this.finishStateUsage(stateToken, nameToken, qualifiedName, isParallel);
  }
  finishStateUsage(stateToken, nameToken, qualifiedName, isParallel) {
    let typeRef = "?";
    if (this.check("COLON")) {
      this.advance();
      typeRef = this.parseTypeRefText("Expected state type reference");
    }
    const states = [];
    const transitions = [];
    let entryAction;
    let exitAction;
    let doAction;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("STATE")) {
          states.push(this.parseStateUsage(qualifiedName));
        } else if (this.check("TRANSITION")) {
          transitions.push(this.parseTransitionUsage(qualifiedName));
        } else if (this.check("ENTRY")) {
          this.advance();
          entryAction = this.parseQualifiedIdentifier("Expected entry action reference");
          this.consumeStatementEnd("entry action", {});
        } else if (this.check("EXIT")) {
          this.advance();
          exitAction = this.parseQualifiedIdentifier("Expected exit action reference");
          this.consumeStatementEnd("exit action", {});
        } else if (this.check("DO")) {
          this.advance();
          doAction = this.parseQualifiedIdentifier("Expected do action reference");
          this.consumeStatementEnd("do action", {});
        } else if (this.check("DOC")) {
          const _discard = {};
          this.parseDocInto(_discard);
        } else if (this.check("SEMICOLON")) {
          this.advance();
        } else {
          const token = this.advance();
          this.addDiagnostic("warning", `Unexpected token in state usage: ${token.value}`, token);
        }
      }
      this.consume("RBRACE", 'Expected "}" to close state usage');
    } else {
      this.consumeStatementEnd("state usage", {});
    }
    return {
      id: this.deriveId("stateUsage", qualifiedName),
      elementType: "stateUsage",
      name: nameToken.value,
      qualifiedName,
      typeRef,
      isParallel: isParallel || void 0,
      states: states.length ? states : void 0,
      transitions: transitions.length ? transitions : void 0,
      entryAction,
      exitAction,
      doAction,
      source: this.rangeFrom(stateToken, this.peek())
    };
  }
  /**
   * Parse: transition <source> then <target>;
   * or:    transition first <source> then <target>;
   * Optional: when <guard>
   */
  parseTransitionUsage(containerQualifiedName) {
    const transToken = this.consume("TRANSITION", 'Expected "transition"');
    if (this.peek().type === "IDENTIFIER" && this.peek().value.toLowerCase() === "first") {
      this.advance();
    }
    const sourceState = this.parseElementRef("Expected transition source state");
    this.consume("THEN", 'Expected "then" after transition source');
    const targetState = this.parseElementRef("Expected transition target state");
    let guard;
    if (this.check("WHEN")) {
      this.advance();
      if (this.check("LBRACKET")) {
        this.advance();
        const guardParts = [];
        while (!this.check("RBRACKET") && !this.isAtEnd()) {
          guardParts.push(this.advance().value);
        }
        if (this.check("RBRACKET")) this.advance();
        guard = guardParts.join(" ");
      } else {
        guard = this.parseQualifiedIdentifier("Expected guard condition");
      }
    }
    const sourceRefText = String(sourceState.ref);
    const targetRefText = String(targetState.ref);
    const qualifiedName = this.joinQualifiedName(containerQualifiedName, `${sourceRefText}_to_${targetRefText}`);
    const endToken = this.consumeStatementEnd("transition", {});
    return {
      id: this.deriveId("transition", qualifiedName),
      elementType: "transition",
      name: `${sourceRefText} -> ${targetRefText}`,
      qualifiedName,
      sourceState,
      targetState,
      guard,
      source: this.rangeFrom(transToken, endToken)
    };
  }
  // ---------------------------------------------------------------------------
  // Constraint parsing
  // ---------------------------------------------------------------------------
  parseConstraintDef(parentQualifiedName2) {
    const startToken = this.consume("CONSTRAINT", 'Expected "constraint"');
    this.consume("DEF", 'Expected "def" after "constraint"');
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consume("IDENTIFIER", "Expected constraint name");
    const name = nameToken.value;
    const qualifiedName = this.joinQualifiedName(parentQualifiedName2, name);
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText('Expected supertype after ":>"');
    }
    const params = [];
    if (this.check("LPAREN")) {
      this.advance();
      while (!this.check("RPAREN") && !this.isAtEnd()) {
        if (this.check("IN") || this.check("OUT") || this.check("INOUT")) {
          params.push(this.parseActionParam());
        } else {
          this.advance();
        }
        if (this.check("COMMA")) this.advance();
      }
      if (this.check("RPAREN")) this.advance();
    }
    let expression;
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      let depth = 1;
      let exprStartOffset = -1;
      let exprEndOffset = -1;
      while (depth > 0 && !this.isAtEnd()) {
        if (this.check("LBRACE")) depth++;
        if (this.check("RBRACE")) {
          depth--;
          if (depth === 0) break;
        }
        if (this.check("DOC")) {
          this.advance();
          if (this.check("STRING")) this.advance();
          if (this.check("SEMICOLON")) this.advance();
          continue;
        }
        if (this.check("SEMICOLON")) {
          this.advance();
          continue;
        }
        const tok = this.peek();
        if (exprStartOffset < 0) exprStartOffset = tok.startOffset;
        exprEndOffset = tok.endOffset;
        this.advance();
      }
      if (exprStartOffset >= 0 && exprEndOffset > exprStartOffset && this.sourceText) {
        expression = this.sourceText.slice(exprStartOffset, exprEndOffset).trim() || void 0;
      }
      endToken = this.consume("RBRACE", 'Expected "}" to close constraint def');
    } else {
      endToken = this.consumeStatementEnd("constraint def", {});
    }
    return {
      id: explicitId ?? this.deriveId("constraintDef", qualifiedName),
      elementType: "constraintDef",
      name,
      qualifiedName,
      supertype,
      params: params.length > 0 ? params : void 0,
      expression,
      source: this.rangeFrom(startToken, endToken),
      meta: Object.keys(metaProps).length > 0 ? metaProps : void 0
    };
  }
  // ---------------------------------------------------------------------------
  // Generic "keyword def Name { body }" parser for simple definition types
  // ---------------------------------------------------------------------------
  /**
   * Skips a brace-delimited body, handling nested braces and doc annotations.
   * Returns the closing brace token.
   */
  skipDefBody() {
    this.advance();
    let depth = 1;
    while (depth > 0 && !this.isAtEnd()) {
      if (this.check("LBRACE")) depth++;
      if (this.check("RBRACE")) {
        depth--;
        if (depth === 0) break;
      }
      if (this.check("DOC")) {
        this.advance();
        if (this.check("STRING")) this.advance();
        if (this.check("SEMICOLON")) this.advance();
        continue;
      }
      this.advance();
    }
    return this.consume("RBRACE", 'Expected "}"');
  }
  /**
   * Parse: `<keyword> def <name> [{ body }] ;`
   * Used for element types that share the same simple structure.
   */
  parseSimpleDef(keywordToken, elementType, idPrefix, parentQualifiedName2) {
    const startToken = this.consume(keywordToken, `Expected "${keywordToken.toLowerCase()}"`);
    this.consume("DEF", 'Expected "def"');
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike(`Expected ${idPrefix} name`);
    const qualifiedName = this.joinQualifiedName(parentQualifiedName2, nameToken.value);
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText("Expected supertype");
    }
    let endToken;
    if (this.check("LBRACE")) {
      endToken = this.skipDefBody();
    } else {
      endToken = this.consumeStatementEnd(idPrefix, {});
    }
    return {
      id: explicitId ?? this.deriveId(idPrefix, qualifiedName),
      elementType,
      name: nameToken.value,
      qualifiedName,
      supertype,
      source: this.rangeFrom(startToken, endToken),
      meta: Object.keys(metaProps).length > 0 ? metaProps : void 0
    };
  }
  parseConnectionDef(parentQualifiedName2) {
    const base = this.parseSimpleDef("CONNECTION", "connectionDef", "connectionDef", parentQualifiedName2);
    return base;
  }
  parseAllocationDef(parentQualifiedName2) {
    const base = this.parseSimpleDef("ALLOCATION", "allocationDef", "allocationDef", parentQualifiedName2);
    return base;
  }
  parseUseCaseDef(parentQualifiedName2) {
    const startToken = this.consume("USE", 'Expected "use"');
    this.consume("CASE", 'Expected "case"');
    this.consume("DEF", 'Expected "def"');
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike("Expected use case name");
    const qualifiedName = this.joinQualifiedName(parentQualifiedName2, nameToken.value);
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText("Expected supertype");
    }
    let subject;
    const actors = [];
    const includes = [];
    let objective;
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("DOC")) {
          this.parseDocInto(metaProps);
        } else if (this.check("SUBJECT")) {
          this.advance();
          if (this.check("COLON")) this.advance();
          const ref = this.consumeIdentifierLike("Expected subject reference");
          subject = { ref: ref.value };
          this.consumeStatementEnd("subject statement", {
            allowImplicitEndBefore: ["RBRACE", "DOC", "ACTOR", "SUBJECT", "INCLUDE", "OBJECTIVE", "META"]
          });
        } else if (this.check("ACTOR")) {
          const actorStart = this.advance();
          const actorName = this.consumeIdentifierLike("Expected actor name");
          let typeRef;
          if (this.check("COLON")) {
            this.advance();
            typeRef = this.parseTypeRefText("Expected actor type");
          }
          this.consumeStatementEnd("actor declaration", {
            allowImplicitEndBefore: ["RBRACE", "DOC", "ACTOR", "SUBJECT", "INCLUDE", "OBJECTIVE", "META"]
          });
          actors.push({
            id: this.deriveId("actorUsage", this.joinQualifiedName(qualifiedName, actorName.value)),
            elementType: "actorUsage",
            name: actorName.value,
            qualifiedName: this.joinQualifiedName(qualifiedName, actorName.value),
            typeRef,
            source: this.rangeFrom(actorStart, actorName)
          });
        } else if (this.check("INCLUDE") && this.checkNext("USE")) {
          this.advance();
          this.advance();
          this.consume("CASE", 'Expected "case" after "include use"');
          const refToken = this.consumeIdentifierLike("Expected use case reference");
          this.consumeStatementEnd("include use case", {
            allowImplicitEndBefore: ["RBRACE", "DOC", "ACTOR", "SUBJECT", "INCLUDE", "OBJECTIVE", "META"]
          });
          includes.push({
            id: this.deriveId("includeUseCase", this.joinQualifiedName(qualifiedName, refToken.value)),
            elementType: "includeUseCase",
            name: refToken.value,
            qualifiedName: this.joinQualifiedName(qualifiedName, refToken.value),
            useCaseRef: { ref: refToken.value },
            source: this.rangeFrom(refToken, refToken)
          });
        } else if (this.check("OBJECTIVE")) {
          this.advance();
          if (this.check("STRING")) {
            objective = this.advance().value;
          }
          this.consumeStatementEnd("objective statement", {
            allowImplicitEndBefore: ["RBRACE", "DOC", "ACTOR", "SUBJECT", "INCLUDE", "OBJECTIVE", "META"]
          });
        } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          this.parseMetaStmtInto(metaProps);
        } else if (this.check("SEMICOLON")) {
          this.advance();
        } else {
          this.advance();
        }
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
    } else {
      endToken = this.consumeStatementEnd("useCaseDef", {});
    }
    return {
      id: explicitId ?? this.deriveId("useCaseDef", qualifiedName),
      elementType: "useCaseDef",
      name: nameToken.value,
      qualifiedName,
      supertype,
      ...subject ? { subject } : {},
      ...actors.length > 0 ? { actors } : {},
      ...includes.length > 0 ? { includes } : {},
      ...objective ? { objective } : {},
      source: this.rangeFrom(startToken, endToken),
      meta: Object.keys(metaProps).length > 0 ? metaProps : void 0
    };
  }
  parseInterfaceDef(parentQualifiedName2) {
    const startToken = this.consume("INTERFACE", 'Expected "interface"');
    this.consume("DEF", 'Expected "def" after "interface"');
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike("Expected interface name");
    const qualifiedName = this.joinQualifiedName(parentQualifiedName2, nameToken.value);
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText("Expected supertype");
    }
    const ends = [];
    const ports = [];
    let endToken;
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.check("RBRACE") && !this.isAtEnd()) {
        if (this.check("END")) {
          this.advance();
          const endName = this.consumeIdentifierLike("Expected end name");
          this.consume("COLON", 'Expected ":" after end name');
          const endTypeRef = this.parseTypeRefText("Expected port type reference");
          this.consumeStatementEnd("end declaration", {
            allowImplicitEndBefore: ["END", "PORT", "DOC", "META", "RBRACE"]
          });
          ends.push({ ref: { ref: endTypeRef }, port: { ref: endName.value } });
        } else if (this.check("PORT")) {
          ports.push(this.parsePort(qualifiedName));
        } else if (this.check("DOC")) {
          this.parseDocInto(metaProps);
        } else if (this.check("META") || this.check("AT") && this.checkNext("META")) {
          this.parseMetaStmtInto(metaProps);
        } else if (this.check("SEMICOLON")) {
          this.advance();
        } else {
          this.advance();
        }
      }
      endToken = this.consume("RBRACE", 'Expected "}"');
    } else {
      endToken = this.consumeStatementEnd("interface def", {});
    }
    return {
      id: explicitId ?? this.deriveId("interfaceDef", qualifiedName),
      elementType: "interfaceDef",
      name: nameToken.value,
      qualifiedName,
      supertype,
      ends: ends.length ? ends : void 0,
      ports: ports.length ? ports : void 0,
      source: this.rangeFrom(startToken, endToken),
      meta: Object.keys(metaProps).length > 0 ? metaProps : void 0
    };
  }
  parseAnalysisDef(parentQualifiedName2) {
    const startToken = this.consume("ANALYSIS", 'Expected "analysis"');
    if (this.check("CASE")) this.advance();
    this.consume("DEF", 'Expected "def"');
    const metaProps = {};
    this.parseOptStereotypeInto(metaProps);
    const nameToken = this.consumeIdentifierLike("Expected analysis name");
    const qualifiedName = this.joinQualifiedName(parentQualifiedName2, nameToken.value);
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    let supertype;
    if (this.check("REDEFINES")) {
      this.advance();
      supertype = this.parseTypeRefText("Expected supertype");
    }
    let endToken;
    if (this.check("LBRACE")) {
      endToken = this.skipDefBody();
    } else {
      endToken = this.consumeStatementEnd("analysisDef", {});
    }
    return {
      id: explicitId ?? this.deriveId("analysisDef", qualifiedName),
      elementType: "analysisDef",
      name: nameToken.value,
      qualifiedName,
      supertype,
      source: this.rangeFrom(startToken, endToken),
      meta: Object.keys(metaProps).length > 0 ? metaProps : void 0
    };
  }
  parseCalcDef(parentQualifiedName2) {
    const base = this.parseSimpleDef("CALC", "calcDef", "calcDef", parentQualifiedName2);
    return base;
  }
  parseViewpointDef(parentQualifiedName2) {
    const base = this.parseSimpleDef("VIEWPOINT", "viewpointDef", "viewpointDef", parentQualifiedName2);
    return base;
  }
  parseRenderingDef(parentQualifiedName2) {
    const base = this.parseSimpleDef("RENDERING", "renderingDef", "renderingDef", parentQualifiedName2);
    return base;
  }
  parseOccurrenceDef(parentQualifiedName2) {
    const base = this.parseSimpleDef("OCCURRENCE", "occurrenceDef", "occurrenceDef", parentQualifiedName2);
    return base;
  }
  parseMetadataDefBlock(parentQualifiedName2) {
    const base = this.parseSimpleDef("METADATA", "metadataDef", "metadataDef", parentQualifiedName2);
    return base;
  }
  // ---------------------------------------------------------------------------
  // Helper methods
  // ---------------------------------------------------------------------------
  isAtEnd() {
    return this.peek().type === "EOF";
  }
  peek() {
    return this.tokens[this.pos] ?? { type: "EOF", value: "", line: 0, column: 0, startOffset: 0, endOffset: 0 };
  }
  peekNext() {
    return this.tokens[this.pos + 1] ?? { type: "EOF", value: "", line: 0, column: 0, startOffset: 0, endOffset: 0 };
  }
  check(type) {
    return this.peek().type === type;
  }
  checkNext(type) {
    return this.peekNext().type === type;
  }
  checkNth(n, type) {
    const token = this.tokens[this.pos + (n - 1)];
    return (token?.type ?? "EOF") === type;
  }
  advance() {
    if (!this.isAtEnd()) this.pos++;
    return this.tokens[this.pos - 1];
  }
  previous() {
    return this.tokens[Math.max(0, this.pos - 1)] ?? this.peek();
  }
  consume(type, message) {
    if (this.check(type)) return this.advance();
    const token = this.peek();
    this.addDiagnostic("error", `${message}, got "${token.value}"`, token);
    throw new Error(message);
  }
  checkIdentifierLike() {
    return IDENTIFIER_LIKE_TOKENS.has(this.peek().type);
  }
  consumeIdentifierLike(message) {
    if (this.checkIdentifierLike()) return this.advance();
    const token = this.peek();
    this.addDiagnostic("error", `${message}, got "${token.value}"`, token);
    throw new Error(message);
  }
  consumeStatementEnd(context, opts) {
    if (this.check("SEMICOLON")) return this.advance();
    const message = `Expected ";" after ${context}`;
    const nextToken = this.peek();
    const prev = this.previous();
    const prevTokenLen = Math.max(1, prev.endOffset - prev.startOffset);
    const prevEndCol = prev.column + prevTokenLen;
    const prevLastCharCol = Math.max(1, prevEndCol - 1);
    if (prev.line > 0 && prev.endOffset <= nextToken.startOffset) {
      this.diagnostics.push({
        severity: "error",
        message,
        line: prev.line,
        column: prevLastCharCol,
        endLine: prev.line,
        endColumn: prevEndCol
      });
    } else {
      this.addDiagnostic("error", message, nextToken);
    }
    if (opts?.allowImplicitEndBeforeAssignment && this.checkIdentifierLike() && this.checkNext("COLON")) {
      return this.previous();
    }
    if (opts?.allowImplicitEndBefore?.some((t) => this.check(t))) {
      return this.previous();
    }
    if (this.check("RBRACE") || this.isAtEnd()) return this.previous();
    while (!this.isAtEnd() && !this.check("SEMICOLON") && !this.check("RBRACE")) {
      this.advance();
    }
    if (this.check("SEMICOLON")) return this.advance();
    return this.previous();
  }
  addDiagnostic(severity, message, token) {
    this.diagnostics.push({
      severity,
      message,
      line: token.line,
      column: token.column,
      endLine: token.line,
      endColumn: token.column + Math.max(1, token.value.length)
    });
  }
  synchronize() {
    while (!this.isAtEnd()) {
      if (this.check("PACKAGE") || this.check("PART") || this.check("REL") || this.check("VIEW") || this.check("ENUM") || this.check("TYPE") || this.check("LET") || this.check("IMPORT") || this.check("ALLOCATION") || this.check("INTERFACE") || this.check("ANALYSIS") || this.check("CALC") || this.check("VIEWPOINT") || this.check("RENDERING") || this.check("OCCURRENCE") || this.check("METADATA"))
        return;
      this.advance();
    }
  }
  synchronizeToRbrace() {
    let depth = 1;
    while (!this.isAtEnd() && depth > 0) {
      if (this.check("LBRACE")) depth++;
      if (this.check("RBRACE")) depth--;
      this.advance();
    }
  }
  deriveId(elementType, qualifiedName) {
    return `sysml:${elementType}:${qualifiedName}`;
  }
  stableId(elementType, qualifiedName, explicitId) {
    if (explicitId) {
      const normalized = explicitId.startsWith("@") ? explicitId : `@${explicitId}`;
      return `sysml:${elementType}:${normalized}`;
    }
    return this.deriveId(elementType, qualifiedName);
  }
  joinQualifiedName(parent, name) {
    if (!parent || parent === "$default") return name;
    return `${parent}.${name}`;
  }
  rangeFrom(start, end) {
    return {
      uri: this.uri,
      startOffset: start.startOffset,
      endOffset: end.endOffset,
      startLine: start.line,
      startColumn: start.column,
      endLine: end.line,
      endColumn: end.column + Math.max(1, end.value.length)
    };
  }
  parseOptId() {
    if (!this.check("AT")) return void 0;
    this.advance();
    const idToken = this.consumeIdentifierLike('Expected identifier after "@"');
    return `@${idToken.value}`;
  }
  parseOptMetaInto(metaProps) {
    if (!this.check("META")) return;
    this.parseMetaStmtInto(metaProps);
  }
  parseMetaStmtInto(metaProps) {
    if (this.check("AT") && this.checkNext("META")) this.advance();
    this.consume("META", 'Expected "meta"');
    this.consume("LBRACE", 'Expected "{" after "meta"');
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      if (this.checkIdentifierLike() && this.checkNext("COLON")) {
        const { key, value } = this.parsePropertyAssignment();
        metaProps[key] = value;
      } else if (this.check("SEMICOLON")) {
        this.advance();
      } else {
        const token = this.advance();
        if (token.type !== "RBRACE") {
          this.addDiagnostic("warning", `Unexpected token in meta block: ${token.value}`, token);
        }
      }
    }
    this.consume("RBRACE", 'Expected "}" to close meta block');
  }
  parseMetaTagsAssignmentInto(metaProps) {
    this.consume("META", 'Expected "meta"');
    this.consume("COLON", 'Expected ":" after "meta"');
    const value = this.parseValueList();
    metaProps["tags"] = this.toStringArray(value);
    this.consumeStatementEnd("meta assignment", { allowImplicitEndBefore: ["RBRACE"] });
  }
  parseDocInto(metaProps) {
    this.consume("DOC", 'Expected "doc"');
    let text = "";
    if (this.check("STRING")) text = this.advance().value;
    this.consumeStatementEnd("doc statement", {
      allowImplicitEndBefore: ["META", "TAG", "PART", "ACTION", "FLOW", "PERFORM", "PORT", "PROP", "REL", "CONNECTION", "VIEW", "REQUIREMENT", "VERIFICATION", "SUBJECT", "SATISFY", "RBRACE"],
      allowImplicitEndBeforeAssignment: true
    });
    if (!text) return;
    const existing = metaProps["notes"];
    metaProps["notes"] = existing ? `${String(existing)}
${text}` : text;
  }
  parseOptStereotypeInto(metaProps) {
    if (!(this.check("UNKNOWN") && this.peek().value === "\xAB")) return;
    this.advance();
    const parts = [];
    while (!this.isAtEnd() && !(this.check("UNKNOWN") && this.peek().value === "\xBB")) {
      parts.push(this.advance().value);
    }
    if (this.check("UNKNOWN") && this.peek().value === "\xBB") this.advance();
    const stereotype = parts.join("").trim();
    if (stereotype) metaProps["stereotype"] = stereotype;
  }
  synchronizeToStatementEnd() {
    while (!this.isAtEnd() && !this.check("SEMICOLON") && !this.check("RBRACE")) {
      this.advance();
    }
    if (this.check("SEMICOLON")) this.advance();
  }
  parsePropertyAssignment() {
    const keyToken = this.consumeIdentifierLike("Expected property key");
    this.consume("COLON", 'Expected ":"');
    const value = this.parseValueList();
    this.consumeStatementEnd("property assignment", { allowImplicitEndBeforeAssignment: true, allowImplicitEndBefore: ["RBRACE"] });
    return { key: keyToken.value.toLowerCase(), value };
  }
  parseValueList() {
    const values = [];
    values.push(this.parseValue());
    while (this.check("COMMA")) {
      this.advance();
      values.push(this.parseValue());
    }
    return values.length === 1 ? values[0] : values;
  }
  parseValue() {
    if (this.check("STRING")) return this.advance().value;
    if (this.check("NUMBER")) return parseFloat(this.advance().value);
    if (this.check("QUESTION")) {
      this.advance();
      return "?";
    }
    if (this.check("STAR")) {
      this.advance();
      return "*";
    }
    if (this.check("LBRACKET")) {
      return this.parseListValue();
    }
    if (this.check("LBRACE")) {
      return this.parseObjectValue();
    }
    if (this.check("HASH")) {
      this.advance();
      return { $ref: `#${this.parseQualifiedIdentifier('Expected reference path after "#"')}` };
    }
    if (this.check("IDENTIFIER")) {
      const token2 = this.advance();
      const lower = token2.value.toLowerCase();
      if (lower === "true") return true;
      if (lower === "false") return false;
      if (lower === "null") return null;
      return token2.value;
    }
    if (this.check("PACKAGE") || this.check("PART") || this.check("DEF") || this.check("META") || this.check("PROP") || this.check("PORT") || this.check("TAG") || this.check("IN") || this.check("OUT") || this.check("INOUT") || this.check("ENUM") || this.check("TYPE") || this.check("LET") || this.check("IMPORT") || this.check("REL") || this.check("VIEW") || this.check("USE") || this.check("SELECT") || this.check("INCLUDE") || this.check("EXCLUDE") || this.check("RELS") || this.check("LAYOUT") || this.check("WHERE") || this.check("AND") || this.check("OR") || this.check("NOT") || this.check("ALL")) {
      return this.advance().value;
    }
    const token = this.advance();
    this.addDiagnostic("warning", `Unexpected value token: ${token.value}`, token);
    return token.value;
  }
  parseListValue() {
    this.consume("LBRACKET", 'Expected "["');
    const items = [];
    if (!this.check("RBRACKET")) {
      items.push(this.parseValue());
      while (this.check("COMMA")) {
        this.advance();
        if (this.check("RBRACKET")) break;
        items.push(this.parseValue());
      }
    }
    this.consume("RBRACKET", 'Expected "]"');
    return items;
  }
  parseObjectValue() {
    this.consume("LBRACE", 'Expected "{"');
    const obj = {};
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      if (this.check("COMMA") || this.check("SEMICOLON")) {
        this.advance();
        continue;
      }
      if (!(this.check("IDENTIFIER") || this.check("META"))) {
        const token = this.advance();
        if (token.type !== "RBRACE") this.addDiagnostic("warning", `Unexpected token in object: ${token.value}`, token);
        continue;
      }
      const keyToken = this.check("IDENTIFIER") ? this.advance() : this.consume("META", "Expected object key");
      this.consume("COLON", 'Expected ":"');
      obj[keyToken.value] = this.parseValue();
      if (this.check("COMMA") || this.check("SEMICOLON")) {
        this.advance();
        continue;
      }
    }
    this.consume("RBRACE", 'Expected "}"');
    return obj;
  }
  buildMetadata(props) {
    if (Object.keys(props).length === 0) return void 0;
    const meta = { props: {} };
    for (const [key, value] of Object.entries(props)) {
      switch (key) {
        case "kind":
          meta.kind = value;
          break;
        case "state":
          meta.state = this.parseLifecycleState(value);
          break;
        case "confidence":
          meta.confidence = this.parseConfidence(value);
          break;
        case "notes":
        case "note":
          meta.notes = typeof value === "string" ? value : String(value);
          break;
        case "tags":
        case "tag":
          meta.tags = this.toStringArray(value);
          break;
        case "questions":
        case "question":
          meta.questions = this.toStringArray(value);
          break;
        case "risks":
        case "risk":
          meta.risks = this.toStringArray(value);
          break;
        default:
          meta.props[key] = value;
          break;
      }
    }
    if (Object.keys(meta.props).length === 0) delete meta.props;
    return meta;
  }
  buildViewFilter(props) {
    const filter = {};
    for (const [key, value] of Object.entries(props)) {
      switch (key) {
        case "state":
        case "states":
          filter.states = this.toStringArray(value).map((v) => this.parseLifecycleState(v)).filter(Boolean);
          break;
        case "confidence":
          filter.confidence = this.toStringArray(value).map((v) => this.parseConfidence(v)).filter(Boolean);
          break;
        case "tagsany":
        case "tags":
          filter.tagsAny = this.toStringArray(value);
          break;
        case "kindsany":
        case "kinds":
        case "kind":
          filter.kindsAny = this.toStringArray(value);
          break;
        case "reltypesany":
        case "reltype":
        case "reltypes":
          filter.relTypesAny = this.toStringArray(value);
          break;
        case "includeunknowns":
          filter.includeUnknowns = Boolean(value);
          break;
        default:
          filter.tagsAny ??= [];
          filter.tagsAny.push(`where:${key}`);
          break;
      }
    }
    return filter;
  }
  parseLifecycleState(value) {
    if (value === "?") return "unknown";
    const v = String(value).toLowerCase();
    switch (v) {
      case "draft":
      case "wip":
      case "active":
      case "done":
      case "deprecated":
      case "abandoned":
      case "speculative":
      case "unknown":
        return v;
      default:
        return "unknown";
    }
  }
  parseConfidence(value) {
    if (value === "?") return "unknown";
    const v = String(value).toLowerCase();
    switch (v) {
      case "low":
      case "medium":
      case "high":
      case "unknown":
        return v;
      default:
        return "unknown";
    }
  }
  toStringArray(value) {
    if (Array.isArray(value)) return value.map((v) => String(v));
    return [String(value)];
  }
  parseQualifiedIdentifier(message) {
    const first = this.consumeIdentifierLike(message);
    const parts = [first.value];
    while (this.check("DOT")) {
      this.advance();
      const next = this.consumeIdentifierLike(message);
      parts.push(next.value);
    }
    return parts.join(".");
  }
  parseTypeRefText(message) {
    const first = this.consumeIdentifierLike(message);
    const parts = [first.value];
    while (this.check("SCOPE") || this.check("DOT")) {
      const sep = this.advance().type === "SCOPE" ? "::" : ".";
      parts.push(sep + this.consumeIdentifierLike(message).value);
    }
    return parts.join("");
  }
  parseRefText(message) {
    if (this.check("QUESTION")) {
      this.advance();
      return "?";
    }
    if (this.check("STAR")) {
      this.advance();
      return "*";
    }
    if (this.check("HASH")) {
      this.advance();
      return `#${this.parseQualifiedIdentifier(message)}`;
    }
    return this.parseQualifiedIdentifier(message);
  }
  parseElementRef(message) {
    return { ref: this.parseRefText(message) };
  }
  parseRefList(message) {
    const refs = [this.parseElementRef(message)];
    while (this.check("COMMA")) {
      this.advance();
      refs.push(this.parseElementRef(message));
    }
    return refs;
  }
  parseTag() {
    this.consume("TAG", 'Expected "tag"');
    const nameToken = this.consumeIdentifierLike("Expected tag name");
    if (this.check("LPAREN")) {
      this.advance();
      if (!this.check("RPAREN")) {
        this.parseValue();
        while (this.check("COMMA")) {
          this.advance();
          this.parseValue();
        }
      }
      this.consume("RPAREN", 'Expected ")"');
    }
    this.consumeStatementEnd("tag statement", {
      allowImplicitEndBefore: ["TAG", "PART", "PORT", "PROP", "REL", "VIEW", "PACKAGE", "META", "RBRACE"]
    });
    return nameToken.value;
  }
  parsePort(partQualifiedName) {
    const portToken = this.consume("PORT", 'Expected "port"');
    const nameToken = this.consumeIdentifierLike("Expected port name");
    const metaProps = {};
    const explicitId = this.parseOptId();
    this.parseOptMetaInto(metaProps);
    this.consume("COLON", 'Expected ":"');
    const typeRef = this.parseTypeRefText("Expected port type");
    let multiplicity;
    if (this.check("LBRACKET")) {
      this.advance();
      multiplicity = this.parseMultiplicity();
      this.consume("RBRACKET", 'Expected "]"');
    }
    let dir = "inout";
    if (this.check("IN") || this.check("OUT") || this.check("INOUT")) {
      dir = this.advance().value;
    }
    const endToken = this.consumeStatementEnd("port declaration", {
      allowImplicitEndBefore: ["PORT", "PROP", "PART", "REL", "TAG", "META", "RBRACE"]
    });
    const qualifiedName = this.joinQualifiedName(partQualifiedName, nameToken.value);
    return {
      id: this.stableId("port", qualifiedName, explicitId),
      elementType: "port",
      name: nameToken.value,
      qualifiedName,
      typeRef,
      dir,
      multiplicity,
      meta: this.buildMetadata(metaProps),
      source: this.rangeFrom(portToken, endToken)
    };
  }
  parseProp(partQualifiedName) {
    const propToken = this.consume("PROP", 'Expected "prop"');
    const nameToken = this.consumeIdentifierLike("Expected property name");
    this.consume("COLON", 'Expected ":"');
    const typeRef = this.parseTypeRefText("Expected property type");
    let defaultValue;
    if (this.check("EQ")) {
      this.advance();
      defaultValue = this.parseValue();
    }
    const endToken = this.consumeStatementEnd("property declaration", {
      allowImplicitEndBefore: ["PORT", "PROP", "PART", "REL", "TAG", "META", "RBRACE"]
    });
    const qualifiedName = this.joinQualifiedName(partQualifiedName, nameToken.value);
    return {
      id: this.deriveId("prop", qualifiedName),
      elementType: "prop",
      name: nameToken.value,
      qualifiedName,
      typeRef,
      defaultValue,
      source: this.rangeFrom(propToken, endToken)
    };
  }
  isStartOfPredicate() {
    return this.check("ALL") || this.check("PART") || this.check("REL") || this.check("TAG") || this.check("WHERE") || this.check("NOT") || this.check("LPAREN");
  }
  parsePredicate() {
    if (this.check("ALL")) {
      this.advance();
      return { kind: "all" };
    }
    if (this.check("PART")) {
      this.advance();
      return { kind: "part" };
    }
    if (this.check("REL")) {
      this.advance();
      return { kind: "rel" };
    }
    if (this.check("TAG")) {
      this.advance();
      this.consume("LPAREN", 'Expected "(" after "tag"');
      const tagToken = this.consume("IDENTIFIER", "Expected tag name");
      this.consume("RPAREN", 'Expected ")"');
      return { kind: "tag", tag: tagToken.value };
    }
    if (this.check("WHERE")) {
      this.advance();
      return { kind: "where", expr: this.parseExpr() };
    }
    const token = this.peek();
    this.addDiagnostic("error", `Expected predicate, got "${token.value}"`, token);
    throw new Error("Invalid predicate");
  }
  parseExpr() {
    return this.parseOrExpr();
  }
  parseOrExpr() {
    let expr = this.parseAndExpr();
    while (this.check("OR")) {
      this.advance();
      expr = { kind: "or", left: expr, right: this.parseAndExpr() };
    }
    return expr;
  }
  parseAndExpr() {
    let expr = this.parseNotExpr();
    while (this.check("AND")) {
      this.advance();
      expr = { kind: "and", left: expr, right: this.parseNotExpr() };
    }
    return expr;
  }
  parseNotExpr() {
    if (this.check("NOT")) {
      this.advance();
      return { kind: "not", expr: this.parseNotExpr() };
    }
    return this.parsePrimaryExpr();
  }
  parsePrimaryExpr() {
    if (this.check("LPAREN")) {
      this.advance();
      const expr = this.parseExpr();
      this.consume("RPAREN", 'Expected ")"');
      return expr;
    }
    return this.parseCmpExpr();
  }
  parseCmpExpr() {
    const fieldToken = this.check("IDENTIFIER") ? this.advance() : this.consume("META", "Expected field");
    const field = [fieldToken.value];
    while (this.check("DOT")) {
      this.advance();
      field.push((this.check("IDENTIFIER") ? this.advance() : this.consume("META", "Expected field segment")).value);
    }
    let op;
    if (this.check("EQEQ")) {
      this.advance();
      op = "==";
    } else if (this.check("NEQ")) {
      this.advance();
      op = "!=";
    } else if (this.check("MATCH")) {
      this.advance();
      op = "~=";
    } else if (this.check("IN")) {
      this.advance();
      op = "in";
    } else {
      const token = this.peek();
      this.addDiagnostic("error", "Expected comparison operator", token);
      throw new Error("Invalid comparison operator");
    }
    const lit = this.parseValue();
    return { kind: "cmp", field, op, lit };
  }
};
function parseSysML(sourceText, options) {
  try {
    const uri = options?.uri ?? "sysml:document";
    const allowPartialModel = options?.allowPartialModel ?? false;
    const lexer = new Lexer(sourceText, uri);
    const tokens = lexer.tokenize();
    const parser = new Parser(tokens, uri, sourceText);
    const { model, diagnostics } = parser.parse();
    const hasErrors = diagnostics.some((d) => d.severity === "error");
    if (!hasErrors) {
      const { graph, viewProjections } = compileSysMLModel(model);
      model.graph = graph;
      model.viewProjections = viewProjections;
    }
    return {
      success: !hasErrors,
      model: hasErrors ? allowPartialModel ? model : null : model,
      diagnostics,
      sourceText
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown parse error";
    return {
      success: false,
      model: null,
      diagnostics: [{ severity: "error", message, line: 1, column: 1 }],
      sourceText
    };
  }
}
function isValidParseResult(result) {
  return result.success && result.model !== null;
}

// src/formatter.ts
function formatSysmlSource(sourceText, options) {
  const uri = options?.uri;
  const result = parseSysML(sourceText, { uri });
  if (isValidParseResult(result)) return printModel(result.model);
  const errors = result.diagnostics.filter((d) => d.severity === "error");
  const onlySemicolonErrors = errors.length > 0 && errors.every((e) => /Expected\s+";"\b/.test(e.message));
  if (onlySemicolonErrors) {
    const fixed = autoInsertMissingSemicolons(sourceText);
    const retry = parseSysML(fixed, { uri });
    if (isValidParseResult(retry)) return printModel(retry.model);
  }
  const head = errors[0];
  const detail = head ? `${head.message} (L${head.line}:${head.column})` : "Unknown parse error";
  throw new Error(`Cannot format: fix parse errors first. ${detail}`);
}
function printModel(model) {
  const blocks = model.packages.map((pkg, idx) => printPackage(pkg, 0, { isTopLevel: true}));
  return blocks.filter(Boolean).join("\n\n").trimEnd() + "\n";
}
function printPackage(pkg, indentLevel, ctx) {
  const kw = ctx.isTopLevel ? "system" : "package";
  const id = extractExplicitId(pkg.id);
  const meta = printMeta(pkg.meta, indentLevel);
  const header = [kw, pkg.name, id].filter(Boolean).join(" ");
  const inner = [];
  for (const imp of pkg.imports ?? []) inner.push(`${indent(indentLevel + 1)}import ${quote(imp.path)};`);
  for (const letStmt of pkg.lets ?? []) {
    inner.push(`${indent(indentLevel + 1)}let ${letStmt.name} = ${printValue(letStmt.value, "let")};`);
  }
  for (const typeAlias of pkg.typeAliases ?? []) {
    inner.push(`${indent(indentLevel + 1)}type ${typeAlias.name} = ${typeAlias.typeRef};`);
  }
  for (const en of pkg.enums ?? []) {
    inner.push(
      `${indent(indentLevel + 1)}enum ${en.name} { ${en.values.join(", ")} };`
    );
  }
  for (const partDef of pkg.partDefs ?? []) inner.push(printPartDef(partDef, indentLevel + 1));
  for (const rel of pkg.relationships ?? []) inner.push(printRelationship(rel, indentLevel + 1));
  for (const view of pkg.views ?? []) inner.push(printView(view, indentLevel + 1));
  for (const nested of pkg.packages ?? []) inner.push(printPackage(nested, indentLevel + 1, { isTopLevel: false}));
  const metaBlock = meta ? ` ${meta}` : "";
  const body = inner.length ? `
${inner.join("\n")}
${indent(indentLevel)}` : "";
  return `${indent(indentLevel)}${header}${metaBlock} {${body}}`;
}
function printPartDef(partDef, indentLevel) {
  const id = extractExplicitId(partDef.id);
  const meta = printMeta(partDef.meta, indentLevel);
  const header = ["part", partDef.name, id].filter(Boolean).join(" ");
  const metaBlock = meta ? ` ${meta}` : "";
  const inner = [];
  for (const port of partDef.ports ?? []) inner.push(printPort(port, indentLevel + 1));
  for (const prop of partDef.props ?? []) inner.push(printProp(prop, indentLevel + 1));
  for (const partUsage of partDef.parts ?? []) inner.push(printPartUsage(partUsage, indentLevel + 1));
  if (inner.length === 0) {
    return `${indent(indentLevel)}${header}${metaBlock} {};`;
  }
  return `${indent(indentLevel)}${header}${metaBlock} {
${inner.join("\n")}
${indent(indentLevel)}};`;
}
function printPartUsage(usage, indentLevel) {
  const mult = usage.multiplicity ? printMultiplicity(usage.multiplicity) : "";
  return `${indent(indentLevel)}part ${usage.name} : ${usage.typeRef}${mult};`;
}
function printMultiplicity(m) {
  if (m.lower === 0 && m.upper === "*") return "[*]";
  if (m.lower === m.upper) return `[${m.lower}]`;
  return `[${m.lower}..${m.upper}]`;
}
function printPort(port, indentLevel) {
  const id = extractExplicitId(port.id);
  const meta = printMeta(port.meta, indentLevel);
  const metaBlock = meta ? ` ${meta}` : "";
  return `${indent(indentLevel)}port ${port.name}${id ? ` ${id}` : ""}${metaBlock} : ${port.typeRef} ${port.dir};`;
}
function printProp(prop, indentLevel) {
  const defaultValue = prop.defaultValue !== void 0 ? ` = ${printValue(prop.defaultValue, "prop")}` : "";
  return `${indent(indentLevel)}prop ${prop.name} : ${prop.typeRef}${defaultValue};`;
}
function printRelationship(rel, indentLevel) {
  const id = extractExplicitId(rel.id);
  const meta = printMeta(rel.meta, indentLevel);
  const metaBlock = meta ? ` ${meta}` : "";
  return `${indent(indentLevel)}rel ${rel.relType} ${rel.name}${id ? ` ${id}` : ""}${metaBlock} : ${rel.from.ref} -> ${rel.to.ref};`;
}
function printView(view, indentLevel) {
  const id = extractExplicitId(view.id);
  const meta = printMeta(view.meta, indentLevel);
  const metaBlock = meta ? ` ${meta}` : "";
  const header = `${indent(indentLevel)}view ${view.name}${id ? ` ${id}` : ""}${metaBlock} {`;
  const lines = [];
  for (const u of view.uses ?? []) lines.push(`${indent(indentLevel + 1)}use ${u};`);
  if (view.select && view.select.length) {
    lines.push(`${indent(indentLevel + 1)}select ${view.select.map((r) => r.ref).join(", ")};`);
  }
  for (const pred of view.includePredicates ?? []) {
    lines.push(`${indent(indentLevel + 1)}include ${printPredicate(pred)};`);
  }
  if (view.include && view.include.length) {
    lines.push(`${indent(indentLevel + 1)}include ${view.include.map((r) => r.ref).join(", ")};`);
  }
  for (const pred of view.excludePredicates ?? []) {
    lines.push(`${indent(indentLevel + 1)}exclude ${printPredicate(pred)};`);
  }
  if (view.exclude && view.exclude.length) {
    lines.push(`${indent(indentLevel + 1)}exclude ${view.exclude.map((r) => r.ref).join(", ")};`);
  }
  if (view.relMode) lines.push(`${indent(indentLevel + 1)}rels: ${view.relMode};`);
  if (view.layout) lines.push(`${indent(indentLevel + 1)}layout: ${view.layout};`);
  if (view.filter) {
    if (view.filter.states?.length) lines.push(`${indent(indentLevel + 1)}where states: [${view.filter.states.join(", ")}];`);
    if (view.filter.confidence?.length) lines.push(`${indent(indentLevel + 1)}where confidence: [${view.filter.confidence.join(", ")}];`);
    if (view.filter.tagsAny?.length) lines.push(`${indent(indentLevel + 1)}where tagsAny: ${printValue(view.filter.tagsAny, "meta")};`);
    if (view.filter.kindsAny?.length) lines.push(`${indent(indentLevel + 1)}where kindsAny: ${printValue(view.filter.kindsAny, "meta")};`);
    if (view.filter.relTypesAny?.length) lines.push(`${indent(indentLevel + 1)}where relTypesAny: ${printValue(view.filter.relTypesAny, "meta")};`);
  }
  if (lines.length === 0) {
    return `${header} }`;
  }
  return `${header}
${lines.join("\n")}
${indent(indentLevel)}}`;
}
function printPredicate(pred) {
  switch (pred.kind) {
    case "all":
      return "all";
    case "part":
      return "part";
    case "rel":
      return "rel";
    case "tag":
      return `tag(${pred.tag})`;
    case "where":
      return `where ${printExpr(pred.expr)}`;
  }
}
function printExpr(expr, parentPrecedence = 0) {
  const precedence = exprPrecedence(expr);
  const wrap = (s) => precedence < parentPrecedence ? `(${s})` : s;
  switch (expr.kind) {
    case "or": {
      const s = `${printExpr(expr.left, precedence)} or ${printExpr(expr.right, precedence)}`;
      return wrap(s);
    }
    case "and": {
      const s = `${printExpr(expr.left, precedence)} and ${printExpr(expr.right, precedence)}`;
      return wrap(s);
    }
    case "not": {
      const s = `not ${printExpr(expr.expr, precedence)}`;
      return wrap(s);
    }
    case "cmp": {
      const field = expr.field.join(".");
      const op = expr.op === "in" ? "in" : expr.op;
      const lit = printValue(expr.lit, "expr");
      return `${field} ${op} ${lit}`;
    }
  }
}
function exprPrecedence(expr) {
  switch (expr.kind) {
    case "or":
      return 1;
    case "and":
      return 2;
    case "not":
      return 3;
    case "cmp":
      return 4;
  }
}
function printMeta(meta, indentLevel) {
  if (!meta) return null;
  const entries = [];
  if (meta.kind !== void 0) entries.push(["kind", meta.kind]);
  if (meta.state !== void 0) entries.push(["state", meta.state]);
  if (meta.confidence !== void 0) entries.push(["confidence", meta.confidence]);
  const props = meta.props ?? {};
  if (props.owner !== void 0) entries.push(["owner", props.owner]);
  if (meta.notes !== void 0) entries.push(["notes", meta.notes]);
  if (meta.tags?.length) entries.push(["tags", meta.tags]);
  if (meta.questions?.length) entries.push(["questions", meta.questions]);
  if (meta.risks?.length) entries.push(["risks", meta.risks]);
  for (const key of Object.keys(props).sort((a, b) => a.localeCompare(b))) {
    if (key === "owner") continue;
    entries.push([key, props[key]]);
  }
  if (entries.length === 0) return null;
  const i = indent(indentLevel);
  const ii = indent(indentLevel + 1);
  const lines = entries.map(([k, v]) => `${ii}${k}: ${printValue(v, k)};`);
  return `meta {
${lines.join("\n")}
${i}}`;
}
function printValue(value, keyHint) {
  if (value === null) return "null";
  if (value === true) return "true";
  if (value === false) return "false";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "0";
  if (typeof value === "string") return printStringValue(value, keyHint);
  if (Array.isArray(value)) {
    return `[${value.map((v) => printValue(v, keyHint)).join(", ")}]`;
  }
  if (typeof value === "object") {
    if (typeof value.$ref === "string") return value.$ref;
    const keys = Object.keys(value);
    return `{ ${keys.map((k) => `${k}: ${printValue(value[k], k)}`).join(", ")} }`;
  }
  return quote(String(value));
}
function printStringValue(value, keyHint) {
  if (value === "?" || value === "*") return value;
  const lowerKey = keyHint.toLowerCase();
  if (lowerKey === "state" || lowerKey === "confidence") return value;
  if (lowerKey === "notes" || lowerKey === "owner" || lowerKey === "questions" || lowerKey === "risks") return quote(value);
  if (lowerKey === "tags") return quote(value);
  if (isSafeBareIdentifier(value)) return value;
  return quote(value);
}
function isSafeBareIdentifier(value) {
  return /^[A-Za-z_][\w-]*(?:(?:::|\.|::)[A-Za-z_][\w-]*)*$/.test(value);
}
function quote(value) {
  const escaped = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n").replace(/\t/g, "\\t");
  return `"${escaped}"`;
}
function indent(level) {
  return "  ".repeat(Math.max(0, level));
}
function extractExplicitId(modelId) {
  const idx = modelId.lastIndexOf(":@");
  if (idx < 0) return null;
  return modelId.slice(idx + 1);
}
function autoInsertMissingSemicolons(sourceText) {
  const lines = sourceText.split("\n");
  let inBlockComment = false;
  let metaDepth = 0;
  let metaBracePending = false;
  const out = [];
  for (const rawLine of lines) {
    const line = rawLine;
    const scan = scanCodeParts(line, inBlockComment);
    inBlockComment = scan.inBlockComment;
    const braceScan = scanMetaBraceState(scan.code, metaDepth, metaBracePending);
    metaDepth = braceScan.metaDepth;
    metaBracePending = braceScan.metaBracePending;
    if (scan.inBlockComment || scan.code.trim().length === 0) {
      out.push(line);
      continue;
    }
    const trimmed = scan.code.trimEnd();
    if (trimmed.length === 0) {
      out.push(line);
      continue;
    }
    const alreadyTerminated = /[;{]$/.test(trimmed);
    const needsSemicolon = !alreadyTerminated && (looksLikeStatementNeedingSemicolon(trimmed) || metaDepth > 0 && looksLikeMetaAssignment(trimmed));
    if (!needsSemicolon) {
      out.push(line);
      continue;
    }
    if (trimmed === "{" || trimmed === "}" || trimmed.endsWith("{")) {
      out.push(line);
      continue;
    }
    if (metaDepth > 0) {
      if (/[\\[{(]$/.test(trimmed)) {
        out.push(line);
        continue;
      }
    }
    const updated = scan.comment ? `${trimmed};${scan.comment}` : `${trimmed};`;
    out.push(replaceCodePreservingIndent(line, updated));
  }
  return out.join("\n");
}
function looksLikeStatementNeedingSemicolon(trimmed) {
  if (/^(system|package)\\b/.test(trimmed)) return false;
  if (/^view\\b/.test(trimmed)) return false;
  if (/^enum\\b/.test(trimmed) && trimmed.includes("{")) return false;
  if (/^(import|let|type|rel|prop|port|use|select|include|exclude)\\b/.test(trimmed)) return true;
  if (/^(rels\\s*:|layout\\s*:|where\\b)/.test(trimmed)) return true;
  if (/^part\\s+\\S+\\s*:\\s*\\S+/.test(trimmed)) return true;
  return false;
}
function looksLikeMetaAssignment(trimmed) {
  if (!/^[A-Za-z_][\\w-]*\\s*:/.test(trimmed)) return false;
  if (/^(rels\\s*:|layout\\s*:)/.test(trimmed)) return false;
  return true;
}
function scanCodeParts(line, inBlockComment) {
  let code = "";
  let comment = "";
  let inString = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    const next = line[i + 1] ?? "";
    if (inBlockComment) {
      code += ch;
      if (ch === "*" && next === "/") {
        code += next;
        i += 2;
        inBlockComment = false;
        continue;
      }
      i++;
      continue;
    }
    if (!inString && ch === "/" && next === "*") {
      code += ch + next;
      i += 2;
      inBlockComment = true;
      continue;
    }
    if (!inString && ch === "/" && next === "/") {
      comment = line.slice(i);
      break;
    }
    if (ch === '"' && line[i - 1] !== "\\\\") {
      inString = !inString;
    }
    code += ch;
    i++;
  }
  return { code, comment, inBlockComment };
}
function scanMetaBraceState(code, metaDepth, metaBracePending) {
  let inString = false;
  for (let i = 0; i < code.length; i++) {
    const ch = code[i];
    if (ch === '"' && code[i - 1] !== "\\\\") inString = !inString;
    if (inString) continue;
    if (!metaBracePending && startsWordAt(code, i, "meta")) {
      metaBracePending = true;
      i += 3;
      continue;
    }
    if (ch === "{") {
      if (metaBracePending) {
        metaDepth++;
        metaBracePending = false;
      } else if (metaDepth > 0) {
        metaDepth++;
      }
    } else if (ch === "}") {
      if (metaDepth > 0) metaDepth--;
      if (metaDepth === 0) metaBracePending = false;
    }
  }
  return { metaDepth, metaBracePending };
}
function startsWordAt(code, index, word) {
  if (index < 0 || index + word.length > code.length) return false;
  const before = code[index - 1];
  const after = code[index + word.length];
  if (before && /[A-Za-z0-9_]/.test(before)) return false;
  if (after && /[A-Za-z0-9_]/.test(after)) return false;
  return code.slice(index, index + word.length) === word;
}
function replaceCodePreservingIndent(originalLine, newCode) {
  const leading = originalLine.match(/^\\s*/)?.[0] ?? "";
  return leading + newCode.trimStart();
}

// src/exprEvaluator.ts
function evalViewExpr(element, expr) {
  switch (expr.kind) {
    case "or":
      return evalViewExpr(element, expr.left) || evalViewExpr(element, expr.right);
    case "and":
      return evalViewExpr(element, expr.left) && evalViewExpr(element, expr.right);
    case "not":
      return !evalViewExpr(element, expr.expr);
    case "cmp": {
      const left = readField2(element, expr.field);
      const right = expr.lit;
      switch (expr.op) {
        case "==":
          return looselyEqual2(left, right);
        case "!=":
          return !looselyEqual2(left, right);
        case "~=":
          return typeof left === "string" && typeof right === "string" ? left.toLowerCase().includes(right.toLowerCase()) : false;
        case "in":
          return isIn2(left, right);
        default:
          return false;
      }
    }
    default:
      return false;
  }
}
function readField2(element, field) {
  if (!field.length) return void 0;
  const [head, ...rest] = field;
  if (head === "name") return rest.length ? void 0 : element.name;
  if (head === "qualifiedName") return rest.length ? void 0 : element.qualifiedName;
  if (head === "elementType") return rest.length ? void 0 : element.elementType;
  if (head === "id") return rest.length ? void 0 : element.id;
  if (head === "tags" || head === "tag") {
    return rest.length ? void 0 : element.meta?.tags ?? [];
  }
  if (head === "meta") {
    let current = element.meta ?? {};
    for (const seg of rest) {
      if (current && typeof current === "object" && seg in current) {
        current = current[seg];
      } else {
        return "?";
      }
    }
    return current;
  }
  if ("relType" in element && head === "relType") {
    return rest.length ? void 0 : element.relType;
  }
  if ("typeRef" in element && head === "typeRef") {
    return rest.length ? void 0 : element.typeRef;
  }
  return void 0;
}
function looselyEqual2(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => looselyEqual2(v, b[i]));
  }
  if (Array.isArray(a)) return a.some((v) => looselyEqual2(v, b));
  if (Array.isArray(b)) return b.some((v) => looselyEqual2(a, v));
  return String(a) === String(b);
}
function isIn2(left, right) {
  if (Array.isArray(right)) return right.some((v) => looselyEqual2(left, v));
  if (Array.isArray(left)) return left.some((v) => looselyEqual2(v, right));
  return false;
}

// src/queryExecutor.ts
var TARGET_TO_ELEMENT_TYPE = {
  all: null,
  // null = match all
  package: "package",
  part: "partDef",
  partUsage: "partUsage",
  port: "port",
  prop: "prop",
  rel: "relationship",
  view: "view",
  enum: "enum",
  typeAlias: "typeAlias",
  let: "let",
  import: "import"
};
function executeQuery(model, query) {
  const startTime = performance.now();
  try {
    const graph = model.graph ?? buildModelGraph(model);
    let candidates = getCandidates(graph, query.select ?? "all");
    if (query.where) {
      candidates = candidates.filter((el) => evalViewExpr(el, query.where));
    }
    if (query.include?.length) {
      const includeIds = resolveRefs(graph, query.include);
      const includeSet = new Set(includeIds);
      for (const id of includeIds) {
        const el = graph.index.byId[id];
        if (el && !candidates.some((c) => c.id === id)) {
          candidates.push(el);
        }
      }
    }
    if (query.exclude?.length) {
      const excludeIds = new Set(resolveRefs(graph, query.exclude));
      candidates = candidates.filter((el) => !excludeIds.has(el.id));
    }
    const totalCount = candidates.length;
    if (query.offset && query.offset > 0) {
      candidates = candidates.slice(query.offset);
    }
    if (query.limit && query.limit > 0) {
      candidates = candidates.slice(0, query.limit);
    }
    const executionTimeMs = performance.now() - startTime;
    return {
      success: true,
      elements: candidates,
      totalCount,
      executionTimeMs,
      query
    };
  } catch (error) {
    const executionTimeMs = performance.now() - startTime;
    return {
      success: false,
      elements: [],
      totalCount: 0,
      executionTimeMs,
      error: error instanceof Error ? error.message : "Unknown query error",
      query
    };
  }
}
function getCandidates(graph, target) {
  const elementType = TARGET_TO_ELEMENT_TYPE[target];
  if (elementType === null) {
    return Object.values(graph.elements);
  }
  if (Array.isArray(elementType)) {
    const candidates = [];
    for (const type of elementType) {
      const ids2 = graph.index.byElementType[type] ?? [];
      for (const id of ids2) {
        const el = graph.index.byId[id];
        if (el) candidates.push(el);
      }
    }
    return candidates;
  }
  const ids = graph.index.byElementType[elementType] ?? [];
  return ids.map((id) => graph.index.byId[id]).filter((el) => el !== void 0);
}
function resolveRefs(graph, refs) {
  const ids = [];
  for (const ref of refs) {
    if (ref.ref === "?" || ref.ref === "*") continue;
    const id = resolveByNameOrQualified2(graph, ref.ref);
    if (id) ids.push(id);
  }
  return ids;
}
function resolveByNameOrQualified2(graph, refText) {
  const raw = refText.startsWith("#") ? refText.slice(1) : refText;
  const byQn = graph.index.byQualifiedName[raw];
  if (byQn) return byQn;
  if (raw.includes("::")) {
    const dotted = raw.replace(/::/g, ".");
    const byDotted = graph.index.byQualifiedName[dotted];
    if (byDotted) return byDotted;
  }
  if (!raw.includes(".")) {
    const matches = graph.index.byName[raw] ?? [];
    if (matches.length === 1) return matches[0];
  }
  return void 0;
}

// src/queryParser.ts
var KEYWORDS2 = {
  all: "ALL",
  part: "PART",
  partusage: "PARTUSAGE",
  port: "PORT",
  prop: "PROP",
  rel: "REL",
  view: "VIEW",
  enum: "ENUM",
  package: "PACKAGE",
  typealias: "TYPEALIAS",
  let: "LET",
  import: "IMPORT",
  where: "WHERE",
  and: "AND",
  or: "OR",
  not: "NOT",
  in: "IN",
  limit: "LIMIT",
  offset: "OFFSET",
  true: "TRUE",
  false: "FALSE"
};
var QueryLexer = class {
  source;
  pos = 0;
  line = 1;
  column = 1;
  tokens = [];
  constructor(source) {
    this.source = source;
  }
  tokenize() {
    while (!this.isAtEnd()) {
      this.skipWhitespace();
      if (this.isAtEnd()) break;
      this.scanToken();
    }
    this.tokens.push({ type: "EOF", value: "", line: this.line, column: this.column });
    return this.tokens;
  }
  isAtEnd() {
    return this.pos >= this.source.length;
  }
  peek() {
    return this.source[this.pos] ?? "";
  }
  peekNext() {
    return this.source[this.pos + 1] ?? "";
  }
  advance() {
    const ch = this.source[this.pos++];
    if (ch === "\n") {
      this.line++;
      this.column = 1;
    } else {
      this.column++;
    }
    return ch;
  }
  addToken(type, value, line, column) {
    this.tokens.push({ type, value, line, column });
  }
  skipWhitespace() {
    while (!this.isAtEnd()) {
      const ch = this.peek();
      if (ch === " " || ch === "	" || ch === "\n" || ch === "\r") {
        this.advance();
      } else {
        break;
      }
    }
  }
  scanToken() {
    const startLine = this.line;
    const startColumn = this.column;
    const ch = this.advance();
    switch (ch) {
      case "(":
        this.addToken("LPAREN", "(", startLine, startColumn);
        break;
      case ")":
        this.addToken("RPAREN", ")", startLine, startColumn);
        break;
      case "[":
        this.addToken("LBRACKET", "[", startLine, startColumn);
        break;
      case "]":
        this.addToken("RBRACKET", "]", startLine, startColumn);
        break;
      case ".":
        this.addToken("DOT", ".", startLine, startColumn);
        break;
      case ",":
        this.addToken("COMMA", ",", startLine, startColumn);
        break;
      case "=":
        if (this.peek() === "=") {
          this.advance();
          this.addToken("EQ", "==", startLine, startColumn);
        }
        break;
      case "!":
        if (this.peek() === "=") {
          this.advance();
          this.addToken("NE", "!=", startLine, startColumn);
        }
        break;
      case "~":
        if (this.peek() === "=") {
          this.advance();
          this.addToken("CONTAINS", "~=", startLine, startColumn);
        }
        break;
      case '"':
      case "'":
        this.scanString(ch, startLine, startColumn);
        break;
      default:
        if (this.isDigit(ch) || ch === "-" && this.isDigit(this.peek())) {
          this.scanNumber(ch, startLine, startColumn);
        } else if (this.isAlpha(ch)) {
          this.scanIdentifier(ch, startLine, startColumn);
        }
        break;
    }
  }
  scanString(quote2, startLine, startColumn) {
    let value = "";
    while (!this.isAtEnd() && this.peek() !== quote2) {
      if (this.peek() === "\\" && this.peekNext() === quote2) {
        this.advance();
        value += this.advance();
      } else {
        value += this.advance();
      }
    }
    if (!this.isAtEnd()) {
      this.advance();
    }
    this.addToken("STRING", value, startLine, startColumn);
  }
  scanNumber(first, startLine, startColumn) {
    let value = first;
    while (!this.isAtEnd() && (this.isDigit(this.peek()) || this.peek() === ".")) {
      value += this.advance();
    }
    this.addToken("NUMBER", value, startLine, startColumn);
  }
  scanIdentifier(first, startLine, startColumn) {
    let value = first;
    while (!this.isAtEnd() && this.isAlphaNumeric(this.peek())) {
      value += this.advance();
    }
    const keyword = KEYWORDS2[value.toLowerCase()];
    this.addToken(keyword ?? "IDENTIFIER", value, startLine, startColumn);
  }
  isDigit(ch) {
    return ch >= "0" && ch <= "9";
  }
  isAlpha(ch) {
    return ch >= "a" && ch <= "z" || ch >= "A" && ch <= "Z" || ch === "_";
  }
  isAlphaNumeric(ch) {
    return this.isAlpha(ch) || this.isDigit(ch);
  }
};
var QueryParser = class {
  tokens;
  pos = 0;
  errors = [];
  constructor(tokens) {
    this.tokens = tokens;
  }
  parse() {
    const query = this.parseQuery();
    return { query, errors: this.errors };
  }
  parseQuery() {
    const query = {};
    query.select = this.parseTarget();
    if (this.check("WHERE")) {
      this.advance();
      query.where = this.parseExpr();
    }
    if (this.check("LIMIT")) {
      this.advance();
      const numToken = this.consume("NUMBER", "Expected number after limit");
      query.limit = parseInt(numToken.value, 10);
    }
    if (this.check("OFFSET")) {
      this.advance();
      const numToken = this.consume("NUMBER", "Expected number after offset");
      query.offset = parseInt(numToken.value, 10);
    }
    return query;
  }
  parseTarget() {
    if (this.check("ALL")) {
      this.advance();
      return "all";
    }
    if (this.check("PART")) {
      this.advance();
      return "part";
    }
    if (this.check("PARTUSAGE")) {
      this.advance();
      return "partUsage";
    }
    if (this.check("PORT")) {
      this.advance();
      return "port";
    }
    if (this.check("PROP")) {
      this.advance();
      return "prop";
    }
    if (this.check("REL")) {
      this.advance();
      return "rel";
    }
    if (this.check("VIEW")) {
      this.advance();
      return "view";
    }
    if (this.check("ENUM")) {
      this.advance();
      return "enum";
    }
    if (this.check("PACKAGE")) {
      this.advance();
      return "package";
    }
    if (this.check("TYPEALIAS")) {
      this.advance();
      return "typeAlias";
    }
    if (this.check("LET")) {
      this.advance();
      return "let";
    }
    if (this.check("IMPORT")) {
      this.advance();
      return "import";
    }
    return "all";
  }
  parseExpr() {
    return this.parseOrExpr();
  }
  parseOrExpr() {
    let left = this.parseAndExpr();
    while (this.check("OR")) {
      this.advance();
      const right = this.parseAndExpr();
      left = { kind: "or", left, right };
    }
    return left;
  }
  parseAndExpr() {
    let left = this.parseNotExpr();
    while (this.check("AND")) {
      this.advance();
      const right = this.parseNotExpr();
      left = { kind: "and", left, right };
    }
    return left;
  }
  parseNotExpr() {
    if (this.check("NOT")) {
      this.advance();
      const expr = this.parsePrimary();
      return { kind: "not", expr };
    }
    return this.parsePrimary();
  }
  parsePrimary() {
    if (this.check("LPAREN")) {
      this.advance();
      const expr = this.parseExpr();
      this.consume("RPAREN", "Expected )");
      return expr;
    }
    return this.parseComparison();
  }
  parseComparison() {
    const field = this.parseFieldPath();
    const op = this.parseOp();
    const lit = this.parseValue();
    return { kind: "cmp", field, op, lit };
  }
  parseFieldPath() {
    const path = [];
    const first = this.consume("IDENTIFIER", "Expected field name");
    path.push(first.value);
    while (this.check("DOT")) {
      this.advance();
      const next = this.consume("IDENTIFIER", "Expected field name after .");
      path.push(next.value);
    }
    return path;
  }
  parseOp() {
    if (this.check("EQ")) {
      this.advance();
      return "==";
    }
    if (this.check("NE")) {
      this.advance();
      return "!=";
    }
    if (this.check("CONTAINS")) {
      this.advance();
      return "~=";
    }
    if (this.check("IN")) {
      this.advance();
      return "in";
    }
    this.addError("Expected operator (==, !=, ~=, in)");
    return "==";
  }
  parseValue() {
    if (this.check("STRING")) {
      const token = this.advance();
      return token.value;
    }
    if (this.check("NUMBER")) {
      const token = this.advance();
      const num = parseFloat(token.value);
      return isNaN(num) ? token.value : num;
    }
    if (this.check("TRUE")) {
      this.advance();
      return true;
    }
    if (this.check("FALSE")) {
      this.advance();
      return false;
    }
    if (this.check("LBRACKET")) {
      return this.parseArray();
    }
    this.addError("Expected value");
    return "";
  }
  parseArray() {
    this.advance();
    const values = [];
    if (!this.check("RBRACKET")) {
      values.push(this.parseValue());
      while (this.check("COMMA")) {
        this.advance();
        values.push(this.parseValue());
      }
    }
    this.consume("RBRACKET", "Expected ]");
    return values;
  }
  check(type) {
    return this.current().type === type;
  }
  current() {
    return this.tokens[this.pos] ?? { type: "EOF", value: "", line: 1, column: 1 };
  }
  advance() {
    const token = this.current();
    if (token.type !== "EOF") {
      this.pos++;
    }
    return token;
  }
  consume(type, message) {
    if (this.check(type)) {
      return this.advance();
    }
    this.addError(message);
    return { type, value: "", line: this.current().line, column: this.current().column };
  }
  addError(message) {
    const token = this.current();
    this.errors.push({ message, line: token.line, column: token.column });
  }
};
function parseQuery(queryText) {
  const lexer = new QueryLexer(queryText);
  const tokens = lexer.tokenize();
  const parser = new QueryParser(tokens);
  return parser.parse();
}
function isValidQuery(parsed) {
  return parsed.errors.length === 0;
}

// src/query.ts
function queryModel(model, query) {
  if (typeof query === "string") {
    const parsed = parseQuery(query);
    if (!isValidQuery(parsed)) {
      return {
        success: false,
        elements: [],
        totalCount: 0,
        executionTimeMs: 0,
        error: parsed.errors.map((e) => `${e.message} at ${e.line}:${e.column}`).join("; "),
        query: parsed.query
      };
    }
    return executeQuery(model, parsed.query);
  }
  return executeQuery(model, query);
}
var FluentQueryBuilder = class {
  query = {};
  currentExpr = null;
  pendingNot = false;
  select(target) {
    this.query.select = target;
    return this;
  }
  where(field, op, value) {
    const expr = this.buildCmpExpr(field, op, value);
    this.currentExpr = this.pendingNot ? { kind: "not", expr } : expr;
    this.pendingNot = false;
    return this;
  }
  and(field, op, value) {
    if (!this.currentExpr) {
      return this.where(field, op, value);
    }
    const right = this.buildCmpExpr(field, op, value);
    const rightExpr = this.pendingNot ? { kind: "not", expr: right } : right;
    this.currentExpr = { kind: "and", left: this.currentExpr, right: rightExpr };
    this.pendingNot = false;
    return this;
  }
  or(field, op, value) {
    if (!this.currentExpr) {
      return this.where(field, op, value);
    }
    const right = this.buildCmpExpr(field, op, value);
    const rightExpr = this.pendingNot ? { kind: "not", expr: right } : right;
    this.currentExpr = { kind: "or", left: this.currentExpr, right: rightExpr };
    this.pendingNot = false;
    return this;
  }
  not() {
    this.pendingNot = true;
    return this;
  }
  include(...refs) {
    this.query.include = (this.query.include ?? []).concat(refs.map((r) => ({ ref: r })));
    return this;
  }
  exclude(...refs) {
    this.query.exclude = (this.query.exclude ?? []).concat(refs.map((r) => ({ ref: r })));
    return this;
  }
  limit(n) {
    this.query.limit = n;
    return this;
  }
  offset(n) {
    this.query.offset = n;
    return this;
  }
  build() {
    if (this.currentExpr) {
      this.query.where = this.currentExpr;
    }
    return this.query;
  }
  /**
   * Execute the built query against a model.
   */
  run(model) {
    return executeQuery(model, this.build());
  }
  buildCmpExpr(field, op, value) {
    const fieldPath = field.split(".");
    return { kind: "cmp", field: fieldPath, op, lit: value };
  }
};
var Query = {
  /**
   * Start building a query with a select target.
   */
  select(target) {
    return new FluentQueryBuilder().select(target);
  },
  /**
   * Start building a query with a where clause (defaults to 'all').
   */
  where(field, op, value) {
    return new FluentQueryBuilder().where(field, op, value);
  },
  /**
   * Create a new empty builder.
   */
  create() {
    return new FluentQueryBuilder();
  }
};

// src/validation.ts
var ValidationRuleRegistry = class {
  rules = /* @__PURE__ */ new Map();
  severityOverrides = /* @__PURE__ */ new Map();
  /** Register a programmatic validation rule. Replaces any existing rule with the same id. */
  register(rule) {
    this.rules.set(rule.id, rule);
  }
  /** Register multiple rules at once. */
  registerAll(rules) {
    for (const rule of rules) {
      this.rules.set(rule.id, rule);
    }
  }
  /** Remove a registered rule by id. */
  unregister(ruleId) {
    this.rules.delete(ruleId);
  }
  /** Check if a rule is registered. */
  has(ruleId) {
    return this.rules.has(ruleId);
  }
  /** Get all registered external rules. */
  getAll() {
    return [...this.rules.values()];
  }
  /** Get all registered rules in a specific category. */
  getByCategory(category) {
    return [...this.rules.values()].filter((r) => r.category === category);
  }
  /**
   * Override the severity of a rule (model-defined or external).
   * This applies during evaluation — the original rule definition is not mutated.
   */
  overrideSeverity(ruleId, severity) {
    this.severityOverrides.set(ruleId, severity);
  }
  /** Remove a severity override, reverting to the rule's default severity. */
  clearSeverityOverride(ruleId) {
    this.severityOverrides.delete(ruleId);
  }
  /** Clear all severity overrides. */
  clearAllSeverityOverrides() {
    this.severityOverrides.clear();
  }
  /**
   * Get the effective severity for a rule, accounting for overrides.
   * Falls back to the provided default if no override is set.
   */
  getEffectiveSeverity(ruleId, defaultSeverity) {
    return this.severityOverrides.get(ruleId) ?? defaultSeverity;
  }
};
function toQueryTarget(target) {
  return target;
}
function interpolateMessage(template, element) {
  return template.replace(/\{name\}/g, element.name || "<unnamed>");
}
function resolveTargetElements(model, target) {
  const query = {
    select: target
  };
  const result = executeQuery(model, query);
  return result.success ? result.elements : [];
}
function evaluateValidationRules(model, registry) {
  const reports = [];
  for (const rule of model.allRules ?? []) {
    const query = {
      select: toQueryTarget(rule.target) ?? "all",
      where: rule.where
    };
    const effectiveSeverity = registry ? registry.getEffectiveSeverity(rule.id, rule.severity) : rule.severity;
    const candidates = executeQuery(model, query);
    const issues = [];
    if (candidates.success) {
      for (const element of candidates.elements) {
        if (!evalViewExpr(element, rule.assert)) {
          issues.push({
            ruleId: rule.id,
            ruleName: rule.name,
            severity: effectiveSeverity,
            message: rule.message ?? rule.name,
            element
          });
        }
      }
    }
    reports.push({
      rule: { ...rule, severity: effectiveSeverity },
      checkedCount: candidates.success ? candidates.elements.length : 0,
      failedCount: issues.length,
      issues
    });
  }
  if (registry) {
    for (const extRule of registry.getAll()) {
      const effectiveSeverity = registry.getEffectiveSeverity(
        extRule.id,
        extRule.severity
      );
      const candidates = resolveTargetElements(model, extRule.target);
      const issues = [];
      for (const element of candidates) {
        if (!extRule.check(element, model)) {
          issues.push({
            ruleId: extRule.id,
            ruleName: extRule.name,
            severity: effectiveSeverity,
            message: interpolateMessage(extRule.message, element),
            element
          });
        }
      }
      const syntheticRule = {
        id: extRule.id,
        elementType: "rule",
        name: extRule.name,
        qualifiedName: `external::${extRule.id}`,
        target: extRule.target,
        severity: effectiveSeverity,
        assert: { kind: "cmp", field: ["name"], op: "!=", lit: "" },
        message: extRule.message
      };
      reports.push({
        rule: syntheticRule,
        checkedCount: candidates.length,
        failedCount: issues.length,
        issues
      });
    }
  }
  return reports.sort((a, b) => {
    if (a.failedCount !== b.failedCount) return b.failedCount - a.failedCount;
    return a.rule.name.localeCompare(b.rule.name);
  });
}
function summarizeValidation(reports) {
  let error = 0;
  let warning = 0;
  let info = 0;
  for (const report of reports) {
    if (!report.failedCount) continue;
    if (report.rule.severity === "error") error += report.failedCount;
    else if (report.rule.severity === "warning") warning += report.failedCount;
    else info += report.failedCount;
  }
  return { error, warning, info, totalFailed: error + warning + info };
}
function isModuleImportPath(path) {
  const p = path.trim();
  if (!p) return false;
  if (p.startsWith("http://") || p.startsWith("https://")) return true;
  if (p.startsWith("file://")) return true;
  if (p.startsWith("./") || p.startsWith("../")) return true;
  if (p.startsWith("/") || p.startsWith("~")) return true;
  if (p.includes("\\") || p.includes("/")) return true;
  if (/\.(sysml|sysml2|txt|json|yaml|yml)$/i.test(p)) return true;
  return false;
}
async function parseSysMLWithImports(root, resolver, options) {
  const maxDepth = options?.maxDepth ?? 8;
  const maxModules = options?.maxModules ?? 50;
  const diagnostics = [];
  const modules = [];
  const models = [];
  const visitedByUri = /* @__PURE__ */ new Set();
  const visitedByPath = /* @__PURE__ */ new Set();
  async function loadModule(module, depth) {
    if (depth > maxDepth) {
      diagnostics.push({
        severity: "warning",
        message: `Import depth exceeded (>${maxDepth}) at ${module.uri}`,
        line: 1,
        column: 1
      });
      return;
    }
    if (modules.length >= maxModules) {
      diagnostics.push({
        severity: "warning",
        message: `Import module limit exceeded (>${maxModules})`,
        line: 1,
        column: 1
      });
      return;
    }
    if (visitedByUri.has(module.uri)) return;
    visitedByUri.add(module.uri);
    const result = parseSysML(module.sourceText, { uri: module.uri });
    diagnostics.push(...result.diagnostics);
    if (!result.success || !result.model) return;
    modules.push(module);
    models.push(result.model);
    for (const imp of result.model.allImports) {
      const path = imp.path;
      const dedupeKey = `${module.uri}::${path}`;
      if (visitedByPath.has(dedupeKey)) continue;
      visitedByPath.add(dedupeKey);
      if (!isModuleImportPath(path)) continue;
      const resolved = await resolver.resolve(path, module);
      if (!resolved) {
        diagnostics.push({
          severity: "warning",
          message: `Unresolved import: "${path}"`,
          line: imp.source?.startLine ?? 1,
          column: imp.source?.startColumn ?? 1,
          endLine: imp.source?.endLine,
          endColumn: imp.source?.endColumn
        });
        continue;
      }
      await loadModule(resolved, depth + 1);
    }
  }
  await loadModule(root, 0);
  const hasErrors = diagnostics.some((d) => d.severity === "error");
  if (models.length === 0) {
    return { success: false, model: null, diagnostics, sourceText: root.sourceText, modules };
  }
  const mergedPackages = models.flatMap((m) => m.packages);
  const mergedModel = buildModelFromPackages(mergedPackages);
  const { graph, viewProjections } = compileSysMLModel(mergedModel);
  mergedModel.graph = graph;
  mergedModel.viewProjections = viewProjections;
  return { success: !hasErrors, model: mergedModel, diagnostics, sourceText: root.sourceText, modules };
}

// src/diagnosticFixes.ts
function isMissingSemicolonDiagnostic(d) {
  return d.severity === "error" && /Expected\s+\";\"/i.test(d.message);
}
function lineStartOffsets(text) {
  const starts = [0];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\n") starts.push(i + 1);
  }
  return starts;
}
function lineColToIndex(text, line, column) {
  const starts = lineStartOffsets(text);
  const lineIdx = Math.max(0, Math.min(starts.length - 1, line - 1));
  const start = starts[lineIdx] ?? 0;
  return start + Math.max(0, column - 1);
}
function getFixesForDiagnostic(sourceText, diag) {
  if (!sourceText) return [];
  if (!diag) return [];
  if (isMissingSemicolonDiagnostic(diag)) {
    const line = diag.endLine ?? diag.line;
    const column = diag.endColumn ?? diag.column;
    const idx = lineColToIndex(sourceText, line, column);
    const already = sourceText[idx] === ";";
    if (already) return [];
    return [
      {
        id: "insert-semicolon",
        title: 'Insert missing ";"',
        edit: {
          text: ";",
          range: { startLine: line, startColumn: column, endLine: line, endColumn: column }
        }
      }
    ];
  }
  return [];
}
function applyTextEdit(sourceText, edit) {
  const start = lineColToIndex(sourceText, edit.range.startLine, edit.range.startColumn);
  const end = lineColToIndex(sourceText, edit.range.endLine, edit.range.endColumn);
  return sourceText.slice(0, start) + edit.text + sourceText.slice(end);
}

// src/spec-to-ir.ts
var idCounter = 0;
function generateId() {
  return `elem_${++idCounter}`;
}
function qnToString(qn) {
  return qn.segments.join("::");
}
function convertSourceRange(range) {
  return {
    startLine: range.startLine,
    startColumn: range.startColumn,
    endLine: range.endLine,
    endColumn: range.endColumn,
    startOffset: range.startOffset,
    endOffset: range.endOffset,
    uri: range.uri
  };
}
function createElementRef(qn) {
  return {
    ref: qnToString(qn),
    source: convertSourceRange(qn.range)
  };
}
function parseMultiplicity(text) {
  if (!text) return void 0;
  const match = text.match(/\[(\d+|\*)(?:\.\.(\d+|\*))?\]/);
  if (!match) return void 0;
  const lower = match[1] === "*" ? 0 : parseInt(match[1], 10);
  const upper = match[2] ? match[2] === "*" ? "*" : parseInt(match[2], 10) : lower;
  return { lower, upper };
}
function transformSpecToIR(doc) {
  idCounter = 0;
  const model = (0, model_exports.createEmptyModel)();
  for (const member of doc.members) {
    if (member.kind === "package") {
      const pkg = transformPackage(member, []);
      model.packages.push(pkg);
    } else {
      const rootPkg = transformMembersToPackage(doc.members, []);
      if (rootPkg) {
        model.packages.push(rootPkg);
      }
      break;
    }
  }
  flattenModel(model);
  return model;
}
function transformMembersToPackage(members, prefix) {
  const pkg = {
    id: generateId(),
    elementType: "package",
    name: "(root)",
    qualifiedName: prefix.join("::") || "(root)",
    partDefs: [],
    partUsages: [],
    actionDefs: [],
    portDefs: [],
    itemDefs: [],
    stateDefs: [],
    requirementDefs: [],
    constraintDefs: [],
    connectionDefs: [],
    connections: [],
    allocationDefs: [],
    allocations: [],
    useCaseDefs: [],
    interfaceDefs: [],
    verificationDefs: [],
    analysisDefs: [],
    calcDefs: [],
    viewDefs: [],
    viewpointDefs: [],
    renderingDefs: [],
    metadataDefs: [],
    occurrenceDefs: [],
    satisfies: [],
    asserts: [],
    relationships: [],
    views: [],
    rules: [],
    enums: [],
    typeAliases: [],
    lets: [],
    imports: [],
    packages: []
  };
  let hasContent = false;
  for (const m of members) {
    const result = transformMember(m, prefix, pkg);
    if (result) hasContent = true;
  }
  return hasContent ? pkg : null;
}
function transformPackage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const pkg = {
    id: generateId(),
    elementType: "package",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    partDefs: [],
    partUsages: [],
    actionDefs: [],
    portDefs: [],
    itemDefs: [],
    stateDefs: [],
    requirementDefs: [],
    constraintDefs: [],
    connectionDefs: [],
    connections: [],
    allocationDefs: [],
    allocations: [],
    useCaseDefs: [],
    interfaceDefs: [],
    verificationDefs: [],
    analysisDefs: [],
    calcDefs: [],
    viewDefs: [],
    viewpointDefs: [],
    renderingDefs: [],
    metadataDefs: [],
    occurrenceDefs: [],
    satisfies: [],
    asserts: [],
    relationships: [],
    views: [],
    rules: [],
    enums: [],
    typeAliases: [],
    lets: [],
    imports: [],
    packages: []
  };
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      transformMember(member, [...prefix, name], pkg);
    }
  }
  return pkg;
}
function transformMember(m, prefix, pkg) {
  const name = getName(m);
  name ? [...prefix, name].filter(Boolean).join("::") : "";
  switch (m.kind) {
    case "package":
      pkg.packages.push(transformPackage(m, prefix));
      return true;
    case "partDef":
      pkg.partDefs.push(transformPartDef(m, prefix));
      return true;
    case "partUsage":
      return true;
    case "actionDef":
      pkg.actionDefs.push(transformActionDef(m, prefix));
      return true;
    case "actionUsage":
      return true;
    case "portDef":
      pkg.portDefs.push(transformPortDef(m, prefix));
      return true;
    case "itemDef":
      pkg.itemDefs.push(transformItemDef(m, prefix));
      return true;
    case "stateDef":
      pkg.stateDefs.push(transformStateDef(m, prefix));
      return true;
    case "stateUsage":
      return true;
    case "requirementDef":
      pkg.requirementDefs.push(transformRequirementDef(m, prefix));
      return true;
    case "requirementUsage":
      return true;
    case "constraintDef":
      pkg.constraintDefs.push(transformConstraintDef(m, prefix));
      return true;
    case "constraintUsage":
      return true;
    case "connectionDef":
      pkg.connectionDefs.push(transformConnectionDef(m, prefix));
      return true;
    case "connectionUsage":
      pkg.connections.push(transformConnectionUsage(m, prefix));
      return true;
    case "allocationDef":
      pkg.allocationDefs.push(transformAllocationDef(m, prefix));
      return true;
    case "allocationUsage":
      pkg.allocations.push(transformAllocationUsage(m, prefix));
      return true;
    case "useCaseDef":
      pkg.useCaseDefs.push(transformUseCaseDef(m, prefix));
      return true;
    case "useCaseUsage":
      return true;
    case "interfaceDef":
      pkg.interfaceDefs.push(transformInterfaceDef(m, prefix));
      return true;
    case "interfaceUsage":
      return true;
    case "verificationDef":
      pkg.verificationDefs.push(transformVerificationDef(m, prefix));
      return true;
    case "verificationUsage":
      return true;
    case "transitionUsage":
      return true;
    case "flowConnectionUsage":
      return true;
    case "performActionUsage":
      return true;
    case "exhibitStateUsage":
      return true;
    case "satisfyRequirementUsage":
      pkg.satisfies.push(transformSatisfyUsage(m, prefix));
      return true;
    case "assertConstraintUsage":
      pkg.asserts.push(transformAssertUsage(m, prefix));
      return true;
    case "import":
      return true;
    case "dependency":
      return true;
    default:
      return false;
  }
}
function getName(m) {
  if ("identification" in m && m.identification) {
    return m.identification.name ?? m.identification.shortName ?? null;
  }
  return null;
}
function transformPartDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const parts = [];
  const actions = [];
  const ports = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "partUsage") {
        parts.push(transformPartUsage(member, [...prefix, name]));
      } else if (member.kind === "portUsage") {
        ports.push(transformPortUsage(member, [...prefix, name]));
      } else if (member.kind === "actionUsage") {
        actions.push(transformActionUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "partDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    parts,
    actions,
    ports
  };
}
function transformPartUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "partUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    typeRef: m.typeRef ? qnToString(m.typeRef) : "?",
    multiplicity: parseMultiplicity(m.multiplicityText)
  };
}
function transformActionDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const actions = [];
  const flows = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "actionUsage") {
        actions.push(transformActionUsage(member, [...prefix, name]));
      } else if (member.kind === "flowConnectionUsage") {
        flows.push(transformFlowStatement(member, [...prefix, name]));
      }
    }
  }
  const params = m.parameters?.map((p) => ({
    dir: p.direction,
    name: p.identification.name ?? p.identification.shortName ?? "(anonymous)",
    typeRef: p.typeRef ? qnToString(p.typeRef) : "?",
    multiplicity: parseMultiplicity(p.multiplicityText),
    source: convertSourceRange(p.range)
  })) ?? [];
  return {
    id: generateId(),
    elementType: "actionDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    params,
    actions,
    flows
  };
}
function transformActionUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "actionUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    typeRef: m.typeRef ? qnToString(m.typeRef) : "?",
    multiplicity: parseMultiplicity(m.multiplicityText)
  };
}
function transformFlowStatement(m, prefix) {
  const name = m.identification?.name ?? m.identification?.shortName ?? "flow";
  return {
    id: generateId(),
    elementType: "flow",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    itemTypeRef: m.itemTypeRef ? qnToString(m.itemTypeRef) : "?",
    fromRef: qnToString(m.from.ref),
    toRef: qnToString(m.to.ref)
  };
}
function transformPortDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "portDef",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range)
  };
}
function transformPortUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const dir = "inout";
  return {
    id: generateId(),
    elementType: "port",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    dir,
    typeRef: m.typeRef ? qnToString(m.typeRef) : "?",
    multiplicity: parseMultiplicity(m.multiplicityText)
  };
}
function transformItemDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "itemDef",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range)
  };
}
function transformStateDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const states = [];
  const transitions = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "stateUsage") {
        states.push(transformStateUsage(member, [...prefix, name]));
      } else if (member.kind === "transitionUsage") {
        transitions.push(transformTransitionUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "stateDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    states,
    transitions
  };
}
function transformStateUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const states = [];
  const transitions = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "stateUsage") {
        states.push(transformStateUsage(member, [...prefix, name]));
      } else if (member.kind === "transitionUsage") {
        transitions.push(transformTransitionUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "stateUsage",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    typeRef: m.typeRef ? qnToString(m.typeRef) : "?",
    isParallel: m.isParallel,
    states: states.length > 0 ? states : void 0,
    transitions: transitions.length > 0 ? transitions : void 0
  };
}
function transformTransitionUsage(m, prefix) {
  const name = m.identification?.name ?? m.identification?.shortName ?? "transition";
  return {
    id: generateId(),
    elementType: "transition",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    sourceState: m.source ? createElementRef(m.source) : { ref: "?" },
    targetState: m.target ? createElementRef(m.target) : { ref: "?" },
    trigger: m.trigger ? createElementRef(m.trigger) : void 0,
    guard: m.guard,
    effect: m.effect ? createElementRef(m.effect) : void 0
  };
}
function transformRequirementDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const subrequirements = [];
  const constraints = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "requirementUsage") {
        subrequirements.push(transformRequirementUsage(member, [...prefix, name]));
      } else if (member.kind === "constraintUsage") {
        constraints.push(transformConstraintUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "requirementDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    text: m.text,
    subrequirements: subrequirements.length > 0 ? subrequirements : void 0,
    constraints: constraints.length > 0 ? constraints : void 0
  };
}
function transformRequirementUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "requirementUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    typeRef: m.typeRef ? qnToString(m.typeRef) : "?",
    text: m.text
  };
}
function transformConstraintDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "constraintDef",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    expression: m.expressionText
  };
}
function transformConstraintUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "constraintUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    typeRef: m.typeRef ? qnToString(m.typeRef) : "?",
    expression: m.expressionText
  };
}
function transformConnectionDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "connectionDef",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0
  };
}
function transformConnectionUsage(m, prefix) {
  const name = m.identification?.name ?? m.identification?.shortName ?? "connection";
  const ends = m.ends.map((end) => ({
    ref: createElementRef(end.ref),
    port: end.port ? createElementRef(end.port) : void 0
  }));
  return {
    id: generateId(),
    elementType: "connectionUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    ends
  };
}
function transformAllocationDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "allocationDef",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0
  };
}
function transformAllocationUsage(m, prefix) {
  const name = m.identification?.name ?? m.identification?.shortName ?? "allocation";
  return {
    id: generateId(),
    elementType: "allocationUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    from: createElementRef(m.from),
    to: createElementRef(m.to)
  };
}
function transformUseCaseDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const actors = [];
  const includes = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "actorUsage") {
        actors.push(transformActorUsage(member, [...prefix, name]));
      } else if (member.kind === "includeUseCaseUsage") {
        includes.push(transformIncludeUseCaseUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "useCaseDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    objective: m.objective,
    actors: actors.length > 0 ? actors : void 0,
    includes: includes.length > 0 ? includes : void 0
  };
}
function transformActorUsage(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  return {
    id: generateId(),
    elementType: "actorUsage",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    typeRef: m.typeRef ? qnToString(m.typeRef) : void 0
  };
}
function transformIncludeUseCaseUsage(m, prefix) {
  const name = qnToString(m.useCaseRef);
  return {
    id: generateId(),
    elementType: "includeUseCase",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    useCaseRef: createElementRef(m.useCaseRef)
  };
}
function transformInterfaceDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const ports = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "portUsage") {
        ports.push(transformPortUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "interfaceDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    ports: ports.length > 0 ? ports : void 0
  };
}
function transformVerificationDef(m, prefix) {
  const name = m.identification.name ?? m.identification.shortName ?? "(anonymous)";
  const qualifiedName = [...prefix, name].filter(Boolean).join("::");
  const actions = [];
  if (m.body.kind === "braced") {
    for (const member of m.body.members) {
      if (member.kind === "actionUsage") {
        actions.push(transformActionUsage(member, [...prefix, name]));
      }
    }
  }
  return {
    id: generateId(),
    elementType: "verificationDef",
    name,
    qualifiedName,
    source: convertSourceRange(m.range),
    supertype: m.supers.length > 0 ? qnToString(m.supers[0]) : void 0,
    subject: m.subject ? createElementRef(m.subject) : void 0,
    actions: actions.length > 0 ? actions : void 0
  };
}
function transformSatisfyUsage(m, prefix) {
  const name = qnToString(m.requirement);
  return {
    id: generateId(),
    elementType: "satisfy",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    requirement: createElementRef(m.requirement),
    by: m.by ? createElementRef(m.by) : void 0
  };
}
function transformAssertUsage(m, prefix) {
  const name = qnToString(m.constraint);
  return {
    id: generateId(),
    elementType: "assert",
    name,
    qualifiedName: [...prefix, name].filter(Boolean).join("::"),
    source: convertSourceRange(m.range),
    constraint: createElementRef(m.constraint)
  };
}
function flattenModel(model) {
  for (const pkg of model.packages) {
    flattenPackage(pkg, model);
  }
}
function flattenPackage(pkg, model) {
  model.allPartDefs.push(...pkg.partDefs);
  model.allActionDefs.push(...pkg.actionDefs);
  model.allPortDefs.push(...pkg.portDefs);
  model.allItemDefs.push(...pkg.itemDefs);
  model.allStateDefs.push(...pkg.stateDefs);
  model.allRequirementDefs.push(...pkg.requirementDefs);
  model.allConstraintDefs.push(...pkg.constraintDefs);
  model.allConnectionDefs.push(...pkg.connectionDefs);
  model.allConnections.push(...pkg.connections);
  model.allAllocationDefs.push(...pkg.allocationDefs);
  model.allAllocations.push(...pkg.allocations);
  model.allUseCaseDefs.push(...pkg.useCaseDefs);
  model.allInterfaceDefs.push(...pkg.interfaceDefs);
  model.allVerificationDefs.push(...pkg.verificationDefs);
  model.allSatisfies.push(...pkg.satisfies);
  model.allAsserts.push(...pkg.asserts);
  model.allViews.push(...pkg.views);
  model.allRules.push(...pkg.rules);
  model.allEnums.push(...pkg.enums);
  model.allTypeAliases.push(...pkg.typeAliases);
  model.allLets.push(...pkg.lets);
  model.allImports.push(...pkg.imports);
  model.allRelationships.push(...pkg.relationships);
  for (const partDef of pkg.partDefs) {
    model.allPartUsages.push(...partDef.parts);
    if (partDef.ports) model.allPorts.push(...partDef.ports);
    if (partDef.actions) model.allActionUsages.push(...partDef.actions);
  }
  for (const actionDef of pkg.actionDefs) {
    if (actionDef.actions) model.allActionUsages.push(...actionDef.actions);
    if (actionDef.flows) model.allFlows.push(...actionDef.flows);
  }
  for (const stateDef of pkg.stateDefs) {
    if (stateDef.states) model.allStateUsages.push(...stateDef.states);
    if (stateDef.transitions) model.allTransitions.push(...stateDef.transitions);
  }
  for (const reqDef of pkg.requirementDefs) {
    if (reqDef.subrequirements) model.allRequirementUsages.push(...reqDef.subrequirements);
    if (reqDef.constraints) model.allConstraintUsages.push(...reqDef.constraints);
  }
  for (const ucDef of pkg.useCaseDefs) {
    if (ucDef.actors) model.allActorUsages.push(...ucDef.actors);
    if (ucDef.includes) model.allIncludeUseCases.push(...ucDef.includes);
  }
  for (const ifaceDef of pkg.interfaceDefs) {
    if (ifaceDef.ports) model.allPorts.push(...ifaceDef.ports);
  }
  for (const verDef of pkg.verificationDefs) {
    if (verDef.actions) model.allActionUsages.push(...verDef.actions);
  }
  for (const nestedPkg of pkg.packages) {
    flattenPackage(nestedPkg, model);
  }
}

// src/sysml2-generator.ts
var DEFAULT_OPTIONS = {
  indent: "  ",
  includeSourceComments: false,
  includeMetadata: true
};
function generateSysMLv2(model, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const ctx = {
    options: opts,
    indentLevel: 0,
    output: []
  };
  for (const pkg of model.packages) {
    generatePackage(pkg, ctx);
    ctx.output.push("");
  }
  return ctx.output.join("\n").trim();
}
function indent2(ctx) {
  return ctx.options.indent.repeat(ctx.indentLevel);
}
function emit(ctx, line) {
  ctx.output.push(indent2(ctx) + line);
}
function emitBlank(ctx) {
  ctx.output.push("");
}
function formatMultiplicity(mult) {
  if (!mult) return "";
  if (mult.lower === mult.upper) {
    return `[${mult.lower}]`;
  }
  return `[${mult.lower}..${mult.upper === "*" ? "*" : mult.upper}]`;
}
function formatRef(ref) {
  if (typeof ref === "string") return ref;
  return ref.ref === "?" ? "?" : ref.ref;
}
function generatePackage(pkg, ctx) {
  if (pkg.name === "(root)") {
    generatePackageContents(pkg, ctx);
  } else {
    emit(ctx, `package ${pkg.name} {`);
    ctx.indentLevel++;
    generatePackageContents(pkg, ctx);
    ctx.indentLevel--;
    emit(ctx, "}");
  }
}
function generatePackageContents(pkg, ctx) {
  for (const imp of pkg.imports) {
    emit(ctx, `import ${imp.path};`);
  }
  if (pkg.imports.length > 0) emitBlank(ctx);
  for (const partDef of pkg.partDefs) {
    generatePartDef(partDef, ctx);
    emitBlank(ctx);
  }
  for (const actionDef of pkg.actionDefs) {
    generateActionDef(actionDef, ctx);
    emitBlank(ctx);
  }
  for (const stateDef of pkg.stateDefs) {
    generateStateDef(stateDef, ctx);
    emitBlank(ctx);
  }
  for (const reqDef of pkg.requirementDefs) {
    generateRequirementDef(reqDef, ctx);
    emitBlank(ctx);
  }
  for (const constDef of pkg.constraintDefs) {
    generateConstraintDef(constDef, ctx);
    emitBlank(ctx);
  }
  for (const connDef of pkg.connectionDefs) {
    generateConnectionDef(connDef, ctx);
    emitBlank(ctx);
  }
  for (const conn of pkg.connections) {
    generateConnectionUsage(conn, ctx);
  }
  for (const allocDef of pkg.allocationDefs) {
    generateAllocationDef(allocDef, ctx);
    emitBlank(ctx);
  }
  for (const alloc of pkg.allocations) {
    generateAllocationUsage(alloc, ctx);
  }
  for (const ucDef of pkg.useCaseDefs) {
    generateUseCaseDef(ucDef, ctx);
    emitBlank(ctx);
  }
  for (const ifaceDef of pkg.interfaceDefs) {
    generateInterfaceDef(ifaceDef, ctx);
    emitBlank(ctx);
  }
  for (const verDef of pkg.verificationDefs) {
    generateVerificationDef(verDef, ctx);
    emitBlank(ctx);
  }
  for (const sat of pkg.satisfies) {
    generateSatisfyUsage(sat, ctx);
  }
  for (const asrt of pkg.asserts) {
    generateAssertUsage(asrt, ctx);
  }
  for (const nestedPkg of pkg.packages) {
    generatePackage(nestedPkg, ctx);
    emitBlank(ctx);
  }
}
function generatePartDef(partDef, ctx) {
  const supertype = partDef.supertype ? ` :> ${partDef.supertype}` : "";
  const hasBody = partDef.parts.length > 0 || partDef.ports && partDef.ports.length > 0 || partDef.actions && partDef.actions.length > 0 || partDef.props && partDef.props.length > 0;
  if (!hasBody) {
    emit(ctx, `part def ${partDef.name}${supertype};`);
    return;
  }
  emit(ctx, `part def ${partDef.name}${supertype} {`);
  ctx.indentLevel++;
  if (partDef.ports) {
    for (const port of partDef.ports) {
      generatePortDef(port, ctx);
    }
  }
  if (partDef.props) {
    for (const prop of partDef.props) {
      emit(ctx, `attribute ${prop.name} : ${formatRef(prop.typeRef)};`);
    }
  }
  for (const part of partDef.parts) {
    generatePartUsage(part, ctx);
  }
  if (partDef.actions) {
    for (const action of partDef.actions) {
      generateActionUsage(action, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generatePartUsage(part, ctx) {
  const typeRef = formatRef(part.typeRef);
  const mult = formatMultiplicity(part.multiplicity);
  emit(ctx, `part ${part.name} : ${typeRef}${mult};`);
}
function generatePortDef(port, ctx) {
  const dir = port.dir;
  const typeRef = formatRef(port.typeRef);
  const mult = formatMultiplicity(port.multiplicity);
  emit(ctx, `${dir} port ${port.name} : ${typeRef}${mult};`);
}
function generateActionDef(actionDef, ctx) {
  const supertype = actionDef.supertype ? ` :> ${actionDef.supertype}` : "";
  const params = actionDef.params && actionDef.params.length > 0 ? `(${actionDef.params.map(formatActionParam).join(", ")})` : "";
  const hasBody = actionDef.actions && actionDef.actions.length > 0 || actionDef.flows && actionDef.flows.length > 0 || actionDef.performs && actionDef.performs.length > 0;
  if (!hasBody) {
    emit(ctx, `action def ${actionDef.name}${params}${supertype};`);
    return;
  }
  emit(ctx, `action def ${actionDef.name}${params}${supertype} {`);
  ctx.indentLevel++;
  if (actionDef.actions) {
    for (const action of actionDef.actions) {
      generateActionUsage(action, ctx);
    }
  }
  if (actionDef.flows) {
    for (const flow of actionDef.flows) {
      generateFlowStatement(flow, ctx);
    }
  }
  if (actionDef.performs) {
    for (const perf of actionDef.performs) {
      emit(ctx, `perform action ${formatRef(perf.ref)};`);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function formatActionParam(param) {
  const typeRef = formatRef(param.typeRef);
  const mult = formatMultiplicity(param.multiplicity);
  return `${param.dir} ${param.name} : ${typeRef}${mult}`;
}
function generateActionUsage(action, ctx) {
  const typeRef = formatRef(action.typeRef);
  const mult = formatMultiplicity(action.multiplicity);
  emit(ctx, `action ${action.name} : ${typeRef}${mult};`);
}
function generateFlowStatement(flow, ctx) {
  const itemType = formatRef(flow.itemTypeRef);
  const from = formatRef(flow.fromRef);
  const to = formatRef(flow.toRef);
  emit(ctx, `flow of ${itemType} from ${from} to ${to};`);
}
function generateStateDef(stateDef, ctx) {
  const supertype = stateDef.supertype ? ` :> ${stateDef.supertype}` : "";
  const hasBody = stateDef.states && stateDef.states.length > 0 || stateDef.transitions && stateDef.transitions.length > 0;
  if (!hasBody) {
    emit(ctx, `state def ${stateDef.name}${supertype};`);
    return;
  }
  emit(ctx, `state def ${stateDef.name}${supertype} {`);
  ctx.indentLevel++;
  if (stateDef.entryAction) {
    emit(ctx, `entry action ${stateDef.entryAction};`);
  }
  if (stateDef.exitAction) {
    emit(ctx, `exit action ${stateDef.exitAction};`);
  }
  if (stateDef.doAction) {
    emit(ctx, `do action ${stateDef.doAction};`);
  }
  if (stateDef.states) {
    for (const state of stateDef.states) {
      generateStateUsage(state, ctx);
    }
  }
  if (stateDef.transitions) {
    for (const trans of stateDef.transitions) {
      generateTransitionUsage(trans, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateStateUsage(state, ctx) {
  const typeRef = state.typeRef !== "?" ? ` : ${formatRef(state.typeRef)}` : "";
  const parallel = state.isParallel ? " parallel" : "";
  const hasBody = state.states && state.states.length > 0 || state.transitions && state.transitions.length > 0;
  if (!hasBody) {
    emit(ctx, `state ${state.name}${typeRef}${parallel};`);
    return;
  }
  emit(ctx, `state ${state.name}${typeRef}${parallel} {`);
  ctx.indentLevel++;
  if (state.states) {
    for (const s of state.states) {
      generateStateUsage(s, ctx);
    }
  }
  if (state.transitions) {
    for (const t of state.transitions) {
      generateTransitionUsage(t, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateTransitionUsage(trans, ctx) {
  const source = formatRef(trans.sourceState);
  const target = formatRef(trans.targetState);
  let parts = [`first ${source} then ${target}`];
  if (trans.trigger) {
    parts.push(`accept ${formatRef(trans.trigger)}`);
  }
  if (trans.guard) {
    parts.push(`if ${trans.guard}`);
  }
  if (trans.effect) {
    parts.push(`do ${formatRef(trans.effect)}`);
  }
  emit(ctx, `transition ${parts.join(" ")};`);
}
function generateRequirementDef(reqDef, ctx) {
  const supertype = reqDef.supertype ? ` :> ${reqDef.supertype}` : "";
  const hasBody = reqDef.text || reqDef.subrequirements && reqDef.subrequirements.length > 0 || reqDef.constraints && reqDef.constraints.length > 0;
  if (!hasBody) {
    emit(ctx, `requirement def ${reqDef.name}${supertype};`);
    return;
  }
  emit(ctx, `requirement def ${reqDef.name}${supertype} {`);
  ctx.indentLevel++;
  if (reqDef.subject) {
    emit(ctx, `subject : ${formatRef(reqDef.subject)};`);
  }
  if (reqDef.text) {
    emit(ctx, `doc /* ${reqDef.text} */`);
  }
  if (reqDef.subrequirements) {
    for (const req of reqDef.subrequirements) {
      generateRequirementUsage(req, ctx);
    }
  }
  if (reqDef.constraints) {
    for (const con of reqDef.constraints) {
      generateConstraintUsage(con, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateRequirementUsage(req, ctx) {
  const typeRef = formatRef(req.typeRef);
  if (req.text) {
    emit(ctx, `requirement ${req.name} : ${typeRef} {`);
    ctx.indentLevel++;
    emit(ctx, `doc /* ${req.text} */`);
    ctx.indentLevel--;
    emit(ctx, "}");
  } else {
    emit(ctx, `requirement ${req.name} : ${typeRef};`);
  }
}
function generateConstraintDef(constDef, ctx) {
  const supertype = constDef.supertype ? ` :> ${constDef.supertype}` : "";
  const params = constDef.params && constDef.params.length > 0 ? `(${constDef.params.map(formatActionParam).join(", ")})` : "";
  if (!constDef.expression) {
    emit(ctx, `constraint def ${constDef.name}${params}${supertype};`);
    return;
  }
  emit(ctx, `constraint def ${constDef.name}${params}${supertype} {`);
  ctx.indentLevel++;
  emit(ctx, constDef.expression);
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateConstraintUsage(con, ctx) {
  const typeRef = formatRef(con.typeRef);
  if (con.expression) {
    emit(ctx, `constraint ${con.name} : ${typeRef} {`);
    ctx.indentLevel++;
    emit(ctx, con.expression);
    ctx.indentLevel--;
    emit(ctx, "}");
  } else {
    emit(ctx, `constraint ${con.name} : ${typeRef};`);
  }
}
function generateConnectionDef(connDef, ctx) {
  const supertype = connDef.supertype ? ` :> ${connDef.supertype}` : "";
  emit(ctx, `connection def ${connDef.name}${supertype};`);
}
function generateConnectionUsage(conn, ctx) {
  if (conn.ends.length === 2) {
    const end1 = formatConnectionEnd(conn.ends[0]);
    const end2 = formatConnectionEnd(conn.ends[1]);
    emit(ctx, `connect ${end1} to ${end2};`);
  } else {
    const ends = conn.ends.map(formatConnectionEnd).join(", ");
    emit(ctx, `connect (${ends});`);
  }
}
function formatConnectionEnd(end) {
  const ref = formatRef(end.ref);
  if (end.port) {
    return `${ref}.${formatRef(end.port)}`;
  }
  return ref;
}
function generateAllocationDef(allocDef, ctx) {
  const supertype = allocDef.supertype ? ` :> ${allocDef.supertype}` : "";
  emit(ctx, `allocation def ${allocDef.name}${supertype};`);
}
function generateAllocationUsage(alloc, ctx) {
  const from = formatRef(alloc.from);
  const to = formatRef(alloc.to);
  emit(ctx, `allocate ${from} to ${to};`);
}
function generateUseCaseDef(ucDef, ctx) {
  const supertype = ucDef.supertype ? ` :> ${ucDef.supertype}` : "";
  const hasBody = ucDef.subject || ucDef.objective || ucDef.actors && ucDef.actors.length > 0 || ucDef.includes && ucDef.includes.length > 0;
  if (!hasBody) {
    emit(ctx, `use case def ${ucDef.name}${supertype};`);
    return;
  }
  emit(ctx, `use case def ${ucDef.name}${supertype} {`);
  ctx.indentLevel++;
  if (ucDef.subject) {
    emit(ctx, `subject : ${formatRef(ucDef.subject)};`);
  }
  if (ucDef.objective) {
    emit(ctx, `objective {`);
    ctx.indentLevel++;
    emit(ctx, `doc /* ${ucDef.objective} */`);
    ctx.indentLevel--;
    emit(ctx, "}");
  }
  if (ucDef.actors) {
    for (const actor of ucDef.actors) {
      generateActorUsage(actor, ctx);
    }
  }
  if (ucDef.includes) {
    for (const inc of ucDef.includes) {
      generateIncludeUseCaseUsage(inc, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateActorUsage(actor, ctx) {
  const typeRef = actor.typeRef ? ` : ${actor.typeRef}` : "";
  emit(ctx, `actor ${actor.name}${typeRef};`);
}
function generateIncludeUseCaseUsage(inc, ctx) {
  const ref = formatRef(inc.useCaseRef);
  emit(ctx, `include ${ref};`);
}
function generateInterfaceDef(ifaceDef, ctx) {
  const supertype = ifaceDef.supertype ? ` :> ${ifaceDef.supertype}` : "";
  const hasBody = ifaceDef.ports && ifaceDef.ports.length > 0 || ifaceDef.ends && ifaceDef.ends.length > 0;
  if (!hasBody) {
    emit(ctx, `interface def ${ifaceDef.name}${supertype};`);
    return;
  }
  emit(ctx, `interface def ${ifaceDef.name}${supertype} {`);
  ctx.indentLevel++;
  if (ifaceDef.ports) {
    for (const port of ifaceDef.ports) {
      generatePortDef(port, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateVerificationDef(verDef, ctx) {
  const supertype = verDef.supertype ? ` :> ${verDef.supertype}` : "";
  const hasBody = verDef.subject || verDef.requirements && verDef.requirements.length > 0 || verDef.actions && verDef.actions.length > 0;
  if (!hasBody) {
    emit(ctx, `verification def ${verDef.name}${supertype};`);
    return;
  }
  emit(ctx, `verification def ${verDef.name}${supertype} {`);
  ctx.indentLevel++;
  if (verDef.subject) {
    emit(ctx, `subject : ${formatRef(verDef.subject)};`);
  }
  if (verDef.requirements) {
    for (const req of verDef.requirements) {
      emit(ctx, `verify requirement ${formatRef(req)};`);
    }
  }
  if (verDef.actions) {
    for (const action of verDef.actions) {
      generateActionUsage(action, ctx);
    }
  }
  ctx.indentLevel--;
  emit(ctx, "}");
}
function generateSatisfyUsage(sat, ctx) {
  const req = formatRef(sat.requirement);
  if (sat.by) {
    emit(ctx, `satisfy ${req} by ${formatRef(sat.by)};`);
  } else {
    emit(ctx, `satisfy ${req};`);
  }
}
function generateAssertUsage(asrt, ctx) {
  const con = formatRef(asrt.constraint);
  emit(ctx, `assert ${con};`);
}

// src/stdlib.ts
var nextStdlibId = 1;
function stdId() {
  return `stdlib_${nextStdlibId++}`;
}
var STDLIB_SOURCE = { uri: "std:stdlib", startLine: 1, startColumn: 1, endLine: 1, endColumn: 1 };
function makeQuantityDef(name, pkg) {
  return {
    id: stdId(),
    elementType: "partDef",
    name,
    qualifiedName: `${pkg}.${name}`,
    parts: [],
    meta: { tags: ["stdlib", "isq"], kind: "quantity" },
    source: STDLIB_SOURCE
  };
}
function makeUnitDef(name, symbol, quantityRef, pkg) {
  return {
    id: stdId(),
    elementType: "itemDef",
    name,
    qualifiedName: `${pkg}.${name}`,
    meta: {
      tags: ["stdlib", "si"],
      kind: "unit",
      props: { symbol, quantityRef }
    },
    source: STDLIB_SOURCE
  };
}
function makeScalarDef(name, pkg) {
  return {
    id: stdId(),
    elementType: "partDef",
    name,
    qualifiedName: `${pkg}.${name}`,
    parts: [],
    meta: { tags: ["stdlib", "scalar"], kind: "scalar" },
    source: STDLIB_SOURCE
  };
}
function emptyPkg(name) {
  return {
    id: stdId(),
    elementType: "package",
    name,
    qualifiedName: name,
    partDefs: [],
    partUsages: [],
    actionDefs: [],
    portDefs: [],
    itemDefs: [],
    stateDefs: [],
    requirementDefs: [],
    constraintDefs: [],
    connectionDefs: [],
    connections: [],
    allocationDefs: [],
    allocations: [],
    useCaseDefs: [],
    interfaceDefs: [],
    verificationDefs: [],
    analysisDefs: [],
    calcDefs: [],
    viewDefs: [],
    viewpointDefs: [],
    renderingDefs: [],
    metadataDefs: [],
    occurrenceDefs: [],
    satisfies: [],
    asserts: [],
    relationships: [],
    views: [],
    rules: [],
    enums: [],
    typeAliases: [],
    lets: [],
    imports: [],
    packages: [],
    source: STDLIB_SOURCE,
    meta: { tags: ["stdlib"] }
  };
}
function buildISQPackage() {
  const pkg = "ISQ";
  const quantities = [
    // Base quantities (SI base)
    makeQuantityDef("Mass", pkg),
    makeQuantityDef("Length", pkg),
    makeQuantityDef("Time", pkg),
    makeQuantityDef("Temperature", pkg),
    makeQuantityDef("ElectricCurrent", pkg),
    makeQuantityDef("AmountOfSubstance", pkg),
    makeQuantityDef("LuminousIntensity", pkg),
    // Derived quantities
    makeQuantityDef("Area", pkg),
    makeQuantityDef("Volume", pkg),
    makeQuantityDef("Velocity", pkg),
    makeQuantityDef("Acceleration", pkg),
    makeQuantityDef("Force", pkg),
    makeQuantityDef("Pressure", pkg),
    makeQuantityDef("Energy", pkg),
    makeQuantityDef("Power", pkg),
    makeQuantityDef("Frequency", pkg),
    makeQuantityDef("Angle", pkg),
    makeQuantityDef("SolidAngle", pkg),
    makeQuantityDef("ElectricCharge", pkg),
    makeQuantityDef("Voltage", pkg),
    makeQuantityDef("Resistance", pkg),
    makeQuantityDef("Capacitance", pkg),
    makeQuantityDef("MagneticFlux", pkg),
    makeQuantityDef("Torque", pkg),
    makeQuantityDef("Density", pkg)
  ];
  const p = emptyPkg(pkg);
  p.partDefs = quantities;
  return p;
}
function buildSIPackage() {
  const pkg = "SI";
  const units = [
    // Base units
    makeUnitDef("kilogram", "kg", "ISQ.Mass", pkg),
    makeUnitDef("meter", "m", "ISQ.Length", pkg),
    makeUnitDef("second", "s", "ISQ.Time", pkg),
    makeUnitDef("kelvin", "K", "ISQ.Temperature", pkg),
    makeUnitDef("ampere", "A", "ISQ.ElectricCurrent", pkg),
    makeUnitDef("mole", "mol", "ISQ.AmountOfSubstance", pkg),
    makeUnitDef("candela", "cd", "ISQ.LuminousIntensity", pkg),
    // Derived units
    makeUnitDef("newton", "N", "ISQ.Force", pkg),
    makeUnitDef("pascal", "Pa", "ISQ.Pressure", pkg),
    makeUnitDef("joule", "J", "ISQ.Energy", pkg),
    makeUnitDef("watt", "W", "ISQ.Power", pkg),
    makeUnitDef("hertz", "Hz", "ISQ.Frequency", pkg),
    makeUnitDef("radian", "rad", "ISQ.Angle", pkg),
    makeUnitDef("steradian", "sr", "ISQ.SolidAngle", pkg),
    makeUnitDef("coulomb", "C", "ISQ.ElectricCharge", pkg),
    makeUnitDef("volt", "V", "ISQ.Voltage", pkg),
    makeUnitDef("ohm", "\u03A9", "ISQ.Resistance", pkg),
    makeUnitDef("farad", "F", "ISQ.Capacitance", pkg),
    makeUnitDef("weber", "Wb", "ISQ.MagneticFlux", pkg),
    // Common prefixed units
    makeUnitDef("gram", "g", "ISQ.Mass", pkg),
    makeUnitDef("milligram", "mg", "ISQ.Mass", pkg),
    makeUnitDef("kilometer", "km", "ISQ.Length", pkg),
    makeUnitDef("centimeter", "cm", "ISQ.Length", pkg),
    makeUnitDef("millimeter", "mm", "ISQ.Length", pkg),
    makeUnitDef("millisecond", "ms", "ISQ.Time", pkg),
    makeUnitDef("microsecond", "\xB5s", "ISQ.Time", pkg)
  ];
  const p = emptyPkg(pkg);
  p.itemDefs = units;
  return p;
}
function buildScalarValuesPackage() {
  const pkg = "ScalarValues";
  const scalars = [
    makeScalarDef("Real", pkg),
    makeScalarDef("Integer", pkg),
    makeScalarDef("Natural", pkg),
    makeScalarDef("Boolean", pkg),
    makeScalarDef("String", pkg)
  ];
  const p = emptyPkg(pkg);
  p.partDefs = scalars;
  return p;
}
var cachedStdlib = null;
function getStandardLibrary() {
  if (cachedStdlib) return cachedStdlib;
  nextStdlibId = 1;
  const packages = [
    buildISQPackage(),
    buildSIPackage(),
    buildScalarValuesPackage()
  ];
  const symbolNames = /* @__PURE__ */ new Set();
  const qualifiedNames = /* @__PURE__ */ new Set();
  for (const pkg of packages) {
    symbolNames.add(pkg.name);
    qualifiedNames.add(pkg.qualifiedName);
    for (const def of pkg.partDefs) {
      symbolNames.add(def.name);
      qualifiedNames.add(def.qualifiedName);
    }
    for (const def of pkg.itemDefs) {
      symbolNames.add(def.name);
      qualifiedNames.add(def.qualifiedName);
    }
  }
  cachedStdlib = { packages, symbolNames, qualifiedNames };
  return cachedStdlib;
}
function detectStdlibCollisions(model, stdlib) {
  const lib = stdlib ?? getStandardLibrary();
  const collisions = [];
  for (const element of model.allElements) {
    if (element.meta?.tags?.includes("stdlib")) continue;
    if (lib.symbolNames.has(element.name)) {
      const libraryQn = [...lib.qualifiedNames].find((qn) => {
        const parts = qn.split(".");
        return parts[parts.length - 1] === element.name;
      }) ?? element.name;
      collisions.push({
        userElement: element,
        libraryQualifiedName: libraryQn,
        diagnostic: {
          severity: "warning",
          message: `"${element.name}" shadows standard library symbol "${libraryQn}". Use qualified name to disambiguate.`,
          line: element.source?.startLine ?? 1,
          column: element.source?.startColumn ?? 1,
          endLine: element.source?.endLine,
          endColumn: element.source?.endColumn
        }
      });
    }
  }
  return collisions;
}
function mergeWithStdlib(model, stdlib) {
  const lib = stdlib ?? getStandardLibrary();
  const libPartDefs = [];
  const libItemDefs = [];
  for (const pkg of lib.packages) {
    libPartDefs.push(...pkg.partDefs);
    libItemDefs.push(...pkg.itemDefs);
  }
  const libElements = [
    ...lib.packages,
    ...libPartDefs,
    ...libItemDefs
  ];
  return {
    ...model,
    packages: [...lib.packages, ...model.packages],
    allPartDefs: [...libPartDefs, ...model.allPartDefs],
    allItemDefs: [...libItemDefs, ...model.allItemDefs],
    allElements: [...libElements, ...model.allElements]
  };
}
function resetStdlibCache() {
  cachedStdlib = null;
}

// src/solver.ts
function tokenizeExpr(input) {
  const tokens = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (/[0-9]/.test(ch) || ch === "." && i + 1 < input.length && /[0-9]/.test(input[i + 1])) {
      let num = "";
      while (i < input.length && /[0-9.]/.test(input[i])) {
        num += input[i++];
      }
      tokens.push({ type: "number", value: num });
      continue;
    }
    if (/[a-zA-Z_]/.test(ch)) {
      let ident = "";
      while (i < input.length && /[a-zA-Z0-9_.]/.test(input[i])) {
        ident += input[i++];
      }
      tokens.push({ type: "identifier", value: ident });
      continue;
    }
    if (ch === "=" && i + 1 < input.length && input[i + 1] === "=") {
      tokens.push({ type: "operator", value: "==" });
      i += 2;
      continue;
    }
    if (ch === "!" && i + 1 < input.length && input[i + 1] === "=") {
      tokens.push({ type: "operator", value: "!=" });
      i += 2;
      continue;
    }
    if (ch === "<" && i + 1 < input.length && input[i + 1] === "=") {
      tokens.push({ type: "operator", value: "<=" });
      i += 2;
      continue;
    }
    if (ch === ">" && i + 1 < input.length && input[i + 1] === "=") {
      tokens.push({ type: "operator", value: ">=" });
      i += 2;
      continue;
    }
    if ("+-*/=<>".includes(ch)) {
      tokens.push({ type: "operator", value: ch });
      i++;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen", value: "(" });
      i++;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen", value: ")" });
      i++;
      continue;
    }
    if (ch === ",") {
      tokens.push({ type: "comma", value: "," });
      i++;
      continue;
    }
    i++;
  }
  tokens.push({ type: "eof", value: "" });
  return tokens;
}
var ExprParser = class {
  tokens;
  pos = 0;
  constructor(tokens) {
    this.tokens = tokens;
  }
  parse() {
    if (this.peek().type === "eof") return null;
    const expr = this.parseComparison();
    return expr;
  }
  parseMultiple() {
    const exprs = [];
    while (this.peek().type !== "eof") {
      const expr = this.parseComparison();
      if (expr) exprs.push(expr);
      if (this.peek().type === "eof") break;
    }
    return exprs;
  }
  parseComparison() {
    let left = this.parseAddSub();
    const ops = ["==", "!=", "<", ">", "<=", ">=", "="];
    while (this.peek().type === "operator" && ops.includes(this.peek().value)) {
      const op = this.advance().value;
      const right = this.parseAddSub();
      left = { type: "binary", op, left, right };
    }
    return left;
  }
  parseAddSub() {
    let left = this.parseMulDiv();
    while (this.peek().type === "operator" && (this.peek().value === "+" || this.peek().value === "-")) {
      const op = this.advance().value;
      const right = this.parseMulDiv();
      left = { type: "binary", op, left, right };
    }
    return left;
  }
  parseMulDiv() {
    let left = this.parseUnary();
    while (this.peek().type === "operator" && (this.peek().value === "*" || this.peek().value === "/")) {
      const op = this.advance().value;
      const right = this.parseUnary();
      left = { type: "binary", op, left, right };
    }
    return left;
  }
  parseUnary() {
    if (this.peek().type === "operator" && this.peek().value === "-") {
      this.advance();
      const operand = this.parseUnary();
      return { type: "unary", op: "-", operand };
    }
    return this.parsePrimary();
  }
  parsePrimary() {
    const token = this.peek();
    if (token.type === "number") {
      this.advance();
      return { type: "number", value: parseFloat(token.value) };
    }
    if (token.type === "identifier") {
      const name = token.value;
      this.advance();
      if (this.peek().type === "lparen") {
        this.advance();
        const args = [];
        while (this.peek().type !== "rparen" && this.peek().type !== "eof") {
          args.push(this.parseComparison());
          if (this.peek().type === "comma") this.advance();
        }
        if (this.peek().type === "rparen") this.advance();
        return { type: "call", name, args };
      }
      return { type: "identifier", name };
    }
    if (token.type === "lparen") {
      this.advance();
      const expr = this.parseComparison();
      if (this.peek().type === "rparen") this.advance();
      return expr;
    }
    this.advance();
    return { type: "number", value: 0 };
  }
  peek() {
    return this.tokens[this.pos] ?? { type: "eof", value: "" };
  }
  advance() {
    const token = this.tokens[this.pos];
    if (this.pos < this.tokens.length) this.pos++;
    return token;
  }
};
function parseExpression(input) {
  const tokens = tokenizeExpr(input);
  const parser = new ExprParser(tokens);
  return parser.parse();
}
function evalExpr2(node, env) {
  switch (node.type) {
    case "number":
      return node.value;
    case "identifier": {
      const val = env.get(node.name);
      return val !== void 0 ? val : null;
    }
    case "unary": {
      const operand = evalExpr2(node.operand, env);
      if (typeof operand !== "number") return null;
      return -operand;
    }
    case "binary": {
      const left = evalExpr2(node.left, env);
      const right = evalExpr2(node.right, env);
      if (node.op === "==" || node.op === "=") {
        if (typeof left === "number" && typeof right === "number") return left === right;
        return null;
      }
      if (node.op === "!=") {
        if (typeof left === "number" && typeof right === "number") return left !== right;
        return null;
      }
      if (node.op === "<") {
        if (typeof left === "number" && typeof right === "number") return left < right;
        return null;
      }
      if (node.op === ">") {
        if (typeof left === "number" && typeof right === "number") return left > right;
        return null;
      }
      if (node.op === "<=") {
        if (typeof left === "number" && typeof right === "number") return left <= right;
        return null;
      }
      if (node.op === ">=") {
        if (typeof left === "number" && typeof right === "number") return left >= right;
        return null;
      }
      if (typeof left !== "number" || typeof right !== "number") return null;
      switch (node.op) {
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return right !== 0 ? left / right : null;
      }
      return null;
    }
    case "call": {
      const args = node.args.map((a) => evalExpr2(a, env));
      if (args.some((a) => typeof a !== "number")) return null;
      const numArgs = args;
      switch (node.name) {
        case "abs":
          return numArgs.length === 1 ? Math.abs(numArgs[0]) : null;
        case "sqrt":
          return numArgs.length === 1 ? Math.sqrt(numArgs[0]) : null;
        case "min":
          return numArgs.length >= 2 ? Math.min(...numArgs) : null;
        case "max":
          return numArgs.length >= 2 ? Math.max(...numArgs) : null;
        case "pow":
          return numArgs.length === 2 ? Math.pow(numArgs[0], numArgs[1]) : null;
        default:
          return null;
      }
    }
  }
}
function collectVariables(node) {
  const vars = /* @__PURE__ */ new Set();
  function walk(n) {
    switch (n.type) {
      case "identifier":
        vars.add(n.name);
        break;
      case "binary":
        walk(n.left);
        walk(n.right);
        break;
      case "unary":
        walk(n.operand);
        break;
      case "call":
        n.args.forEach(walk);
        break;
    }
  }
  walk(node);
  return vars;
}
function classifyVariables(node) {
  const written = /* @__PURE__ */ new Set();
  const read = /* @__PURE__ */ new Set();
  if (node.type === "binary" && node.op === "=") {
    if (node.left.type === "identifier") {
      written.add(node.left.name);
    } else {
      for (const v of collectVariables(node.left)) read.add(v);
    }
    for (const v of collectVariables(node.right)) read.add(v);
  } else {
    for (const v of collectVariables(node)) read.add(v);
  }
  return { written, read };
}
function buildDependencyGraph(constraints) {
  const edges = [];
  const constraintVars = /* @__PURE__ */ new Map();
  for (const cd of constraints) {
    if (!cd.expression) continue;
    const ast = parseExpression(cd.expression);
    if (!ast) continue;
    constraintVars.set(cd.id, classifyVariables(ast));
  }
  for (const [writerIdA, varsA] of constraintVars) {
    for (const [readerIdB, varsB] of constraintVars) {
      if (writerIdA === readerIdB) continue;
      for (const writtenVar of varsA.written) {
        if (varsB.read.has(writtenVar)) {
          edges.push({ from: writerIdA, to: readerIdB, variable: writtenVar });
        }
      }
    }
  }
  const cycles = detectCycles(constraints.map((c) => c.id), edges);
  return { edges, cycles };
}
function detectCycles(nodeIds, edges) {
  const adj = /* @__PURE__ */ new Map();
  for (const id of nodeIds) adj.set(id, /* @__PURE__ */ new Set());
  for (const edge of edges) {
    adj.get(edge.from)?.add(edge.to);
  }
  const cycles = [];
  const visited = /* @__PURE__ */ new Set();
  const inStack = /* @__PURE__ */ new Set();
  const stack = [];
  function dfs(node) {
    if (inStack.has(node)) {
      const cycleStart = stack.indexOf(node);
      if (cycleStart >= 0) {
        cycles.push(stack.slice(cycleStart));
      }
      return;
    }
    if (visited.has(node)) return;
    visited.add(node);
    inStack.add(node);
    stack.push(node);
    for (const neighbor of adj.get(node) ?? []) {
      dfs(neighbor);
    }
    stack.pop();
    inStack.delete(node);
  }
  for (const id of nodeIds) {
    if (!visited.has(id)) dfs(id);
  }
  return cycles;
}
function solveForUnknown(node, env) {
  const vars = collectVariables(node);
  const unknowns = [];
  for (const v of vars) {
    if (!env.has(v)) unknowns.push(v);
  }
  if (unknowns.length !== 1) return null;
  const target = unknowns[0];
  if (node.type === "binary" && (node.op === "=" || node.op === "==")) {
    if (node.left.type === "identifier" && node.left.name === target) {
      const val = evalExpr2(node.right, env);
      if (typeof val === "number") return /* @__PURE__ */ new Map([[target, val]]);
    }
    if (node.right.type === "identifier" && node.right.name === target) {
      const val = evalExpr2(node.left, env);
      if (typeof val === "number") return /* @__PURE__ */ new Map([[target, val]]);
    }
  }
  return null;
}
function solveConstraints(model, initialBindings) {
  const constraintDefs = model.allConstraintDefs;
  const constraintResults = [];
  const globalEnv = new Map(initialBindings ?? []);
  for (const prop of model.allProps) {
    if (prop.defaultValue !== void 0 && prop.defaultValue !== null && typeof prop.defaultValue === "number") {
      globalEnv.set(prop.name, prop.defaultValue);
    }
  }
  for (const cd of constraintDefs) {
    for (const param of cd.params ?? []) {
      if (!globalEnv.has(param.name) && param.dir === "in") ;
    }
  }
  const depGraph = buildDependencyGraph(constraintDefs);
  for (const cd of constraintDefs) {
    const result = evaluateConstraint(cd, globalEnv);
    constraintResults.push(result);
    for (const [k, v] of result.bindings) {
      globalEnv.set(k, v);
    }
  }
  let satisfied = 0;
  let violated = 0;
  let errors = 0;
  for (const r of constraintResults) {
    if (r.satisfied) {
      satisfied++;
    } else if (r.diagnostics.some((d) => d.severity === "error" && d.message.startsWith("Failed to parse"))) {
      errors++;
    } else {
      violated++;
    }
  }
  return {
    constraintResults,
    dependencies: depGraph.edges,
    cycles: depGraph.cycles,
    totalConstraints: constraintDefs.length,
    satisfied,
    violated,
    errors
  };
}
function evaluateConstraint(cd, env) {
  const diagnostics = [];
  const bindings = /* @__PURE__ */ new Map();
  if (!cd.expression) {
    diagnostics.push({
      severity: "warning",
      message: `Constraint "${cd.name}" has no expression to evaluate`,
      constraintId: cd.id,
      constraintName: cd.name,
      source: cd.source
    });
    return { constraintId: cd.id, constraintName: cd.name, satisfied: true, bindings, diagnostics };
  }
  const ast = parseExpression(cd.expression);
  if (!ast) {
    diagnostics.push({
      severity: "error",
      message: `Failed to parse constraint expression: ${cd.expression}`,
      constraintId: cd.id,
      constraintName: cd.name,
      source: cd.source
    });
    return { constraintId: cd.id, constraintName: cd.name, satisfied: false, bindings, diagnostics };
  }
  const vars = collectVariables(ast);
  const missing = [];
  for (const v of vars) {
    if (!env.has(v)) missing.push(v);
  }
  if (missing.length === 1) {
    const solved = solveForUnknown(ast, env);
    if (solved) {
      for (const [k, v] of solved) {
        bindings.set(k, v);
        env.set(k, v);
      }
      diagnostics.push({
        severity: "info",
        message: `Solved ${missing[0]} = ${bindings.get(missing[0])}`,
        constraintId: cd.id,
        constraintName: cd.name,
        source: cd.source
      });
      return { constraintId: cd.id, constraintName: cd.name, satisfied: true, bindings, diagnostics };
    }
  }
  if (missing.length > 0) {
    diagnostics.push({
      severity: "warning",
      message: `Unresolved variables in constraint "${cd.name}": ${missing.join(", ")}`,
      constraintId: cd.id,
      constraintName: cd.name,
      source: cd.source
    });
    return { constraintId: cd.id, constraintName: cd.name, satisfied: false, bindings, diagnostics };
  }
  const result = evalExpr2(ast, env);
  if (typeof result === "boolean") {
    if (!result) {
      diagnostics.push({
        severity: "error",
        message: `Constraint "${cd.name}" violated: ${cd.expression}`,
        constraintId: cd.id,
        constraintName: cd.name,
        source: cd.source
      });
    }
    return { constraintId: cd.id, constraintName: cd.name, satisfied: result, bindings, diagnostics };
  }
  if (typeof result === "number") {
    return { constraintId: cd.id, constraintName: cd.name, satisfied: true, bindings, diagnostics };
  }
  diagnostics.push({
    severity: "error",
    message: `Could not evaluate constraint "${cd.name}": ${cd.expression}`,
    constraintId: cd.id,
    constraintName: cd.name,
    source: cd.source
  });
  return { constraintId: cd.id, constraintName: cd.name, satisfied: false, bindings, diagnostics };
}
var ISQ_DIMENSIONS = {
  Mass: "M",
  Length: "L",
  Time: "T",
  Temperature: "\u0398",
  Force: "MLT\u207B\xB2",
  Velocity: "LT\u207B\xB9",
  Acceleration: "LT\u207B\xB2",
  Energy: "ML\xB2T\u207B\xB2",
  Power: "ML\xB2T\u207B\xB3",
  Pressure: "ML\u207B\xB9T\u207B\xB2",
  Area: "L\xB2",
  Volume: "L\xB3",
  Real: "",
  Integer: "",
  Boolean: "",
  String: ""
};
function checkDimensionalCompatibility(typeA, typeB) {
  const dimA = ISQ_DIMENSIONS[typeA];
  const dimB = ISQ_DIMENSIONS[typeB];
  if (dimA === void 0 || dimB === void 0) return null;
  if (dimA === "" && dimB === "") return null;
  if (dimA !== dimB) {
    return `Dimensional mismatch: ${typeA} [${dimA || "dimensionless"}] vs ${typeB} [${dimB || "dimensionless"}]`;
  }
  return null;
}

// src/incremental-parser.ts
var IncrementalParser = class {
  cache = null;
  uri;
  constructor(uri = "sysml:document") {
    this.uri = uri;
  }
  /**
   * Parse source text, using incremental re-parsing if possible.
   *
   * @param sourceText - Full source text to parse
   * @param changeRange - Optional change description from Monaco
   * @returns ParseResult identical to what parseSysML would return
   */
  parse(sourceText, changeRange) {
    if (this.cache && this.cache.sourceText === sourceText) {
      return this.cache.result;
    }
    if (this.cache && changeRange && this.canParseIncrementally(changeRange)) {
      const result2 = this.parseIncrementally(sourceText, changeRange);
      if (result2) {
        this.updateCache(sourceText, result2);
        return result2;
      }
    }
    const result = parseSysML(sourceText, { uri: this.uri });
    this.updateCache(sourceText, result);
    return result;
  }
  /** Reset the cache (e.g., on document switch) */
  reset() {
    this.cache = null;
  }
  /** Get the current cache state (for testing/debugging) */
  getCacheState() {
    return {
      hasCachedResult: this.cache !== null,
      packageCount: this.cache?.packages.length ?? 0
    };
  }
  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------
  canParseIncrementally(changeRange) {
    if (!this.cache) return false;
    const changeSize = Math.abs(changeRange.endLine - changeRange.startLine) + Math.abs(changeRange.lineDelta);
    return changeSize < 50;
  }
  parseIncrementally(sourceText, changeRange) {
    if (!this.cache) return null;
    const lines = sourceText.split("\n");
    const unaffectedPackages = [];
    for (let i = 0; i < this.cache.packages.length; i++) {
      const cached = this.cache.packages[i];
      const adjustedStart = cached.startLine > changeRange.endLine ? cached.startLine + changeRange.lineDelta : cached.startLine;
      const adjustedEnd = cached.endLine > changeRange.endLine ? cached.endLine + changeRange.lineDelta : cached.endLine;
      if (this.rangesOverlap(
        changeRange.startLine,
        changeRange.endLine + changeRange.lineDelta,
        adjustedStart,
        adjustedEnd
      )) ; else {
        const newSlice = lines.slice(adjustedStart - 1, adjustedEnd).join("\n");
        if (newSlice === cached.sourceSlice) {
          unaffectedPackages.push(i);
        }
      }
    }
    if (unaffectedPackages.length === 0) {
      return null;
    }
    const fullResult = parseSysML(sourceText, { uri: this.uri });
    return fullResult;
  }
  updateCache(sourceText, result) {
    const packages = [];
    if (result.model) {
      const lines = sourceText.split("\n");
      for (const pkg of result.model.packages) {
        if (pkg.source) {
          packages.push({
            pkg,
            startLine: pkg.source.startLine,
            endLine: pkg.source.endLine,
            sourceSlice: lines.slice(pkg.source.startLine - 1, pkg.source.endLine).join("\n")
          });
        }
      }
    }
    this.cache = {
      sourceText,
      result,
      packages
    };
  }
  rangesOverlap(aStart, aEnd, bStart, bEnd) {
    return aStart <= bEnd && bStart <= aEnd;
  }
};
function createIncrementalParser(uri) {
  return new IncrementalParser(uri);
}

// src/export-json.ts
function exportToJson(model, options) {
  const opts = {
    includeMetadata: true,
    prettyPrint: true,
    toolVersion: "0.1.0",
    ...options
  };
  const elements = model.allElements.map(
    (el) => elementToJson(el, opts)
  );
  const packages = model.packages.map((pkg) => ({
    "@id": pkg.id,
    "@type": "package",
    name: pkg.name,
    qualifiedName: pkg.qualifiedName,
    elements: getPackageElementIds(pkg)
  }));
  const doc = {
    "$schema": "https://sysml.org/sysml-v2/json/1.0",
    metadata: {
      exportedAt: (/* @__PURE__ */ new Date()).toISOString(),
      toolName: "ArcTek SysML",
      toolVersion: opts.toolVersion ?? "0.1.0",
      sourceUri: opts.sourceUri,
      elementCount: elements.length,
      packageCount: packages.length
    },
    packages,
    elements
  };
  return opts.prettyPrint ? JSON.stringify(doc, null, 2) : JSON.stringify(doc);
}
function elementToJson(el, opts) {
  const obj = el;
  const properties = {};
  for (const [key, value] of Object.entries(obj)) {
    if (["id", "elementType", "name", "qualifiedName", "source", "meta"].includes(key)) continue;
    if (value === void 0) continue;
    properties[key] = value;
  }
  const result = {
    "@id": el.id,
    "@type": el.elementType,
    name: el.name,
    qualifiedName: el.qualifiedName,
    properties
  };
  const lastDot = el.qualifiedName.lastIndexOf(".");
  if (lastDot > 0) {
    result.ownerQualifiedName = el.qualifiedName.slice(0, lastDot);
  }
  if (opts.includeMetadata && el.meta) {
    result.metadata = el.meta;
  }
  return result;
}
function getPackageElementIds(pkg) {
  const ids = [];
  const arrays = [
    pkg.partDefs,
    pkg.actionDefs,
    pkg.stateDefs,
    pkg.requirementDefs,
    pkg.constraintDefs,
    pkg.connectionDefs,
    pkg.allocationDefs,
    pkg.useCaseDefs,
    pkg.interfaceDefs,
    pkg.verificationDefs,
    pkg.analysisDefs,
    pkg.calcDefs,
    pkg.viewpointDefs,
    pkg.renderingDefs,
    pkg.metadataDefs,
    pkg.occurrenceDefs,
    pkg.enums,
    pkg.typeAliases,
    pkg.lets,
    pkg.imports,
    pkg.views,
    pkg.rules
  ];
  for (const arr of arrays) {
    if (arr) {
      for (const el of arr) {
        ids.push(el.id);
      }
    }
  }
  return ids;
}
function importFromJson(jsonString) {
  const diagnostics = [];
  let doc;
  try {
    doc = JSON.parse(jsonString);
  } catch (err) {
    return {
      success: false,
      model: null,
      diagnostics: [{
        severity: "error",
        message: `Invalid JSON: ${err instanceof Error ? err.message : "parse error"}`
      }],
      elementCount: 0
    };
  }
  if (!doc["$schema"]?.includes("sysml")) {
    diagnostics.push({
      severity: "warning",
      message: "Missing or unrecognized $schema \u2014 attempting import anyway"
    });
  }
  if (!doc.elements || !Array.isArray(doc.elements)) {
    return {
      success: false,
      model: null,
      diagnostics: [{
        severity: "error",
        message: 'Missing "elements" array in JSON document'
      }],
      elementCount: 0
    };
  }
  const model = reconstructModel(doc, diagnostics);
  return {
    success: diagnostics.every((d) => d.severity !== "error"),
    model,
    diagnostics,
    elementCount: doc.elements.length
  };
}
function reconstructModel(doc, diagnostics) {
  const allElements = [];
  const byType = /* @__PURE__ */ new Map();
  for (const jsonEl of doc.elements) {
    try {
      const el = jsonElementToIR(jsonEl);
      allElements.push(el);
      let arr = byType.get(el.elementType);
      if (!arr) {
        arr = [];
        byType.set(el.elementType, arr);
      }
      arr.push(el);
    } catch (err) {
      diagnostics.push({
        severity: "warning",
        message: `Failed to import element "${jsonEl.name}" (${jsonEl["@type"]}): ${err instanceof Error ? err.message : "unknown error"}`,
        elementId: jsonEl["@id"]
      });
    }
  }
  const packages = (doc.packages ?? []).map((jp) => {
    const pkgElements = jp.elements.map((id) => allElements.find((e) => e.id === id)).filter((e) => e !== void 0);
    return buildPackageFromElements(jp.name, jp.qualifiedName, jp["@id"], pkgElements);
  });
  if (packages.length === 0 && allElements.length > 0) {
    packages.push(buildPackageFromElements("Imported", "Imported", "pkg_imported", allElements));
    diagnostics.push({
      severity: "info",
      message: 'No packages found in JSON \u2014 created synthetic "Imported" package'
    });
  }
  return {
    packages,
    allPartDefs: typed(byType, "partDef"),
    allPartUsages: typed(byType, "partUsage"),
    allActionDefs: typed(byType, "actionDef"),
    allActionUsages: typed(byType, "actionUsage"),
    allPerforms: typed(byType, "perform"),
    allFlows: typed(byType, "flow"),
    allPortDefs: typed(byType, "portDef"),
    allItemDefs: typed(byType, "itemDef"),
    allPorts: typed(byType, "port"),
    allProps: typed(byType, "prop"),
    allStateDefs: typed(byType, "stateDef"),
    allStateUsages: typed(byType, "stateUsage"),
    allTransitions: typed(byType, "transition"),
    allRequirementDefs: typed(byType, "requirementDef"),
    allRequirementUsages: typed(byType, "requirementUsage"),
    allSatisfies: typed(byType, "satisfy"),
    allConstraintDefs: typed(byType, "constraintDef"),
    allConstraintUsages: typed(byType, "constraintUsage"),
    allAsserts: typed(byType, "assert"),
    allConnectionDefs: typed(byType, "connectionDef"),
    allConnections: typed(byType, "connectionUsage"),
    allAllocationDefs: typed(byType, "allocationDef"),
    allAllocations: typed(byType, "allocationUsage"),
    allUseCaseDefs: typed(byType, "useCaseDef"),
    allUseCaseUsages: typed(byType, "useCaseUsage"),
    allActorUsages: typed(byType, "actorUsage"),
    allIncludeUseCases: typed(byType, "includeUseCase"),
    allInterfaceDefs: typed(byType, "interfaceDef"),
    allInterfaceUsages: typed(byType, "interfaceUsage"),
    allVerificationDefs: typed(byType, "verificationDef"),
    allVerificationUsages: typed(byType, "verificationUsage"),
    allAnalysisDefs: typed(byType, "analysisDef"),
    allAnalysisUsages: typed(byType, "analysisUsage"),
    allCalcDefs: typed(byType, "calcDef"),
    allCalcUsages: typed(byType, "calcUsage"),
    allViewpointDefs: typed(byType, "viewpointDef"),
    allViewpointUsages: typed(byType, "viewpointUsage"),
    allRenderingDefs: typed(byType, "renderingDef"),
    allRenderingUsages: typed(byType, "renderingUsage"),
    allMetadataDefs: typed(byType, "metadataDef"),
    allOccurrenceDefs: typed(byType, "occurrenceDef"),
    allOccurrenceUsages: typed(byType, "occurrenceUsage"),
    allEnums: typed(byType, "enum"),
    allTypeAliases: typed(byType, "typeAlias"),
    allLets: typed(byType, "let"),
    allImports: typed(byType, "import"),
    allRelationships: typed(byType, "relationship"),
    allViews: typed(byType, "view"),
    allRules: typed(byType, "rule"),
    allElements
  };
}
function typed(map, type) {
  return map.get(type) ?? [];
}
function jsonElementToIR(jsonEl) {
  const base = {
    id: jsonEl["@id"],
    elementType: jsonEl["@type"],
    name: jsonEl.name,
    qualifiedName: jsonEl.qualifiedName,
    meta: jsonEl.metadata,
    ...jsonEl.properties
  };
  return base;
}
function buildPackageFromElements(name, qualifiedName, id, elements) {
  const byType = /* @__PURE__ */ new Map();
  for (const el of elements) {
    let arr = byType.get(el.elementType);
    if (!arr) {
      arr = [];
      byType.set(el.elementType, arr);
    }
    arr.push(el);
  }
  return {
    id,
    elementType: "package",
    name,
    qualifiedName,
    partDefs: typed(byType, "partDef"),
    partUsages: typed(byType, "partUsage"),
    actionDefs: typed(byType, "actionDef"),
    stateDefs: typed(byType, "stateDef"),
    requirementDefs: typed(byType, "requirementDef"),
    constraintDefs: typed(byType, "constraintDef"),
    connectionDefs: typed(byType, "connectionDef"),
    allocationDefs: typed(byType, "allocationDef"),
    useCaseDefs: typed(byType, "useCaseDef"),
    interfaceDefs: typed(byType, "interfaceDef"),
    verificationDefs: typed(byType, "verificationDef"),
    analysisDefs: typed(byType, "analysisDef"),
    calcDefs: typed(byType, "calcDef"),
    viewpointDefs: typed(byType, "viewpointDef"),
    renderingDefs: typed(byType, "renderingDef"),
    metadataDefs: typed(byType, "metadataDef"),
    occurrenceDefs: typed(byType, "occurrenceDef"),
    enums: typed(byType, "enum"),
    typeAliases: typed(byType, "typeAlias"),
    lets: typed(byType, "let"),
    imports: typed(byType, "import"),
    relationships: typed(byType, "relationship"),
    views: typed(byType, "view"),
    rules: typed(byType, "rule"),
    portDefs: typed(byType, "portDef"),
    itemDefs: typed(byType, "itemDef"),
    satisfies: typed(byType, "satisfy"),
    asserts: typed(byType, "assert")
  };
}

// src/export-reqif.ts
function exportToReqIF(model, options) {
  const opts = {
    includeVerification: true,
    includeConstraints: true,
    toolId: "ArcTek-SysML",
    title: "SysML Requirements Export",
    ...options
  };
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  const lines = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push(`<REQ-IF xmlns="http://www.omg.org/spec/ReqIF/20110401/reqif.xsd">`);
  lines.push("  <THE-HEADER>");
  lines.push(`    <REQ-IF-HEADER IDENTIFIER="header_${Date.now()}">`);
  lines.push(`      <COMMENT>Exported from ${opts.toolId}</COMMENT>`);
  lines.push(`      <CREATION-TIME>${timestamp}</CREATION-TIME>`);
  lines.push(`      <REPOSITORY-ID>${opts.sourceUri ?? "local"}</REPOSITORY-ID>`);
  lines.push(`      <REQ-IF-TOOL-ID>${opts.toolId}</REQ-IF-TOOL-ID>`);
  lines.push(`      <REQ-IF-VERSION>1.0</REQ-IF-VERSION>`);
  lines.push(`      <SOURCE-TOOL-ID>${opts.toolId}</SOURCE-TOOL-ID>`);
  lines.push(`      <TITLE>${escapeXml(opts.title ?? "")}</TITLE>`);
  lines.push("    </REQ-IF-HEADER>");
  lines.push("  </THE-HEADER>");
  lines.push("  <CORE-CONTENT>");
  lines.push("    <REQ-IF-CONTENT>");
  lines.push("      <DATATYPES>");
  lines.push('        <DATATYPE-DEFINITION-STRING IDENTIFIER="dt_string" LONG-NAME="String" MAX-LENGTH="4000"/>');
  lines.push('        <DATATYPE-DEFINITION-STRING IDENTIFIER="dt_text" LONG-NAME="Text" MAX-LENGTH="32000"/>');
  lines.push("      </DATATYPES>");
  lines.push("      <SPEC-TYPES>");
  lines.push('        <SPEC-OBJECT-TYPE IDENTIFIER="sot_requirement" LONG-NAME="Requirement">');
  lines.push("          <SPEC-ATTRIBUTES>");
  lines.push('            <ATTRIBUTE-DEFINITION-STRING IDENTIFIER="attr_text" LONG-NAME="ReqText">');
  lines.push("              <TYPE><DATATYPE-DEFINITION-STRING-REF>dt_text</DATATYPE-DEFINITION-STRING-REF></TYPE>");
  lines.push("            </ATTRIBUTE-DEFINITION-STRING>");
  lines.push('            <ATTRIBUTE-DEFINITION-STRING IDENTIFIER="attr_priority" LONG-NAME="Priority">');
  lines.push("              <TYPE><DATATYPE-DEFINITION-STRING-REF>dt_string</DATATYPE-DEFINITION-STRING-REF></TYPE>");
  lines.push("            </ATTRIBUTE-DEFINITION-STRING>");
  lines.push('            <ATTRIBUTE-DEFINITION-STRING IDENTIFIER="attr_qname" LONG-NAME="QualifiedName">');
  lines.push("              <TYPE><DATATYPE-DEFINITION-STRING-REF>dt_string</DATATYPE-DEFINITION-STRING-REF></TYPE>");
  lines.push("            </ATTRIBUTE-DEFINITION-STRING>");
  lines.push("          </SPEC-ATTRIBUTES>");
  lines.push("        </SPEC-OBJECT-TYPE>");
  if (opts.includeVerification) {
    lines.push('        <SPEC-OBJECT-TYPE IDENTIFIER="sot_verification" LONG-NAME="Verification">');
    lines.push("          <SPEC-ATTRIBUTES>");
    lines.push('            <ATTRIBUTE-DEFINITION-STRING IDENTIFIER="attr_subject" LONG-NAME="Subject">');
    lines.push("              <TYPE><DATATYPE-DEFINITION-STRING-REF>dt_string</DATATYPE-DEFINITION-STRING-REF></TYPE>");
    lines.push("            </ATTRIBUTE-DEFINITION-STRING>");
    lines.push("          </SPEC-ATTRIBUTES>");
    lines.push("        </SPEC-OBJECT-TYPE>");
  }
  lines.push('        <SPEC-RELATION-TYPE IDENTIFIER="srt_satisfy" LONG-NAME="Satisfies"/>');
  lines.push('        <SPECIFICATION-TYPE IDENTIFIER="spt_doc" LONG-NAME="Requirements Document"/>');
  lines.push("      </SPEC-TYPES>");
  lines.push("      <SPEC-OBJECTS>");
  for (const req of model.allRequirementDefs) {
    lines.push(...specObjectForRequirement(req));
  }
  for (const req of model.allRequirementUsages) {
    lines.push(...specObjectForRequirementUsage(req));
  }
  if (opts.includeVerification) {
    for (const vcase of model.allVerificationDefs) {
      lines.push(...specObjectForVerification(vcase));
    }
  }
  if (opts.includeConstraints) {
    for (const cdef of model.allConstraintDefs) {
      lines.push(...specObjectForConstraint(cdef));
    }
  }
  lines.push("      </SPEC-OBJECTS>");
  lines.push("      <SPEC-RELATIONS>");
  for (const sat of model.allSatisfies) {
    lines.push(...specRelationForSatisfy(sat));
  }
  lines.push("      </SPEC-RELATIONS>");
  lines.push("      <SPECIFICATIONS>");
  lines.push(`        <SPECIFICATION IDENTIFIER="spec_doc" LONG-NAME="${escapeXml(opts.title ?? "Requirements")}">`);
  lines.push("          <TYPE><SPECIFICATION-TYPE-REF>spt_doc</SPECIFICATION-TYPE-REF></TYPE>");
  lines.push("          <CHILDREN>");
  for (const req of model.allRequirementDefs) {
    lines.push(`            <SPEC-HIERARCHY IDENTIFIER="sh_${req.id}">`);
    lines.push(`              <OBJECT><SPEC-OBJECT-REF>${req.id}</SPEC-OBJECT-REF></OBJECT>`);
    lines.push("            </SPEC-HIERARCHY>");
  }
  lines.push("          </CHILDREN>");
  lines.push("        </SPECIFICATION>");
  lines.push("      </SPECIFICATIONS>");
  lines.push("    </REQ-IF-CONTENT>");
  lines.push("  </CORE-CONTENT>");
  lines.push("</REQ-IF>");
  return lines.join("\n");
}
function specObjectForRequirement(req) {
  const lines = [];
  lines.push(`        <SPEC-OBJECT IDENTIFIER="${escapeXml(req.id)}" LONG-NAME="${escapeXml(req.name)}">`);
  lines.push("          <TYPE><SPEC-OBJECT-TYPE-REF>sot_requirement</SPEC-OBJECT-TYPE-REF></TYPE>");
  lines.push("          <VALUES>");
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(req.text ?? "")}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_text</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(req.meta?.state ?? "")}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_priority</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(req.qualifiedName)}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_qname</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push("          </VALUES>");
  lines.push("        </SPEC-OBJECT>");
  return lines;
}
function specObjectForRequirementUsage(req) {
  const lines = [];
  lines.push(`        <SPEC-OBJECT IDENTIFIER="${escapeXml(req.id)}" LONG-NAME="${escapeXml(req.name)}">`);
  lines.push("          <TYPE><SPEC-OBJECT-TYPE-REF>sot_requirement</SPEC-OBJECT-TYPE-REF></TYPE>");
  lines.push("          <VALUES>");
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(req.text ?? "")}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_text</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(req.qualifiedName)}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_qname</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push("          </VALUES>");
  lines.push("        </SPEC-OBJECT>");
  return lines;
}
function specObjectForVerification(vcase) {
  const lines = [];
  lines.push(`        <SPEC-OBJECT IDENTIFIER="${escapeXml(vcase.id)}" LONG-NAME="${escapeXml(vcase.name)}">`);
  lines.push("          <TYPE><SPEC-OBJECT-TYPE-REF>sot_verification</SPEC-OBJECT-TYPE-REF></TYPE>");
  lines.push("          <VALUES>");
  const subject = vcase.subject ? typeof vcase.subject === "string" ? vcase.subject : vcase.subject.ref ?? "" : "";
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(String(subject))}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_subject</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push("          </VALUES>");
  lines.push("        </SPEC-OBJECT>");
  return lines;
}
function specObjectForConstraint(cdef) {
  const lines = [];
  lines.push(`        <SPEC-OBJECT IDENTIFIER="${escapeXml(cdef.id)}" LONG-NAME="${escapeXml(cdef.name)}">`);
  lines.push("          <TYPE><SPEC-OBJECT-TYPE-REF>sot_requirement</SPEC-OBJECT-TYPE-REF></TYPE>");
  lines.push("          <VALUES>");
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(cdef.expression ?? "")}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_text</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push(`            <ATTRIBUTE-VALUE-STRING THE-VALUE="${escapeXml(cdef.qualifiedName)}"><DEFINITION><ATTRIBUTE-DEFINITION-STRING-REF>attr_qname</ATTRIBUTE-DEFINITION-STRING-REF></DEFINITION></ATTRIBUTE-VALUE-STRING>`);
  lines.push("          </VALUES>");
  lines.push("        </SPEC-OBJECT>");
  return lines;
}
function specRelationForSatisfy(sat) {
  const reqRef = typeof sat.requirement === "string" ? sat.requirement : sat.requirement.ref ?? "";
  const byRef = sat.by ? typeof sat.by === "string" ? sat.by : sat.by.ref ?? "" : "";
  return [
    `        <SPEC-RELATION IDENTIFIER="rel_${sat.id}">`,
    "          <TYPE><SPEC-RELATION-TYPE-REF>srt_satisfy</SPEC-RELATION-TYPE-REF></TYPE>",
    `          <SOURCE><SPEC-OBJECT-REF>${escapeXml(String(byRef))}</SPEC-OBJECT-REF></SOURCE>`,
    `          <TARGET><SPEC-OBJECT-REF>${escapeXml(String(reqRef))}</SPEC-OBJECT-REF></TARGET>`,
    "        </SPEC-RELATION>"
  ];
}
function importFromReqIF(xml) {
  const diagnostics = [];
  const requirements = [];
  const verifications = [];
  if (!xml.includes("REQ-IF")) {
    return {
      success: false,
      requirements: [],
      verifications: [],
      diagnostics: [{ severity: "error", message: "Not a valid ReqIF document \u2014 missing REQ-IF root element" }]
    };
  }
  const specObjRegex = /<SPEC-OBJECT\s+IDENTIFIER="([^"]*)"(?:\s+LONG-NAME="([^"]*)")?[^>]*>([\s\S]*?)<\/SPEC-OBJECT>/g;
  let match;
  while ((match = specObjRegex.exec(xml)) !== null) {
    const id = match[1];
    const name = match[2] ?? id;
    const body = match[3];
    const typeRef = extractXmlValue(body, "SPEC-OBJECT-TYPE-REF");
    if (typeRef?.includes("verification")) {
      const subject = extractAttributeValue(body, "attr_subject");
      verifications.push({
        id,
        name,
        requirementId: subject ?? "",
        description: ""
      });
    } else {
      const text = extractAttributeValue(body, "attr_text") ?? "";
      const priority = extractAttributeValue(body, "attr_priority");
      requirements.push({ id, name, text, priority: priority ?? void 0 });
    }
  }
  const relRegex = /<SPEC-RELATION\s+IDENTIFIER="([^"]*)"[^>]*>([\s\S]*?)<\/SPEC-RELATION>/g;
  while ((match = relRegex.exec(xml)) !== null) {
    const body = match[2];
    const source = extractXmlValue(body, "SPEC-OBJECT-REF");
    const target = extractXmlRefFromTag(body, "TARGET");
    if (source && target) {
      const req = requirements.find((r) => r.id === target);
      if (req) {
        if (!req.satisfiedBy) req.satisfiedBy = [];
        req.satisfiedBy.push(source);
      }
    }
  }
  if (requirements.length === 0 && verifications.length === 0) {
    diagnostics.push({ severity: "warning", message: "No requirements or verification cases found in ReqIF document" });
  } else {
    diagnostics.push({
      severity: "info",
      message: `Imported ${requirements.length} requirement(s) and ${verifications.length} verification case(s)`
    });
  }
  return {
    success: diagnostics.every((d) => d.severity !== "error"),
    requirements,
    verifications,
    diagnostics
  };
}
function reqifToSysmlSource(result, packageName = "ImportedRequirements") {
  const lines = [];
  lines.push(`package ${packageName} {`);
  lines.push("");
  for (const req of result.requirements) {
    const safeName = sanitizeName(req.name);
    lines.push(`  requirement def ${safeName} {`);
    if (req.text) {
      lines.push(`    doc /* ${req.text} */;`);
    }
    lines.push("  }");
    lines.push("");
  }
  for (const ver of result.verifications) {
    const safeName = sanitizeName(ver.name);
    lines.push(`  verification def ${safeName} {`);
    if (ver.requirementId) {
      const reqName = result.requirements.find((r) => r.id === ver.requirementId)?.name;
      if (reqName) {
        lines.push(`    subject : ${sanitizeName(reqName)};`);
      }
    }
    lines.push("  }");
    lines.push("");
  }
  lines.push("}");
  return lines.join("\n");
}
function escapeXml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}
function extractXmlValue(xml, tagName) {
  const regex = new RegExp(`<${tagName}>([^<]*)</${tagName}>`);
  const m = regex.exec(xml);
  return m ? m[1].trim() : null;
}
function extractAttributeValue(xml, attrDefRef) {
  const regex = new RegExp(
    `<ATTRIBUTE-VALUE-STRING\\s+THE-VALUE="([^"]*)"[^>]*>[\\s\\S]*?<ATTRIBUTE-DEFINITION-STRING-REF>${attrDefRef}</ATTRIBUTE-DEFINITION-STRING-REF>`
  );
  const m = regex.exec(xml);
  return m ? m[1] : null;
}
function extractXmlRefFromTag(xml, parentTag) {
  const regex = new RegExp(`<${parentTag}>[\\s\\S]*?<SPEC-OBJECT-REF>([^<]*)</SPEC-OBJECT-REF>`);
  const m = regex.exec(xml);
  return m ? m[1].trim() : null;
}
function sanitizeName(name) {
  let safe = name.replace(/[^a-zA-Z0-9_]/g, "_").replace(/^[^a-zA-Z]/, "R");
  if (!safe) safe = "Unnamed";
  return safe;
}

// src/export-xmi.ts
function exportToXMI(model, options) {
  const opts = {
    xmiVersion: "2.5.1",
    includeUMLNamespace: true,
    toolId: "ArcTek-SysML",
    ...options
  };
  const lines = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push(`<xmi:XMI`);
  lines.push(`  xmi:version="${opts.xmiVersion}"`);
  lines.push(`  xmlns:xmi="http://www.omg.org/spec/XMI/20131001"`);
  if (opts.includeUMLNamespace) {
    lines.push(`  xmlns:uml="http://www.omg.org/spec/UML/20131001"`);
  }
  lines.push(`  xmlns:sysml="http://www.omg.org/spec/SysML/20230201">`);
  lines.push(`  <xmi:Documentation>`);
  lines.push(`    <xmi:exporter>${opts.toolId}</xmi:exporter>`);
  lines.push(`    <xmi:exporterVersion>0.1.0</xmi:exporterVersion>`);
  lines.push(`  </xmi:Documentation>`);
  lines.push(`  <uml:Model xmi:type="uml:Model" xmi:id="model_root" name="SysMLModel">`);
  for (const pkg of model.packages) {
    lines.push(...exportPackage(pkg, 2));
  }
  lines.push("  </uml:Model>");
  lines.push("</xmi:XMI>");
  return lines.join("\n");
}
function exportPackage(pkg, depth, opts) {
  const indent3 = "  ".repeat(depth);
  const lines = [];
  lines.push(`${indent3}<packagedElement xmi:type="uml:Package" xmi:id="${esc(pkg.id)}" name="${esc(pkg.name)}">`);
  for (const pd of pkg.partDefs) {
    lines.push(...exportPartDef(pd, depth + 1));
  }
  for (const ad of pkg.actionDefs) {
    lines.push(...exportActionDef(ad, depth + 1));
  }
  for (const rd of pkg.requirementDefs) {
    lines.push(...exportRequirementDef(rd, depth + 1));
  }
  for (const cd of pkg.constraintDefs) {
    lines.push(...exportConstraintDef(cd, depth + 1));
  }
  for (const conn of pkg.connectionDefs) {
    lines.push(...exportConnectionDef(conn, depth + 1));
  }
  for (const vd of pkg.verificationDefs) {
    lines.push(...exportVerificationDef(vd, depth + 1));
  }
  for (const en of pkg.enums) {
    lines.push(`${indent3}  <packagedElement xmi:type="uml:Enumeration" xmi:id="${esc(en.id)}" name="${esc(en.name)}"/>`);
  }
  lines.push(`${indent3}</packagedElement>`);
  return lines;
}
function exportPartDef(pd, depth, opts) {
  const indent3 = "  ".repeat(depth);
  const lines = [];
  lines.push(`${indent3}<packagedElement xmi:type="sysml:Block" xmi:id="${esc(pd.id)}" name="${esc(pd.name)}">`);
  if (pd.supertype && pd.supertype !== "?") {
    lines.push(`${indent3}  <generalization xmi:type="uml:Generalization" general="${esc(String(pd.supertype))}"/>`);
  }
  if (pd.props) {
    for (const prop of pd.props) {
      lines.push(`${indent3}  <ownedAttribute xmi:type="uml:Property" xmi:id="${esc(prop.id)}" name="${esc(prop.name)}"${prop.typeRef ? ` type="${esc(String(prop.typeRef))}"` : ""}/>`);
    }
  }
  if (pd.ports) {
    for (const port of pd.ports) {
      lines.push(`${indent3}  <ownedPort xmi:type="uml:Port" xmi:id="${esc(port.id)}" name="${esc(port.name)}"${port.typeRef ? ` type="${esc(String(port.typeRef))}"` : ""}/>`);
    }
  }
  if (pd.parts) {
    for (const part of pd.parts) {
      lines.push(`${indent3}  <ownedAttribute xmi:type="uml:Property" xmi:id="${esc(part.id)}" name="${esc(part.name)}" aggregation="composite"${part.typeRef ? ` type="${esc(String(part.typeRef))}"` : ""}/>`);
    }
  }
  lines.push(`${indent3}</packagedElement>`);
  return lines;
}
function exportActionDef(ad, depth) {
  const indent3 = "  ".repeat(depth);
  return [
    `${indent3}<packagedElement xmi:type="uml:Activity" xmi:id="${esc(ad.id)}" name="${esc(ad.name)}">`,
    ...(ad.params ?? []).map(
      (p) => `${indent3}  <ownedParameter xmi:type="uml:Parameter" name="${esc(p.name)}" direction="${p.dir ?? "in"}"${p.typeRef ? ` type="${esc(String(p.typeRef))}"` : ""}/>`
    ),
    `${indent3}</packagedElement>`
  ];
}
function exportRequirementDef(rd, depth) {
  const indent3 = "  ".repeat(depth);
  const lines = [];
  lines.push(`${indent3}<packagedElement xmi:type="sysml:Requirement" xmi:id="${esc(rd.id)}" name="${esc(rd.name)}">`);
  if (rd.text) {
    lines.push(`${indent3}  <ownedComment xmi:type="uml:Comment" body="${esc(rd.text)}"/>`);
  }
  lines.push(`${indent3}</packagedElement>`);
  return lines;
}
function exportConstraintDef(cd, depth) {
  const indent3 = "  ".repeat(depth);
  const lines = [];
  lines.push(`${indent3}<packagedElement xmi:type="uml:Constraint" xmi:id="${esc(cd.id)}" name="${esc(cd.name)}">`);
  if (cd.expression) {
    lines.push(`${indent3}  <specification xmi:type="uml:OpaqueExpression">`);
    lines.push(`${indent3}    <body>${esc(cd.expression)}</body>`);
    lines.push(`${indent3}  </specification>`);
  }
  lines.push(`${indent3}</packagedElement>`);
  return lines;
}
function exportConnectionDef(conn, depth) {
  const indent3 = "  ".repeat(depth);
  const lines = [];
  lines.push(`${indent3}<packagedElement xmi:type="uml:Association" xmi:id="${esc(conn.id)}" name="${esc(conn.name)}">`);
  if (conn.ends) {
    for (const end of conn.ends) {
      const ref = typeof end.ref === "string" ? end.ref : end.ref.ref ?? "";
      lines.push(`${indent3}  <memberEnd xmi:type="uml:Property" type="${esc(String(ref))}"/>`);
    }
  }
  lines.push(`${indent3}</packagedElement>`);
  return lines;
}
function exportVerificationDef(vd, depth) {
  const indent3 = "  ".repeat(depth);
  const lines = [];
  lines.push(`${indent3}<packagedElement xmi:type="sysml:TestCase" xmi:id="${esc(vd.id)}" name="${esc(vd.name)}">`);
  if (vd.subject) {
    const subRef = typeof vd.subject === "string" ? vd.subject : vd.subject.ref ?? "";
    lines.push(`${indent3}  <subject xmi:type="uml:Property" type="${esc(String(subRef))}"/>`);
  }
  lines.push(`${indent3}</packagedElement>`);
  return lines;
}
function importFromXMI(xml) {
  const diagnostics = [];
  const unmappedElements = [];
  const lines = [];
  let elementCount = 0;
  if (!xml.includes("xmi:XMI") && !xml.includes("XMI")) {
    return {
      success: false,
      sourceText: "",
      diagnostics: [{ severity: "error", message: "Not a valid XMI document" }],
      elementCount: 0,
      unmappedElements: []
    };
  }
  const topElements = extractBalancedElements(xml, "packagedElement");
  for (const el of topElements) {
    const type = extractAttr(el.openTag, "xmi:type");
    const name = extractAttr(el.openTag, "name");
    if (type === "uml:Package" && name) {
      lines.push(`package ${sanitizeName2(name)} {`);
      lines.push("");
      const children = extractBalancedElements(el.inner, "packagedElement");
      for (const child of children) {
        const childType = extractAttr(child.openTag, "xmi:type");
        const childName = extractAttr(child.openTag, "name");
        if (!childName) continue;
        if (childType === "sysml:Block" || childType === "uml:Class") {
          lines.push(`  part def ${sanitizeName2(childName)} {`);
          elementCount++;
          const propRegex = /<ownedAttribute\s+xmi:type="uml:Property"[^>]*name="([^"]*)"(?:[^>]*type="([^"]*)")?(?:[^>]*aggregation="([^"]*)")?[^>]*\/>/g;
          let propMatch;
          while ((propMatch = propRegex.exec(child.inner)) !== null) {
            const propName = propMatch[1];
            const propType = propMatch[2] ?? "Real";
            const aggregation = propMatch[3];
            if (aggregation === "composite") {
              lines.push(`    part ${sanitizeName2(propName)} : ${sanitizeName2(propType)};`);
            } else {
              lines.push(`    attribute ${sanitizeName2(propName)} : ${sanitizeName2(propType)};`);
            }
            elementCount++;
          }
          const portRegex = /<ownedPort\s+xmi:type="uml:Port"[^>]*name="([^"]*)"(?:[^>]*type="([^"]*)")?[^>]*\/>/g;
          let portMatch;
          while ((portMatch = portRegex.exec(child.inner)) !== null) {
            const portName = portMatch[1];
            const portType = portMatch[2];
            lines.push(`    port ${sanitizeName2(portName)}${portType ? ` : ${sanitizeName2(portType)}` : ""};`);
            elementCount++;
          }
          lines.push("  }");
          lines.push("");
        } else if (childType === "uml:Activity") {
          lines.push(`  action def ${sanitizeName2(childName)};`);
          elementCount++;
        } else if (childType === "sysml:Requirement") {
          lines.push(`  requirement def ${sanitizeName2(childName)} {`);
          const commentMatch = /body="([^"]*)"/.exec(child.inner);
          if (commentMatch) {
            lines.push(`    doc /* ${commentMatch[1]} */;`);
          }
          lines.push("  }");
          lines.push("");
          elementCount++;
        } else if (childType === "uml:Constraint") {
          const exprMatch = /<body>([^<]*)<\/body>/.exec(child.inner);
          if (exprMatch) {
            lines.push(`  constraint def ${sanitizeName2(childName)} { ${exprMatch[1]} }`);
          } else {
            lines.push(`  constraint def ${sanitizeName2(childName)};`);
          }
          lines.push("");
          elementCount++;
        } else if (childType === "sysml:TestCase") {
          lines.push(`  verification def ${sanitizeName2(childName)};`);
          elementCount++;
        }
      }
      lines.push("}");
      lines.push("");
    }
  }
  if (elementCount === 0) {
    diagnostics.push({ severity: "warning", message: "No mappable elements found in XMI document" });
  } else {
    diagnostics.push({ severity: "info", message: `Imported ${elementCount} element(s) from XMI` });
  }
  return {
    success: diagnostics.every((d) => d.severity !== "error"),
    sourceText: lines.join("\n"),
    diagnostics,
    elementCount,
    unmappedElements
  };
}
function extractBalancedElements(xml, tagName) {
  const results = [];
  const openPattern = new RegExp(`<${tagName}\\s`, "g");
  let openMatch;
  while ((openMatch = openPattern.exec(xml)) !== null) {
    const startPos = openMatch.index;
    const openTagEnd = xml.indexOf(">", startPos);
    if (openTagEnd === -1) continue;
    if (xml[openTagEnd - 1] === "/") {
      results.push({ openTag: xml.slice(startPos, openTagEnd + 1), inner: "" });
      continue;
    }
    const openTag = xml.slice(startPos, openTagEnd + 1);
    let depth = 1;
    let pos = openTagEnd + 1;
    const openRe = new RegExp(`<${tagName}[\\s>]`, "g");
    const closeRe = new RegExp(`</${tagName}>`, "g");
    while (depth > 0 && pos < xml.length) {
      openRe.lastIndex = pos;
      closeRe.lastIndex = pos;
      const nextOpen = openRe.exec(xml);
      const nextClose = closeRe.exec(xml);
      if (!nextClose) break;
      if (nextOpen && nextOpen.index < nextClose.index) {
        const selfCloseCheck = xml.indexOf(">", nextOpen.index);
        if (selfCloseCheck !== -1 && xml[selfCloseCheck - 1] === "/") {
          pos = selfCloseCheck + 1;
        } else {
          depth++;
          pos = nextOpen.index + 1;
        }
      } else {
        depth--;
        if (depth === 0) {
          const inner = xml.slice(openTagEnd + 1, nextClose.index);
          results.push({ openTag, inner });
        }
        pos = nextClose.index + nextClose[0].length;
      }
    }
  }
  return results;
}
function extractAttr(openTag, attrName) {
  const regex = new RegExp(`${attrName}="([^"]*)"`);
  const m = regex.exec(openTag);
  return m ? m[1] : null;
}
function esc(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function sanitizeName2(name) {
  let safe = name.replace(/[^a-zA-Z0-9_]/g, "_").replace(/^[^a-zA-Z]/, "X");
  if (!safe) safe = "Unnamed";
  return safe;
}

// src/crdt-adapter.ts
function modelToCRDTElements(model, peerId) {
  const elements = /* @__PURE__ */ new Map();
  const timestamp = Date.now();
  for (const el of model.allElements) {
    const key = el.qualifiedName || el.id;
    const stripped = stripVolatileFields(el);
    elements.set(key, {
      key,
      elementType: el.elementType,
      data: JSON.stringify(stripped),
      timestamp,
      peerId,
      version: 1
    });
  }
  return elements;
}
function computeCRDTChanges(previous, current, peerId) {
  const changes = [];
  const timestamp = Date.now();
  for (const [key, currentEl] of current) {
    const prevEl = previous.get(key);
    if (!prevEl) {
      changes.push({
        type: "add",
        key,
        elementType: currentEl.elementType,
        data: currentEl.data,
        peerId,
        timestamp
      });
    } else if (prevEl.data !== currentEl.data) {
      changes.push({
        type: "update",
        key,
        elementType: currentEl.elementType,
        data: currentEl.data,
        peerId,
        timestamp
      });
    }
  }
  for (const key of previous.keys()) {
    if (!current.has(key)) {
      changes.push({
        type: "delete",
        key,
        peerId,
        timestamp
      });
    }
  }
  return changes;
}
function resolveConflict(local, remote) {
  if (local.type === "delete" || remote.type === "delete") {
    return {
      key: local.key,
      elementType: local.elementType ?? remote.elementType ?? "package",
      localChange: local,
      remoteChange: remote,
      resolution: local.type === "delete" ? "local-wins" : "remote-wins",
      message: `Element "${local.key}" was deleted by one peer and modified by another. Deletion preserved.`
    };
  }
  const resolution = local.timestamp >= remote.timestamp ? "local-wins" : "remote-wins";
  return {
    key: local.key,
    elementType: local.elementType ?? remote.elementType ?? "package",
    localChange: local,
    remoteChange: remote,
    resolution,
    message: `Element "${local.key}" was modified by two peers simultaneously. ${resolution === "local-wins" ? "Local" : "Remote"} version preserved.`
  };
}
function stripVolatileFields(el) {
  const obj = el;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === "source" || key === "id") continue;
    result[key] = value;
  }
  return result;
}
function generatePeerId() {
  return `peer_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
function peerColor(peerId) {
  const colors = [
    "#3b82f6",
    "#ef4444",
    "#22c55e",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#06b6d4",
    "#f97316"
  ];
  let hash = 0;
  for (let i = 0; i < peerId.length; i++) {
    hash = (hash << 5) - hash + peerId.charCodeAt(i) | 0;
  }
  return colors[Math.abs(hash) % colors.length];
}

// src/differ.ts
var CATEGORY_MAP = {
  partDef: "structure",
  partUsage: "structure",
  portDef: "structure",
  itemDef: "structure",
  port: "structure",
  prop: "structure",
  actionDef: "behavior",
  actionUsage: "behavior",
  perform: "behavior",
  flow: "behavior",
  stateDef: "behavior",
  stateUsage: "behavior",
  transition: "behavior",
  requirementDef: "requirement",
  requirementUsage: "requirement",
  satisfy: "requirement",
  constraintDef: "constraint",
  constraintUsage: "constraint",
  assert: "constraint",
  connectionDef: "relationship",
  connectionUsage: "relationship",
  allocationDef: "relationship",
  allocationUsage: "relationship",
  interfaceDef: "relationship",
  interfaceUsage: "relationship",
  metadataDef: "metadata"
};
function classifyElement(elementType) {
  return CATEGORY_MAP[elementType] ?? "other";
}
function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => valuesEqual(v, b[i]));
  }
  if (typeof a === "object" && typeof b === "object") {
    const aObj = a;
    const bObj = b;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((k) => valuesEqual(aObj[k], bObj[k]));
  }
  return false;
}
var SKIP_PROPERTIES = /* @__PURE__ */ new Set(["id", "source", "elementType", "qualifiedName"]);
function compareElements(base, target) {
  const changes = [];
  const allKeys = /* @__PURE__ */ new Set([
    ...Object.keys(base),
    ...Object.keys(target)
  ]);
  for (const key of allKeys) {
    if (SKIP_PROPERTIES.has(key)) continue;
    const baseVal = base[key];
    const targetVal = target[key];
    if (!valuesEqual(baseVal, targetVal)) {
      changes.push({ path: key, oldValue: baseVal, newValue: targetVal });
    }
  }
  return changes;
}
function classifyChanges(elementType, changes) {
  const cats = /* @__PURE__ */ new Set();
  cats.add(classifyElement(elementType));
  for (const change of changes) {
    if (change.path === "meta") {
      cats.add("metadata");
    } else if (change.path === "supertype" || change.path === "typeRef") {
      cats.add("type");
    } else if (change.path === "parts" || change.path === "ports" || change.path === "props") {
      cats.add("structure");
    } else if (change.path === "actions" || change.path === "states" || change.path === "transitions") {
      cats.add("behavior");
    }
  }
  return [...cats];
}
function diffModels(base, target, options) {
  const baseMap = buildElementMap(base.allElements);
  const targetMap = buildElementMap(target.allElements);
  const diffs = [];
  for (const [key, baseEl] of baseMap) {
    const targetEl = targetMap.get(key);
    if (!targetEl) {
      diffs.push({
        operation: "removed",
        elementType: baseEl.elementType,
        qualifiedName: baseEl.qualifiedName,
        name: baseEl.name,
        categories: [classifyElement(baseEl.elementType)]
      });
    } else {
      const changes = compareElements(baseEl, targetEl);
      if (changes.length > 0) {
        const nonMetaChanges = changes.filter((c) => c.path !== "meta");
        if (nonMetaChanges.length > 0 || options?.includeMetadataOnly !== false) {
          diffs.push({
            operation: "modified",
            elementType: targetEl.elementType,
            qualifiedName: targetEl.qualifiedName,
            name: targetEl.name,
            changes,
            categories: classifyChanges(targetEl.elementType, changes)
          });
        }
      }
    }
  }
  for (const [key, targetEl] of targetMap) {
    if (!baseMap.has(key)) {
      diffs.push({
        operation: "added",
        elementType: targetEl.elementType,
        qualifiedName: targetEl.qualifiedName,
        name: targetEl.name,
        categories: [classifyElement(targetEl.elementType)]
      });
    }
  }
  const packageChanges = buildPackageChanges(diffs);
  const summary = buildSummary(diffs);
  return {
    baseTimestamp: options?.baseTimestamp,
    targetTimestamp: options?.targetTimestamp,
    diffs,
    summary,
    packageChanges
  };
}
function buildElementMap(elements) {
  const map = /* @__PURE__ */ new Map();
  for (const el of elements) {
    const key = el.qualifiedName || el.id;
    map.set(key, el);
  }
  return map;
}
function buildPackageChanges(diffs) {
  const pkgMap = /* @__PURE__ */ new Map();
  for (const diff of diffs) {
    const pkg = extractPackageName(diff.qualifiedName);
    let list = pkgMap.get(pkg);
    if (!list) {
      list = [];
      pkgMap.set(pkg, list);
    }
    list.push(diff);
  }
  return [...pkgMap.entries()].map(([pkgName, elementDiffs]) => {
    const ops = new Set(elementDiffs.map((d) => d.operation));
    const operation = ops.size === 1 ? [...ops][0] : "modified";
    return { operation, packageName: pkgName, elementDiffs };
  });
}
function extractPackageName(qualifiedName) {
  const dot = qualifiedName.indexOf(".");
  return dot > 0 ? qualifiedName.slice(0, dot) : qualifiedName;
}
function buildSummary(diffs) {
  const summary = {
    added: 0,
    removed: 0,
    modified: 0,
    total: diffs.length,
    byCategory: {
      structure: 0,
      behavior: 0,
      requirement: 0,
      constraint: 0,
      relationship: 0,
      metadata: 0,
      type: 0,
      other: 0
    },
    byElementType: {}
  };
  for (const diff of diffs) {
    summary[diff.operation]++;
    for (const cat of diff.categories) {
      summary.byCategory[cat]++;
    }
    const et = diff.elementType;
    if (!summary.byElementType[et]) {
      summary.byElementType[et] = { added: 0, removed: 0, modified: 0 };
    }
    summary.byElementType[et][diff.operation]++;
  }
  return summary;
}
function traceElement(qualifiedName, snapshots) {
  const appearances = [];
  let previous = null;
  for (let i = 0; i < snapshots.length; i++) {
    const { model, timestamp } = snapshots[i];
    const element = model.allElements.find((e) => e.qualifiedName === qualifiedName) ?? null;
    let diffFromPrevious = null;
    if (previous && element) {
      diffFromPrevious = compareElements(previous, element);
    } else if (!previous && element) {
      diffFromPrevious = null;
    } else if (previous && !element) {
      diffFromPrevious = [];
    }
    appearances.push({
      snapshotIndex: i,
      timestamp,
      element,
      diffFromPrevious
    });
    previous = element;
  }
  return { qualifiedName, appearances };
}
function createSnapshot(sourceText, model, previousSnapshot, label) {
  const now = Date.now();
  const id = `snap_${now}_${Math.random().toString(36).slice(2, 8)}`;
  const diffFromPrevious = previousSnapshot ? diffModels(previousSnapshot.model, model, {
    baseTimestamp: previousSnapshot.timestamp,
    targetTimestamp: now
  }) : null;
  return {
    id,
    timestamp: now,
    label,
    sourceText,
    model,
    elementCount: model.allElements.length,
    diffFromPrevious
  };
}
function serializeSnapshot(snap) {
  return {
    id: snap.id,
    timestamp: snap.timestamp,
    label: snap.label,
    sourceText: snap.sourceText,
    elementCount: snap.elementCount,
    diffSummary: snap.diffFromPrevious?.summary ?? null
  };
}
function formatDiffSummary(summary) {
  const parts = [];
  if (summary.added > 0) parts.push(`+${summary.added} added`);
  if (summary.removed > 0) parts.push(`-${summary.removed} removed`);
  if (summary.modified > 0) parts.push(`~${summary.modified} modified`);
  return parts.join(", ") || "No changes";
}
function formatElementDiff(diff) {
  const prefix = diff.operation === "added" ? "+" : diff.operation === "removed" ? "-" : "~";
  const base = `${prefix} ${diff.elementType} "${diff.name}" (${diff.qualifiedName})`;
  if (diff.operation === "modified" && diff.changes) {
    const changeList = diff.changes.map((c) => `  ${c.path}: ${formatValue(c.oldValue)} \u2192 ${formatValue(c.newValue)}`).join("\n");
    return `${base}
${changeList}`;
  }
  return base;
}
function formatValue(v) {
  if (v === void 0) return "undefined";
  if (v === null) return "null";
  if (typeof v === "string") return `"${v}"`;
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === "object") return "{...}";
  return String(v);
}

// src/simulation/clock.ts
var SimulationClock = class {
  _time = 0;
  /** Current simulation time in seconds. */
  get time() {
    return this._time;
  }
  /** Advance the clock by a delta (must be >= 0). */
  advance(dt) {
    if (dt < 0) {
      throw new RangeError(`Cannot advance clock by negative delta: ${dt}`);
    }
    this._time += dt;
  }
  /** Advance the clock to an absolute time (must be >= current time). */
  advanceTo(t) {
    if (t < this._time) {
      throw new RangeError(
        `Cannot advance clock backwards from ${this._time} to ${t}`
      );
    }
    this._time = t;
  }
  /** Reset the clock to time 0. */
  reset() {
    this._time = 0;
  }
};

// src/simulation/event-queue.ts
var EventQueue = class {
  heap = [];
  cancelledIds = /* @__PURE__ */ new Set();
  /** Insert an event into the queue. */
  enqueue(event) {
    this.heap.push(event);
    this.bubbleUp(this.heap.length - 1);
  }
  /** Remove and return the next (earliest) event. Skips cancelled events. */
  dequeue() {
    while (this.heap.length > 0) {
      const top = this.removeTop();
      if (top && !this.cancelledIds.has(top.id)) {
        return top;
      }
    }
    return void 0;
  }
  /** Peek at the next event without removing it. Skips cancelled events. */
  peek() {
    while (this.heap.length > 0 && this.cancelledIds.has(this.heap[0].id)) {
      this.removeTop();
    }
    return this.heap.length > 0 ? this.heap[0] : void 0;
  }
  /** Cancel a pending event by its id. */
  cancel(eventId) {
    this.cancelledIds.add(eventId);
  }
  /** Whether the queue has no pending (non-cancelled) events. */
  isEmpty() {
    return this.peek() === void 0;
  }
  /** Number of events in the queue (including cancelled). */
  size() {
    return this.heap.length;
  }
  /** Remove all events and cancellations. */
  clear() {
    this.heap = [];
    this.cancelledIds.clear();
  }
  /** Snapshot of all pending (non-cancelled) events, sorted by time. */
  toArray() {
    return this.heap.filter((e) => !this.cancelledIds.has(e.id)).sort((a, b) => a.time - b.time || a.priority - b.priority);
  }
  // ---- Heap operations ----
  removeTop() {
    if (this.heap.length === 0) return void 0;
    const top = this.heap[0];
    const last = this.heap.pop();
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.sinkDown(0);
    }
    return top;
  }
  bubbleUp(idx) {
    while (idx > 0) {
      const parent = idx - 1 >> 1;
      if (this.compare(idx, parent) < 0) {
        this.swap(idx, parent);
        idx = parent;
      } else {
        break;
      }
    }
  }
  sinkDown(idx) {
    const length = this.heap.length;
    while (true) {
      let smallest = idx;
      const left = 2 * idx + 1;
      const right = 2 * idx + 2;
      if (left < length && this.compare(left, smallest) < 0) {
        smallest = left;
      }
      if (right < length && this.compare(right, smallest) < 0) {
        smallest = right;
      }
      if (smallest !== idx) {
        this.swap(idx, smallest);
        idx = smallest;
      } else {
        break;
      }
    }
  }
  compare(i, j) {
    const a = this.heap[i];
    const b = this.heap[j];
    if (a.time !== b.time) return a.time - b.time;
    return a.priority - b.priority;
  }
  swap(i, j) {
    const tmp = this.heap[i];
    this.heap[i] = this.heap[j];
    this.heap[j] = tmp;
  }
};

// src/simulation/variable-store.ts
var VariableStore = class {
  values = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Map();
  /** Get the current value at the given path, or null if unset. */
  get(path) {
    return this.values.get(path) ?? null;
  }
  /** Set a value and notify listeners if the value changed. */
  set(path, value) {
    const oldVal = this.values.get(path) ?? null;
    this.values.set(path, value);
    if (oldVal !== value) {
      const callbacks = this.listeners.get(path);
      if (callbacks) {
        for (const cb of callbacks) {
          cb(value, oldVal);
        }
      }
    }
  }
  /**
   * Register a change listener for a specific path.
   * Returns an unsubscribe function.
   */
  onChange(path, callback) {
    let pathListeners = this.listeners.get(path);
    if (!pathListeners) {
      pathListeners = /* @__PURE__ */ new Set();
      this.listeners.set(path, pathListeners);
    }
    pathListeners.add(callback);
    return () => {
      pathListeners.delete(callback);
      if (pathListeners.size === 0) {
        this.listeners.delete(path);
      }
    };
  }
  /** Take a snapshot of all current values. */
  snapshot() {
    const result = {};
    for (const [key, val] of this.values) {
      result[key] = val;
    }
    return result;
  }
  /** Replace all values from a snapshot (does not fire listeners). */
  restore(snap) {
    this.values.clear();
    for (const [key, val] of Object.entries(snap)) {
      this.values.set(key, val);
    }
  }
  /** Remove all bindings and listeners. */
  clear() {
    this.values.clear();
    this.listeners.clear();
  }
};

// src/simulation/trace.ts
var TraceRecorder = class {
  entries = [];
  seq = 0;
  /** Append a trace entry with an auto-assigned sequence number. */
  record(entry) {
    this.entries.push({ ...entry, seq: this.seq++ });
  }
  /**
   * Get trace entries, optionally filtered by kind and/or elementId.
   */
  getEntries(filter) {
    if (!filter) return [...this.entries];
    return this.entries.filter((e) => {
      if (filter.kind && e.kind !== filter.kind) return false;
      if (filter.elementId && e.elementId !== filter.elementId) return false;
      return true;
    });
  }
  /** Get entries within a time range [startTime, endTime]. */
  getEntriesInRange(startTime, endTime) {
    return this.entries.filter(
      (e) => e.time >= startTime && e.time <= endTime
    );
  }
  /** Create a SimTrace summary object. */
  toSimTrace(totalSteps) {
    const lastTime = this.entries.length > 0 ? this.entries[this.entries.length - 1].time : 0;
    return {
      entries: [...this.entries],
      totalTime: lastTime,
      totalSteps
    };
  }
  /** Reset the recorder. */
  clear() {
    this.entries = [];
    this.seq = 0;
  }
};

// src/simulation/state-interpreter.ts
var StateInterpreter = class {
  constructor(model, variableStore, eventQueue, traceRecorder, clock) {
    this.model = model;
    this.variableStore = variableStore;
    this.eventQueue = eventQueue;
    this.traceRecorder = traceRecorder;
    this.clock = clock;
  }
  /**
   * Initialize a state machine — find the initial state and enter it.
   * Returns the initial state ID, or null if the state machine has no states.
   */
  initialize(stateDef) {
    const states = stateDef.states;
    if (!states || states.length === 0) {
      return null;
    }
    const initial = states[0];
    this.traceRecorder.record({
      time: this.clock.time,
      kind: "stateEnter",
      elementId: initial.id,
      elementName: initial.name,
      details: { stateMachine: stateDef.name }
    });
    return initial.id;
  }
  /**
   * Process an event against a state machine.
   * Returns the new active state ID (or the current one if no transition fired).
   */
  processEvent(stateDef, activeStateId, event) {
    const transitions = stateDef.transitions;
    if (!transitions || transitions.length === 0) {
      return activeStateId;
    }
    const activeStateName = this.findStateName(stateDef, activeStateId);
    if (!activeStateName) {
      return activeStateId;
    }
    for (const transition of transitions) {
      const sourceRef = typeof transition.sourceState.ref === "string" ? transition.sourceState.ref : null;
      if (!sourceRef || sourceRef !== activeStateName) {
        continue;
      }
      if (transition.trigger) {
        const triggerRef = typeof transition.trigger.ref === "string" ? transition.trigger.ref : null;
        if (triggerRef && triggerRef !== event.name) {
          continue;
        }
      }
      if (transition.guard && !this.evaluateGuard(transition.guard)) {
        continue;
      }
      const targetRef = typeof transition.targetState.ref === "string" ? transition.targetState.ref : null;
      if (!targetRef) {
        continue;
      }
      const targetState = this.findStateByName(stateDef, targetRef);
      if (!targetState) {
        continue;
      }
      this.traceRecorder.record({
        time: this.clock.time,
        kind: "stateExit",
        elementId: activeStateId,
        elementName: activeStateName,
        details: { stateMachine: stateDef.name }
      });
      this.traceRecorder.record({
        time: this.clock.time,
        kind: "transition",
        elementId: transition.id,
        elementName: transition.name,
        from: activeStateName,
        to: targetRef,
        details: {
          stateMachine: stateDef.name,
          trigger: event.name,
          guard: transition.guard ?? null
        }
      });
      this.traceRecorder.record({
        time: this.clock.time,
        kind: "stateEnter",
        elementId: targetState.id,
        elementName: targetState.name,
        details: { stateMachine: stateDef.name }
      });
      return targetState.id;
    }
    return activeStateId;
  }
  /**
   * Find a state's name by its ID within a state definition.
   */
  findStateName(stateDef, stateId) {
    const states = stateDef.states;
    if (!states) return null;
    for (const state of states) {
      if (state.id === stateId) {
        return state.name;
      }
    }
    return null;
  }
  /**
   * Find a state by its name within a state definition.
   */
  findStateByName(stateDef, name) {
    const states = stateDef.states;
    if (!states) return null;
    for (const state of states) {
      if (state.name === name) {
        return { id: state.id, name: state.name };
      }
    }
    return null;
  }
  /**
   * Evaluate a guard expression against the variable store.
   *
   * Supports:
   * - Comparison operators: ==, !=, <, >, <=, >=
   * - Bare identifiers: check if the variable is truthy in the store
   * - Unparseable guards are treated as passing (return true)
   */
  evaluateGuard(guard) {
    const trimmed = guard.trim();
    if (!trimmed) return true;
    const comparisonOps = ["<=", ">=", "!=", "==", "<", ">"];
    for (const op of comparisonOps) {
      const idx = trimmed.indexOf(op);
      if (idx !== -1) {
        const lhs = trimmed.substring(0, idx).trim();
        const rhs = trimmed.substring(idx + op.length).trim();
        return this.evaluateComparison(lhs, rhs, op);
      }
    }
    const value = this.variableStore.get(trimmed);
    return this.isTruthy(value);
  }
  /**
   * Evaluate a comparison expression.
   */
  evaluateComparison(lhs, rhs, op) {
    const leftVal = this.resolveValue(lhs);
    const rightVal = this.resolveValue(rhs);
    switch (op) {
      case "==":
        return leftVal === rightVal;
      case "!=":
        return leftVal !== rightVal;
      case "<":
        return typeof leftVal === "number" && typeof rightVal === "number" && leftVal < rightVal;
      case ">":
        return typeof leftVal === "number" && typeof rightVal === "number" && leftVal > rightVal;
      case "<=":
        return typeof leftVal === "number" && typeof rightVal === "number" && leftVal <= rightVal;
      case ">=":
        return typeof leftVal === "number" && typeof rightVal === "number" && leftVal >= rightVal;
    }
  }
  /**
   * Resolve a value string to a SimValue.
   * If it's a number literal, parse it. If it's a boolean or string literal, use that.
   * Otherwise, look it up in the variable store.
   */
  resolveValue(raw) {
    const num = Number(raw);
    if (!isNaN(num) && raw !== "") {
      return num;
    }
    if (raw === "true") return true;
    if (raw === "false") return false;
    if (raw === "null") return null;
    if (raw.startsWith('"') && raw.endsWith('"') || raw.startsWith("'") && raw.endsWith("'")) {
      return raw.slice(1, -1);
    }
    return this.variableStore.get(raw);
  }
  /**
   * Check if a SimValue is truthy.
   */
  isTruthy(value) {
    if (value === null) return false;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") return value.length > 0;
    return false;
  }
};

// src/simulation/action-executor.ts
var eventCounter = 0;
var ActionExecutor = class {
  constructor(model, variableStore, eventQueue, traceRecorder, clock) {
    this.model = model;
    this.variableStore = variableStore;
    this.eventQueue = eventQueue;
    this.traceRecorder = traceRecorder;
    this.clock = clock;
  }
  /**
   * Start executing an action definition.
   * Records an actionStart trace and returns cursor 0.
   */
  start(actionDef) {
    this.traceRecorder.record({
      time: this.clock.time,
      kind: "actionStart",
      elementId: actionDef.id,
      elementName: actionDef.name,
      details: { isRoot: true }
    });
    return 0;
  }
  /**
   * Step to the next action within the definition.
   * Returns the new cursor, or -1 if the action is complete.
   */
  step(actionDef, cursor) {
    const steps = this.getSteps(actionDef);
    if (cursor < steps.length) {
      const currentStep = steps[cursor];
      this.traceRecorder.record({
        time: this.clock.time,
        kind: "actionStart",
        elementId: currentStep.id,
        elementName: currentStep.name,
        details: {
          parentAction: actionDef.name,
          stepIndex: cursor
        }
      });
      return cursor + 1;
    }
    this.traceRecorder.record({
      time: this.clock.time,
      kind: "actionEnd",
      elementId: actionDef.id,
      elementName: actionDef.name,
      details: { totalSteps: steps.length }
    });
    const completionEvent = {
      id: `sim-evt-${eventCounter++}`,
      time: this.clock.time,
      kind: "completion",
      sourceId: actionDef.id,
      targetId: actionDef.id,
      name: `${actionDef.name}.completed`,
      payload: {},
      priority: 10
    };
    this.eventQueue.enqueue(completionEvent);
    return -1;
  }
  /**
   * Collect the executable steps from an action definition.
   * Combines nested actions and perform statements into one ordered list.
   */
  getSteps(actionDef) {
    const steps = [];
    if (actionDef.actions) {
      for (const action of actionDef.actions) {
        steps.push({ id: action.id, name: action.name });
      }
    }
    if (actionDef.performs) {
      for (const perform of actionDef.performs) {
        steps.push({ id: perform.id, name: perform.name });
      }
    }
    return steps;
  }
};

// src/simulation/engine.ts
var SimulationEngine = class {
  constructor(model) {
    this.model = model;
    this.clock = new SimulationClock();
    this.eventQueue = new EventQueue();
    this.variableStore = new VariableStore();
    this.traceRecorder = new TraceRecorder();
    this.stateInterpreter = new StateInterpreter(
      model,
      this.variableStore,
      this.eventQueue,
      this.traceRecorder,
      this.clock
    );
    this.actionExecutor = new ActionExecutor(
      model,
      this.variableStore,
      this.eventQueue,
      this.traceRecorder,
      this.clock
    );
  }
  clock;
  eventQueue;
  variableStore;
  traceRecorder;
  stateInterpreter;
  actionExecutor;
  status = "idle";
  activeStates = {};
  actionCursors = {};
  stepCount = 0;
  maxDuration = Infinity;
  /**
   * Initialize the simulation with the given configuration.
   * Sets up initial bindings, events, enters initial states,
   * and starts action cursors.
   */
  initialize(config) {
    this.clock.reset();
    this.eventQueue.clear();
    this.variableStore.clear();
    this.traceRecorder.clear();
    this.activeStates = {};
    this.actionCursors = {};
    this.stepCount = 0;
    this.maxDuration = config.maxDuration;
    for (const [key, value] of Object.entries(config.initialBindings)) {
      this.variableStore.set(key, value);
    }
    for (const event of config.initialEvents) {
      this.eventQueue.enqueue(event);
    }
    for (const smId of config.stateMachineIds) {
      const stateDef = this.model.allStateDefs.find((sd) => sd.id === smId);
      if (stateDef) {
        const initialStateId = this.stateInterpreter.initialize(stateDef);
        if (initialStateId) {
          this.activeStates[smId] = initialStateId;
        }
      }
    }
    for (const actionId of config.actionIds) {
      const actionDef = this.model.allActionDefs.find(
        (ad) => ad.id === actionId
      );
      if (actionDef) {
        const cursor = this.actionExecutor.start(actionDef);
        this.actionCursors[actionId] = cursor;
      }
    }
    this.status = "running";
    return this.getState();
  }
  /**
   * Execute a single simulation step.
   * Dequeues the next event, advances the clock, and dispatches
   * to the appropriate interpreter.
   */
  step() {
    const event = this.eventQueue.dequeue();
    if (!event) {
      this.status = "completed";
      return this.getState();
    }
    if (event.time > this.maxDuration) {
      this.status = "completed";
      return this.getState();
    }
    this.clock.advanceTo(event.time);
    this.traceRecorder.record({
      time: this.clock.time,
      kind: "eventConsumed",
      elementId: event.id,
      elementName: event.name,
      details: {
        kind: event.kind,
        sourceId: event.sourceId,
        targetId: event.targetId,
        payload: event.payload
      }
    });
    if (this.activeStates[event.targetId] !== void 0) {
      const stateDef = this.model.allStateDefs.find(
        (sd) => sd.id === event.targetId
      );
      if (stateDef) {
        const newStateId = this.stateInterpreter.processEvent(
          stateDef,
          this.activeStates[event.targetId],
          event
        );
        this.activeStates[event.targetId] = newStateId;
      }
    }
    if (this.actionCursors[event.targetId] !== void 0) {
      const actionDef = this.model.allActionDefs.find(
        (ad) => ad.id === event.targetId
      );
      if (actionDef) {
        const newCursor = this.actionExecutor.step(
          actionDef,
          this.actionCursors[event.targetId]
        );
        this.actionCursors[event.targetId] = newCursor;
      }
    }
    this.stepCount++;
    return this.getState();
  }
  /**
   * Run the simulation to completion.
   * Loops step() until no events remain or maxDuration is reached.
   */
  run(config) {
    this.initialize(config);
    const maxSteps = Math.ceil(config.maxDuration / config.stepSize) + 1e3;
    while (this.status === "running" && this.stepCount < maxSteps) {
      this.step();
    }
    const constraintResults = config.evaluateConstraints ? this.evaluateConstraints() : [];
    const requirementResults = config.checkRequirements ? this.checkRequirements() : [];
    const trace = this.traceRecorder.toSimTrace(this.stepCount);
    const totalTransitions = trace.entries.filter(
      (e) => e.kind === "transition"
    ).length;
    const totalEvents = trace.entries.filter(
      (e) => e.kind === "eventConsumed"
    ).length;
    const constraintsSatisfied = constraintResults.filter(
      (c) => c.satisfied
    ).length;
    const constraintsViolated = constraintResults.filter(
      (c) => !c.satisfied
    ).length;
    const requirementsSatisfied = requirementResults.filter(
      (r) => r.satisfied
    ).length;
    const requirementsUnsatisfied = requirementResults.filter(
      (r) => !r.satisfied
    ).length;
    return {
      config,
      trace,
      finalState: this.getState(),
      constraintResults,
      requirementResults,
      summary: {
        totalSteps: this.stepCount,
        totalTime: this.clock.time,
        totalEvents,
        totalTransitions,
        constraintsSatisfied,
        constraintsViolated,
        requirementsSatisfied,
        requirementsUnsatisfied
      }
    };
  }
  /** Get the current simulation state snapshot. */
  getState() {
    return {
      time: this.clock.time,
      activeStates: { ...this.activeStates },
      variables: this.variableStore.snapshot(),
      actionCursors: { ...this.actionCursors },
      running: this.status === "running",
      paused: this.status === "paused",
      completed: this.status === "completed",
      pendingEvents: this.eventQueue.toArray()
    };
  }
  /** Get the current engine status. */
  getStatus() {
    return this.status;
  }
  /** Reset the engine to its initial idle state. */
  reset() {
    this.clock.reset();
    this.eventQueue.clear();
    this.variableStore.clear();
    this.traceRecorder.clear();
    this.activeStates = {};
    this.actionCursors = {};
    this.stepCount = 0;
    this.maxDuration = Infinity;
    this.status = "idle";
  }
  /**
   * Evaluate all constraint definitions against the current variable state.
   */
  evaluateConstraints() {
    const results = [];
    for (const constraintDef of this.model.allConstraintDefs) {
      const expression = constraintDef.expression ?? "";
      const bindings = this.variableStore.snapshot();
      let satisfied = true;
      if (expression) {
        satisfied = this.evaluateConstraintExpression(expression, bindings);
      }
      results.push({
        constraintId: constraintDef.id,
        constraintName: constraintDef.name,
        satisfied,
        expression,
        violationTime: satisfied ? void 0 : this.clock.time,
        bindings
      });
      this.traceRecorder.record({
        time: this.clock.time,
        kind: "constraintCheck",
        elementId: constraintDef.id,
        elementName: constraintDef.name,
        details: { satisfied, expression }
      });
    }
    return results;
  }
  /**
   * Evaluate a constraint expression against variable bindings.
   * Supports simple comparison operators (==, !=, <, >, <=, >=).
   */
  evaluateConstraintExpression(expression, bindings) {
    const trimmed = expression.trim();
    if (!trimmed) return true;
    const comparisonOps = ["<=", ">=", "!=", "==", "<", ">"];
    for (const op of comparisonOps) {
      const idx = trimmed.indexOf(op);
      if (idx !== -1) {
        const lhs = trimmed.substring(0, idx).trim();
        const rhs = trimmed.substring(idx + op.length).trim();
        const leftVal = this.resolveBindingValue(lhs, bindings);
        const rightVal = this.resolveBindingValue(rhs, bindings);
        switch (op) {
          case "==":
            return leftVal === rightVal;
          case "!=":
            return leftVal !== rightVal;
          case "<":
            return typeof leftVal === "number" && typeof rightVal === "number" && leftVal < rightVal;
          case ">":
            return typeof leftVal === "number" && typeof rightVal === "number" && leftVal > rightVal;
          case "<=":
            return typeof leftVal === "number" && typeof rightVal === "number" && leftVal <= rightVal;
          case ">=":
            return typeof leftVal === "number" && typeof rightVal === "number" && leftVal >= rightVal;
        }
      }
    }
    return true;
  }
  /**
   * Resolve a value token against the variable bindings.
   */
  resolveBindingValue(raw, bindings) {
    const num = Number(raw);
    if (!isNaN(num) && raw !== "") return num;
    if (raw === "true") return true;
    if (raw === "false") return false;
    if (raw === "null") return null;
    if (raw.startsWith('"') && raw.endsWith('"') || raw.startsWith("'") && raw.endsWith("'")) {
      return raw.slice(1, -1);
    }
    return bindings[raw] ?? null;
  }
  /**
   * Check requirement satisfaction.
   * A requirement is satisfied if any SatisfyUsage references it.
   */
  checkRequirements() {
    const results = [];
    for (const reqDef of this.model.allRequirementDefs) {
      const satisfyUsage = this.model.allSatisfies.find((s) => {
        const reqRef = typeof s.requirement.ref === "string" ? s.requirement.ref : null;
        return reqRef === reqDef.name || reqRef === reqDef.id;
      });
      const satisfied = satisfyUsage !== void 0;
      const verificationDef = satisfied ? this.model.allVerificationDefs.find(
        (vd) => vd.requirements?.some((r) => {
          const ref = typeof r.ref === "string" ? r.ref : null;
          return ref === reqDef.name || ref === reqDef.id;
        })
      ) : void 0;
      results.push({
        requirementId: reqDef.id,
        requirementName: reqDef.name,
        satisfied,
        verificationCaseId: verificationDef?.id,
        details: satisfied ? `Satisfied by ${typeof satisfyUsage.by?.ref === "string" ? satisfyUsage.by.ref : "unknown element"}` : "No satisfy relationship found"
      });
      this.traceRecorder.record({
        time: this.clock.time,
        kind: "requirementCheck",
        elementId: reqDef.id,
        elementName: reqDef.name,
        details: { satisfied, verificationCaseId: verificationDef?.id }
      });
    }
    return results;
  }
};

// src/simulation/scenarios.ts
var scenarioEventCounter = 0;
function createEvent(partial) {
  return { ...partial, id: `scenario-evt-${scenarioEventCounter++}` };
}
var normalToastCycle = {
  id: "toaster-normal-cycle",
  name: "Normal Toast Cycle",
  description: "Full toast cycle: off -> loading -> heating -> releasing -> popping -> off. Verifies toast timing requirement (30-180s).",
  config: {
    name: "Normal Toast Cycle",
    stepSize: 1,
    maxDuration: 200,
    initialBindings: {
      breadLoaded: true,
      toastDone: false,
      thermalOverload: false
    },
    initialEvents: [
      createEvent({
        time: 0,
        kind: "trigger",
        sourceId: "",
        targetId: "",
        name: "leverPressed",
        payload: {},
        priority: 0
      }),
      createEvent({
        time: 90,
        kind: "change",
        sourceId: "",
        targetId: "",
        name: "toastDone",
        payload: { toastDone: true },
        priority: 5
      })
    ],
    stateMachineIds: [],
    actionIds: [],
    evaluateConstraints: true,
    checkRequirements: true,
    playbackSpeed: 1
  },
  expectedFinalStates: { ToasterStates: "off" }
};
var thermalSafetyTrip = {
  id: "toaster-thermal-safety",
  name: "Thermal Safety Trip",
  description: "Heating element exceeds thermal envelope -> fault state. Verifies thermal fuse activation.",
  config: {
    name: "Thermal Safety Trip",
    stepSize: 1,
    maxDuration: 100,
    initialBindings: {
      breadLoaded: true,
      thermalOverload: false
    },
    initialEvents: [
      createEvent({
        time: 0,
        kind: "trigger",
        sourceId: "",
        targetId: "",
        name: "leverPressed",
        payload: {},
        priority: 0
      }),
      createEvent({
        time: 45,
        kind: "change",
        sourceId: "",
        targetId: "",
        name: "thermalOverload",
        payload: { thermalOverload: true },
        priority: 5
      })
    ],
    stateMachineIds: [],
    actionIds: [],
    evaluateConstraints: true,
    checkRequirements: true,
    playbackSpeed: 1
  },
  expectedFinalStates: { ToasterStates: "fault" }
};
var interlockTest = {
  id: "toaster-interlock",
  name: "Interlock Test",
  description: "Lever pressed without bread loaded -> interlock prevents heating. Verifies interlock requirement.",
  config: {
    name: "Interlock Test",
    stepSize: 1,
    maxDuration: 30,
    initialBindings: {
      breadLoaded: false,
      thermalOverload: false
    },
    initialEvents: [
      createEvent({
        time: 0,
        kind: "trigger",
        sourceId: "",
        targetId: "",
        name: "leverPressed",
        payload: {},
        priority: 0
      })
    ],
    stateMachineIds: [],
    actionIds: [],
    evaluateConstraints: true,
    checkRequirements: true,
    playbackSpeed: 1
  },
  expectedFinalStates: { ToasterStates: "off" }
};
var browningAdjustment = {
  id: "toaster-browning-adjustment",
  name: "Browning Adjustment",
  description: "Adjust browning dial mid-cycle. BrowningAdjustment action executes, target temp updated.",
  config: {
    name: "Browning Adjustment",
    stepSize: 1,
    maxDuration: 120,
    initialBindings: {
      breadLoaded: true,
      browningLevel: 3,
      targetTemp: 180
    },
    initialEvents: [
      createEvent({
        time: 0,
        kind: "trigger",
        sourceId: "",
        targetId: "",
        name: "leverPressed",
        payload: {},
        priority: 0
      }),
      createEvent({
        time: 30,
        kind: "signal",
        sourceId: "",
        targetId: "",
        name: "browningDialChanged",
        payload: { level: 7 },
        priority: 0
      })
    ],
    stateMachineIds: [],
    actionIds: [],
    evaluateConstraints: true,
    checkRequirements: true,
    playbackSpeed: 1
  }
};
var powerBudgetViolation = {
  id: "toaster-power-budget",
  name: "Power Budget Violation",
  description: "All subsystems draw max power simultaneously. PowerBudget constraint violation flagged.",
  config: {
    name: "Power Budget Violation",
    stepSize: 1,
    maxDuration: 10,
    initialBindings: {
      "heatingElement.power": 1200,
      "controlBoard.power": 400,
      totalPower: 1600,
      maxPower: 1500
    },
    initialEvents: [],
    stateMachineIds: [],
    actionIds: [],
    evaluateConstraints: true,
    checkRequirements: true,
    playbackSpeed: 1
  }
};
var springFatigue = {
  id: "toaster-spring-fatigue",
  name: "Spring Fatigue",
  description: "Simulates cumulative spring usage over many cycles to check fatigue life.",
  config: {
    name: "Spring Fatigue",
    stepSize: 10,
    maxDuration: 1e3,
    initialBindings: {
      cycleCount: 0,
      springFatigueLimit: 1e4
    },
    initialEvents: Array.from(
      { length: 10 },
      (_, i) => createEvent({
        time: (i + 1) * 10,
        kind: "completion",
        sourceId: "",
        targetId: "",
        name: "cycleComplete",
        payload: { cycleCount: i + 1 },
        priority: 10
      })
    ),
    stateMachineIds: [],
    actionIds: [],
    evaluateConstraints: true,
    checkRequirements: true,
    playbackSpeed: 1
  }
};
var TOASTER_SCENARIOS = [
  normalToastCycle,
  thermalSafetyTrip,
  interlockTest,
  browningAdjustment,
  powerBudgetViolation,
  springFatigue
];
function getToasterScenarios() {
  return TOASTER_SCENARIOS;
}

export { ActionExecutor, EventQueue, IncrementalParser, Query, SimulationClock, SimulationEngine, StateInterpreter, TOASTER_SCENARIOS, TraceRecorder, ValidationRuleRegistry, VariableStore, applyTextEdit, checkDimensionalCompatibility, compileSysMLModel, computeCRDTChanges, createIncrementalParser, createSnapshot, detectStdlibCollisions, diffModels, evaluateValidationRules, executeQuery, exportToJson, exportToReqIF, exportToXMI, formatDiffSummary, formatElementDiff, formatSysmlSource, generatePeerId, generateSysMLv2, getFixesForDiagnostic, getStandardLibrary, getToasterScenarios, importFromJson, importFromReqIF, importFromXMI, isValidParseResult, mergeWithStdlib, modelToCRDTElements, parseExpression, parseSysML, parseSysMLWithImports, peerColor, queryModel, reqifToSysmlSource, resetStdlibCache, resolveConflict, serializeSnapshot, solveConstraints, summarizeValidation, traceElement, transformSpecToIR };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map